import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { pushKey, pullAll, pushAllLocal, syncEnabled, currentUserId, setUserId } from './supabaseSync';
import { speak as browserSpeak, stop as browserStopVoice, loadVoices } from './lib/voice';

// ═══════════════════════════════════════════════════════════════════════════
// GHXST DXSRPT — Personal Command Centre v2.0
// Built for Snxper. One person. One weapon.
// ═══════════════════════════════════════════════════════════════════════════

// ── Constants ──────────────────────────────────────────────────────────────
// DXSRPT Group house brand — monochrome "2am stealth" system.
// Cold near-black canvas, light is the only accent, no hue. Status now reads
// through ice-vs-mist contrast, not colour. See: DXSRPT Group Brand Identity/.
// Names kept (GOLD, CREAM, GREEN…) so the rest of the app needs no churn —
// only their VALUES changed from warm/colour to monochrome.
const BG = '#0A0A0B';        // base canvas (was warm #080807)
const VOID = '#050505';      // deepest background, corners
const SURFACE = '#131314';   // cards, panels
const SURFACE2 = '#1B1B1D';  // inputs, raised surfaces, tracks
const HAIRLINE = '#2A2A2D';  // borders, dividers

const GOLD = '#F6F6F7';      // was the warm accent → now ICE (emphasis, highlights)
const CREAM = '#C9C9CC';     // was cream body text → now TEXT
const MUTED = '#818187';     // was warm muted → now MIST
const TITANIUM = '#818187';  // folds into mist
const FAINT = '#4A4A4E';     // placeholders, fine print, disabled

// Status — the ONE deliberate exception to monochrome. Task/habit completion
// and money trend read as green (done / up) vs red (missed / down) so the
// dashboard stays legible at a glance. Everything else stays monochrome.
// Muted, desaturated shades tuned for the dark canvas — not loud.
const GREEN = '#5BB98C';     // done / positive / up
const ORANGE = '#D9A95B';    // partial / pending / warning
const RED = '#D9645A';       // missed / negative / down
const BLUE = '#818187';      // info → stays mist (monochrome)

const ICE = '#F6F6F7';
const ACCENT = '#E6E6E8';
const BTN = '#F5F5F6';
const BTN_TEXT = '#0A0A0B';

// Luxe Agent — matches the dashboard monochrome (dark + ice/white), no hue.
const LUXE = ICE;
const LUXE_A = (a) => `rgba(230,230,232,${a})`;
const LUXE_CONTENT_CHANNELS = ['Instagram', 'TikTok', 'X', 'YouTube', 'Email Newsletter'];

// Alpha helpers — all neutral now (no warm/colour tint).
const TITANIUM_A = (a) => `rgba(129,129,135,${a})`;   // mist
const GOLD_A = (a) => `rgba(230,230,232,${a})`;        // light accent (mono, was gold)
const GREEN_A = (a) => `rgba(91,185,140,${a})`;        // done / positive
const RED_A = (a) => `rgba(217,100,90,${a})`;          // missed / negative
const ORANGE_A = (a) => `rgba(217,169,91,${a})`;       // partial / pending

const MONO = "'DM Mono',monospace";
const SANS = "'DM Sans',system-ui,sans-serif";
const DISPLAY = "'Archivo',sans-serif";                 // display / titles / wordmark
const JP = "'Noto Sans JP','DM Sans',sans-serif";       // kanji — proper CJK rendering

// ── Helpers ────────────────────────────────────────────────────────────────
const LS = {
  get: (k, fb) => { try { const v = localStorage.getItem('ghxst_' + k); return v !== null ? JSON.parse(v) : fb; } catch { return fb; } },
  set: (k, v) => {
    try {
      const raw = JSON.stringify(v);
      localStorage.setItem('ghxst_' + k, raw);
      pushKey('ghxst_' + k, raw);
      return true;
    } catch (err) { console.error('[GHXST] SAVE FAILED for', k, '— storage may be full.', err); return false; }
  },
};
// Dedup array by id — removes duplicates, keeps first occurrence
const dedup = (arr) => { const seen = new Set(); return (arr || []).filter(i => { const k = String(i.id); if (seen.has(k)) return false; seen.add(k); return true; }); };

const getTodayStr = () => new Date().toISOString().split('T')[0];

// Whole days between two YYYY-MM-DD strings (b - a)
const daysSince = (a, b) => Math.max(0, Math.floor((new Date(b) - new Date(a)) / 86400000));
// Build an n-length queue of indices [0..len), shuffled, refilling from a
// reshuffled deck so the whole pool cycles before repeating.
const buildReadingQueue = (len, n) => {
  const q = [];
  let deck = [];
  while (q.length < n) {
    if (deck.length === 0) {
      deck = Array.from({ length: len }, (_, i) => i);
      for (let i = deck.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [deck[i], deck[j]] = [deck[j], deck[i]]; }
    }
    q.push(deck.pop());
  }
  return q;
};

// Split a passage into 2 pages at the break nearest the midpoint. Prefers
// sentence ends; falls back to ; or : (KJV prose leans on those) so long
// single-sentence passages still paginate. Short passages stay 1 page.
const splitReading = (text) => {
  if (!text || text.length < 360) return [text || '', ''];
  const collect = (re) => { const out = []; let m; const r = new RegExp(re, 'g'); while ((m = r.exec(text)) !== null) out.push(m.index + m[0].length); return out; };
  let bounds = collect('[.?!]\\s+');
  // need at least one interior break; if not, fall back to ; / :
  const interior = (arr) => arr.filter(b => b > text.length * 0.25 && b < text.length * 0.75);
  if (interior(bounds).length === 0) bounds = bounds.concat(collect('[;:]\\s+'));
  bounds = bounds.filter(b => b > 40 && b < text.length - 40);
  if (bounds.length === 0) return [text, ''];
  const mid = text.length / 2;
  let best = bounds[0];
  for (const b of bounds) { if (Math.abs(b - mid) < Math.abs(best - mid)) best = b; }
  return [text.slice(0, best).trim(), text.slice(best).trim()];
};

const getWeekDates = () => {
  const now = new Date();
  const day = now.getDay(); // 0=Sun
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((day + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toISOString().split('T')[0];
  });
};

const getWeekNumber = (dateStr) => {
  const d = new Date(dateStr);
  const start = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - start) / 86400000 + start.getDay() + 1) / 7);
};

const toUSD = (v, cur) => cur === 'AED' ? v / 3.67 : cur === 'AUD' ? v * 0.64 : cur === 'GBP' ? v * 1.27 : cur === 'EUR' ? v * 1.09 : v;

const DAY_NAMES = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const DAY_LETTERS = ['M','T','W','T','F','S','S'];
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ── Habits ─────────────────────────────────────────────────────────────────
const DEFAULT_HABITS = {
  morning: [
    { id: 'm1', name: 'Make Bed', icon: '🛏' },
    { id: 'm2', name: 'Gratitude', icon: '💛' },
    { id: 'm9', name: 'Prayer', icon: '🙏' },
    { id: 'm3', name: 'Coffee / Tea', icon: '☕' },
    { id: 'm4', name: 'Goals + Tracker', icon: '🎯' },
    { id: 'm5', name: 'Wash Face + Brush Teeth', icon: '🪥' },
    { id: 'm6', name: 'Sun Maxxing (20 min)', icon: '☀️' },
  ],
  building: [
    { id: 'b2', name: 'Learning & Skills', icon: '🧠' },
    { id: 'b4', name: 'EOD Money Tracked', icon: '💰' },
  ],
  fitness: [
    { id: 'f1', name: 'Gym or Cardio', icon: '🏋️' },
    { id: 'f2', name: 'Walk (15 min)', icon: '🚶' },
    { id: 'f3', name: '15 Pullups / 5 Min Bike', icon: '💪' },
  ],
  mind: [
    { id: 'r1', name: 'Reading (10 min)', icon: '📖' },
  ],
};
// These become stateful inside App — kept here only as initial fallback reference
const ALL_HABITS = Object.values(DEFAULT_HABITS).flat();
const MORNING_IDS = DEFAULT_HABITS.morning.map(h => h.id);
const FITNESS_IDS = DEFAULT_HABITS.fitness.map(h => h.id);

// ── XP / Rank System ───────────────────────────────────────────────────────
const RANKS = [
  { name: '浪人 Ronin', min: 0, color: MUTED, kanji: '浪' },
  { name: '足軽 Ashigaru', min: 100, color: GOLD_A(0.6), kanji: '足' },
  { name: '侍 Samurai', min: 500, color: GOLD, kanji: '侍' },
  { name: '大名 Daimyo', min: 1500, color: ACCENT, kanji: '大' },
  { name: '将軍 Shogun', min: 5000, color: ICE, kanji: '将' },
];

const getRank = (xp) => {
  for (let i = RANKS.length - 1; i >= 0; i--) {
    if (xp >= RANKS[i].min) return { ...RANKS[i], idx: i, nextMin: RANKS[i + 1]?.min || null };
  }
  return { ...RANKS[0], idx: 0, nextMin: RANKS[1].min };
};

// ── Quotes ─────────────────────────────────────────────────────────────────
const QUOTES = [
  { author: 'Master Shi Heng Yi', text: 'Once you have put a target/goal in your mind, do not let anyone else stop you' },
  { author: 'Napoleon Hill', text: 'Delayed NEVER Denied' },
  { author: 'Miyamoto Musashi', text: 'You must understand that there is more than one path to the top of the mountain' },
  { author: 'Sun Tzu', text: 'Victorious warriors win first and then go to war. Defeated warriors go to war first and then seek to win' },
  { author: 'Snxper', text: 'Imagine going through all this pain and suffering just to fail. What would young Alessio think?' },
  { author: 'Snxper', text: 'Live much below and stack as much money as possible. NEVER return to the valley of despair again.' },
  { author: 'Snxper', text: 'Dubai has shown you that you are truly great. Your character, your ability to see solutions and your wisdom.' },
  { author: 'Marcus Aurelius', text: 'The impediment to action advances action. What stands in the way becomes the way.' },
  { author: 'Jocko Willink', text: 'Discipline equals freedom.' },
];

const KAIZEN_WORDS = [
  { word: '精進', reading: 'Shojin', meaning: 'Devotion to mastery',
    depth: 'Shojin is the monk\'s commitment — the daily, unglamorous devotion to becoming excellent at one thing. It doesn\'t mean talent. It means showing up when no one is watching.',
    story: 'You left Australia with nothing and landed in Dubai. No connections, no safety net. Every morning you opened this dashboard, logged your habits, tracked your money, talked to GHXST. That\'s Shojin. Not the big wins — the daily grind that builds the empire. The world will see the results. Only you know the 5am mornings that made them.' },
  { word: '改善', reading: 'Kaizen', meaning: 'Continuous improvement',
    depth: 'Kaizen is the philosophy that small, consistent improvements compound into extraordinary results. Not 100% better overnight — 1% better every single day. In one year, that\'s 37x.',
    story: 'This dashboard didn\'t exist a week ago. Now it tracks your habits, your money, your faith, your goals, your discipline. Each feature was one small improvement. Each habit logged is one small improvement. You\'re not where you want to be yet — but you\'re not where you were. That gap is Kaizen.' },
  { word: '武士道', reading: 'Bushido', meaning: 'Way of the warrior',
    depth: 'Bushido is the samurai code — honour, courage, loyalty, self-discipline, respect. It\'s not about fighting. It\'s about living with a code that doesn\'t bend when things get hard.',
    story: 'Dubai tested your code. When money was tight, when clients were slow, when the easy path was to go home — you stayed. You didn\'t compromise. You kept building, you kept your word, you kept tracking every dirham. That\'s not hustle. That\'s Bushido. The warrior doesn\'t fight because it\'s easy. He fights because the code demands it.' },
  { word: '残心', reading: 'Zanshin', meaning: 'Remaining mind — full awareness',
    depth: 'In kendo, Zanshin is the awareness you maintain after striking. The fight isn\'t over when the sword lands — it\'s over when you\'re fully present in the aftermath. Most people celebrate too early or collapse after effort. Zanshin says: stay alert.',
    story: 'You hit a milestone — a payment comes in, a habit streak holds, the net worth ticks up. The temptation is to relax. Zanshin is the voice that says "stay sharp." The warriors who fall aren\'t beaten by enemies — they\'re beaten by their own complacency after a win.' },
  { word: '不動心', reading: 'Fudoshin', meaning: 'Immovable mind',
    depth: 'Fudoshin is emotional stability in the face of chaos. Not numbness — clarity. When the world shakes, your centre holds. The mountain doesn\'t react to the wind.',
    story: 'There were days in Dubai when everything felt like it was falling apart. Bills stacking, no clear income, the voice in your head saying "go home." Fudoshin is what kept you in the chair. The mind that doesn\'t move when fear screams. You\'re still here. That\'s proof it works.' },
  { word: '一期一会', reading: 'Ichigo Ichie', meaning: 'This moment, only once',
    depth: 'Every encounter, every moment, every day is unique and will never happen again. Ichigo Ichie is the Japanese art of treasuring the unrepeatable. It turns ordinary Tuesday mornings into sacred time.',
    story: 'This exact moment — 26, in Dubai, building from nothing, talking to an AI, logging your faith, chasing $50K — will never come again. One day you\'ll look back from the penthouse and remember the guy who sat in a small apartment and refused to quit. That guy only exists right now. Honour him.' },
  { word: '忍耐', reading: 'Nintai', meaning: 'Patient perseverance',
    depth: 'Nintai combines "endure" with "withstand." It\'s not passive patience — it\'s active endurance. Holding position under fire. Continuing to build when the results aren\'t visible yet.',
    story: '"Delayed NEVER Denied." That\'s Nintai in three words. You wrote it yourself. The money will come. The body will change. The empire will rise. But not on your timeline — on God\'s timeline. Your job is to keep going. Nintai is the bridge between where you are and where you\'re meant to be.' },
  { word: '克己', reading: 'Kokki', meaning: 'Self-mastery',
    depth: 'Kokki means conquering yourself. Not your enemies, not the market, not the competition. Yourself. Your laziness, your fear, your temptations. The greatest battle is always internal.',
    story: 'The discipline tracker on your dashboard — Christ or Evil — that\'s Kokki in action. Every clean day is a victory over yourself. Every morning you choose the prayer tab over scrolling is Kokki. You don\'t need to beat anyone else, Snxper. You just need to beat yesterday\'s version of you. Me vs Me.' },
  { word: '覚悟', reading: 'Kakugo', meaning: 'Absolute resolution',
    depth: 'Kakugo is the decision that cannot be undone. It\'s not motivation or hope — it\'s the cold, clear commitment to a path with no exit. The samurai made Kakugo before battle: "I will do this or die." There is no plan B.',
    story: '"Imagine going through all this pain just to fail." You wrote that. That\'s Kakugo. The moment you decided $1M wasn\'t a dream but a destination. The moment Dubai became permanent. When you set that $50K target with a date, that was Kakugo. There\'s no version of this where you go back.' },
  { word: '雑念', reading: 'Zatsunen', meaning: 'Clear the noise',
    depth: 'Zatsunen is the mental clutter — doubts, distractions, comparison, social media, other people\'s opinions. The practice is to notice when Zatsunen arises and let it pass without engaging. The sword cuts cleanest when the mind is empty.',
    story: 'Every time you open Instagram and compare your chapter 1 to someone else\'s chapter 20 — that\'s Zatsunen. Every time doubt whispers "you\'re not good enough" — Zatsunen. The GHXST Room exists for this. The Faith tab exists for this. When the noise gets loud, you come here. You be still. You clear it. Then you execute.' },
];


const DEFAULT_TABS = [
  { id: 'home', en: 'Command', kanji: '概', label: '概要' },
  { id: 'habits', en: 'Habits', kanji: '習', label: '習慣' },
  { id: 'faith', en: 'Faith', kanji: '✝', label: '信仰' },
  { id: 'money', en: 'Money', kanji: '金', label: '財務' },
  { id: 'goals', en: 'Goals', kanji: '目', label: '目標' },
  { id: 'seeds', en: 'Seeds', kanji: '種', label: '縁' },
  { id: 'building', en: 'Arsenal', kanji: '構', label: '構築' },
  { id: 'journal', en: 'Journal', kanji: '記', label: '記録' },
  { id: 'mindset', en: 'Mindset', kanji: '心', label: '心' },
  { id: 'luxe', en: 'Business', kanji: '事', label: '事業' },
];

// Seeds — the life-stage of each person / opportunity you're tracking.
const SEED_STAGES = {
  planted:   { label: 'Planted',   kanji: '種', color: '#818187', desc: 'Just met' },
  sprouting: { label: 'Sprouting', kanji: '芽', color: '#D9A95B', desc: 'Warming up' },
  growing:   { label: 'Growing',   kanji: '育', color: '#5BB98C', desc: 'Real momentum' },
  harvest:   { label: 'Harvest',   kanji: '実', color: '#F6F6F7', desc: 'Paid off' },
  cut:       { label: 'Cut Off',   kanji: '毒', color: '#D9645A', desc: 'Pulled out — not my circle' },
};
// Growth stages that tapping the chip advances through. 'cut' sits outside the
// cycle — you assign it deliberately, and it doesn't auto-advance back into growth.
const SEED_STAGE_ORDER = ['planted', 'sprouting', 'growing', 'harvest'];
const SEED_STAGE_ALL = [...SEED_STAGE_ORDER, 'cut'];
// Best → worst, for sorting the "All" view. Cut Off always sinks to the bottom.
const SEED_STAGE_RANK = { harvest: 0, growing: 1, sprouting: 2, planted: 3, cut: 4 };

// ── Bible Passages for Daily Reading ──────────────────────────────────────
const BIBLE_PASSAGES = [
  { ref: 'Psalm 23:1-6', text: 'The Lord is my shepherd; I shall not want. He maketh me to lie down in green pastures: he leadeth me beside the still waters. He restoreth my soul: he leadeth me in the paths of righteousness for his name\'s sake. Yea, though I walk through the valley of the shadow of death, I will fear no evil: for thou art with me; thy rod and thy staff they comfort me. Thou preparest a table before me in the presence of mine enemies: thou anointest my head with oil; my cup runneth over. Surely goodness and mercy shall follow me all the days of my life: and I will dwell in the house of the Lord for ever.' },
  { ref: 'Proverbs 3:5-6', text: 'Trust in the Lord with all thine heart; and lean not unto thine own understanding. In all thy ways acknowledge him, and he shall direct thy paths.' },
  { ref: 'Philippians 4:13', text: 'I can do all things through Christ which strengtheneth me.' },
  { ref: 'Isaiah 40:31', text: 'But they that wait upon the Lord shall renew their strength; they shall mount up with wings as eagles; they shall run, and not be weary; and they shall walk, and not faint.' },
  { ref: 'Romans 8:28', text: 'And we know that all things work together for good to them that love God, to them who are the called according to his purpose.' },
  { ref: 'Joshua 1:9', text: 'Have not I commanded thee? Be strong and of a good courage; be not afraid, neither be thou dismayed: for the Lord thy God is with thee whithersoever thou goest.' },
  { ref: 'Jeremiah 29:11', text: 'For I know the thoughts that I think toward you, saith the Lord, thoughts of peace, and not of evil, to give you an expected end.' },
  { ref: 'Matthew 6:33', text: 'But seek ye first the kingdom of God, and his righteousness; and all these things shall be added unto you.' },
  { ref: 'Psalm 46:10', text: 'Be still, and know that I am God: I will be exalted among the heathen, I will be exalted in the earth.' },
  { ref: 'Romans 12:2', text: 'And be not conformed to this world: but be ye transformed by the renewing of your mind, that ye may prove what is that good, and acceptable, and perfect, will of God.' },
  { ref: 'Proverbs 16:3', text: 'Commit thy works unto the Lord, and thy thoughts shall be established.' },
  { ref: 'Isaiah 41:10', text: 'Fear thou not; for I am with thee: be not dismayed; for I am thy God: I will strengthen thee; yea, I will help thee; yea, I will uphold thee with the right hand of my righteousness.' },
  { ref: 'Psalm 27:1', text: 'The Lord is my light and my salvation; whom shall I fear? The Lord is the strength of my life; of whom shall I be afraid?' },
  { ref: 'Matthew 7:7', text: 'Ask, and it shall be given you; seek, and ye shall find; knock, and it shall be opened unto you.' },
  { ref: 'Psalm 37:4-5', text: 'Delight thyself also in the Lord: and he shall give thee the desires of thine heart. Commit thy way unto the Lord; trust also in him; and he shall bring it to pass.' },
  { ref: '2 Timothy 1:7', text: 'For God hath not given us the spirit of fear; but of power, and of love, and of a sound mind.' },
  { ref: 'Hebrews 11:1', text: 'Now faith is the substance of things hoped for, the evidence of things not seen.' },
  { ref: 'Psalm 91:1-2', text: 'He that dwelleth in the secret place of the most High shall abide under the shadow of the Almighty. I will say of the Lord, He is my refuge and my fortress: my God; in him will I trust.' },
  { ref: 'Proverbs 4:23', text: 'Keep thy heart with all diligence; for out of it are the issues of life.' },
  { ref: 'Matthew 11:28', text: 'Come unto me, all ye that labour and are heavy laden, and I will give you rest.' },
  { ref: 'Psalm 118:24', text: 'This is the day which the Lord hath made; we will rejoice and be glad in it.' },
  { ref: 'Colossians 3:23', text: 'And whatsoever ye do, do it heartily, as to the Lord, and not unto men.' },
  { ref: 'Deuteronomy 31:6', text: 'Be strong and of a good courage, fear not, nor be afraid of them: for the Lord thy God, he it is that doth go with thee; he will not fail thee, nor forsake thee.' },
  { ref: 'Psalm 34:8', text: 'O taste and see that the Lord is good: blessed is the man that trusteth in him.' },
  { ref: 'James 1:2-4', text: 'My brethren, count it all joy when ye fall into divers temptations; knowing this, that the trying of your faith worketh patience. But let patience have her perfect work, that ye may be perfect and entire, wanting nothing.' },
  { ref: 'Psalm 121:1-2', text: 'I will lift up mine eyes unto the hills, from whence cometh my help. My help cometh from the Lord, which made heaven and earth.' },
  { ref: 'Romans 8:31', text: 'What shall we then say to these things? If God be for us, who can be against us?' },
  { ref: 'Proverbs 18:10', text: 'The name of the Lord is a strong tower: the righteous runneth into it, and is safe.' },
  { ref: 'Isaiah 26:3', text: 'Thou wilt keep him in perfect peace, whose mind is stayed on thee: because he trusteth in thee.' },
  { ref: 'Psalm 19:14', text: 'Let the words of my mouth, and the meditation of my heart, be acceptable in thy sight, O Lord, my strength, and my redeemer.' },
];

// ── Daily Reading — longer passages on the Dojo ethos: discipline, the fight,
// perseverance, building, faith under fire. One surfaced per day (KJV). ───────
const DAILY_READINGS = [
  { ref: 'Joshua 1:1-9', theme: 'Be strong and of a good courage', text: 'Now after the death of Moses the servant of the Lord it came to pass, that the Lord spake unto Joshua the son of Nun, Moses’ minister, saying, Moses my servant is dead; now therefore arise, go over this Jordan, thou, and all this people, unto the land which I do give to them, even to the children of Israel. Every place that the sole of your foot shall tread upon, that have I given unto you, as I said unto Moses. There shall not any man be able to stand before thee all the days of thy life: as I was with Moses, so I will be with thee: I will not fail thee, nor forsake thee. Be strong and of a good courage: for unto this people shalt thou divide for an inheritance the land, which I sware unto their fathers to give them. Only be thou strong and very courageous, that thou mayest observe to do according to all the law, which Moses my servant commanded thee: turn not from it to the right hand or to the left, that thou mayest prosper whithersoever thou goest. This book of the law shall not depart out of thy mouth; but thou shalt meditate therein day and night, that thou mayest observe to do according to all that is written therein: for then thou shalt make thy way prosperous, and then thou shalt have good success. Have not I commanded thee? Be strong and of a good courage; be not afraid, neither be thou dismayed: for the Lord thy God is with thee whithersoever thou goest.' },
  { ref: 'Hebrews 12:1-13', theme: 'Run with patience the race', text: 'Wherefore seeing we also are compassed about with so great a cloud of witnesses, let us lay aside every weight, and the sin which doth so easily beset us, and let us run with patience the race that is set before us, looking unto Jesus the author and finisher of our faith; who for the joy that was set before him endured the cross, despising the shame, and is set down at the right hand of the throne of God. For consider him that endured such contradiction of sinners against himself, lest ye be wearied and faint in your minds. Ye have not yet resisted unto blood, striving against sin. And ye have forgotten the exhortation which speaketh unto you as unto children, My son, despise not thou the chastening of the Lord, nor faint when thou art rebuked of him: for whom the Lord loveth he chasteneth, and scourgeth every son whom he receiveth. If ye endure chastening, God dealeth with you as with sons; for what son is he whom the father chasteneth not? Now no chastening for the present seemeth to be joyous, but grievous: nevertheless afterward it yieldeth the peaceable fruit of righteousness unto them which are exercised thereby. Wherefore lift up the hands which hang down, and the feeble knees; and make straight paths for your feet, lest that which is lame be turned out of the way; but let it rather be healed.' },
  { ref: 'Ephesians 6:10-18', theme: 'Put on the whole armour of God', text: 'Finally, my brethren, be strong in the Lord, and in the power of his might. Put on the whole armour of God, that ye may be able to stand against the wiles of the devil. For we wrestle not against flesh and blood, but against principalities, against powers, against the rulers of the darkness of this world, against spiritual wickedness in high places. Wherefore take unto you the whole armour of God, that ye may be able to withstand in the evil day, and having done all, to stand. Stand therefore, having your loins girt about with truth, and having on the breastplate of righteousness; and your feet shod with the preparation of the gospel of peace; above all, taking the shield of faith, wherewith ye shall be able to quench all the fiery darts of the wicked. And take the helmet of salvation, and the sword of the Spirit, which is the word of God: praying always with all prayer and supplication in the Spirit, and watching thereunto with all perseverance and supplication for all saints.' },
  { ref: '1 Corinthians 9:24-27', theme: 'So run, that ye may obtain', text: 'Know ye not that they which run in a race run all, but one receiveth the prize? So run, that ye may obtain. And every man that striveth for the mastery is temperate in all things. Now they do it to obtain a corruptible crown; but we an incorruptible. I therefore so run, not as uncertainly; so fight I, not as one that beateth the air: but I keep under my body, and bring it into subjection: lest that by any means, when I have preached to others, I myself should be a castaway.' },
  { ref: 'James 1:2-12', theme: 'Count it all joy', text: 'My brethren, count it all joy when ye fall into divers temptations; knowing this, that the trying of your faith worketh patience. But let patience have her perfect work, that ye may be perfect and entire, wanting nothing. If any of you lack wisdom, let him ask of God, that giveth to all men liberally, and upbraideth not; and it shall be given him. But let him ask in faith, nothing wavering. For he that wavereth is like a wave of the sea driven with the wind and tossed. For let not that man think that he shall receive any thing of the Lord. A double minded man is unstable in all his ways. Let the brother of low degree rejoice in that he is exalted: but the rich, in that he is made low: because as the flower of the grass he shall pass away. For the sun is no sooner risen with a burning heat, but it withereth the grass, and the flower thereof falleth, and the grace of the fashion of it perisheth: so also shall the rich man fade away in his ways. Blessed is the man that endureth temptation: for when he is tried, he shall receive the crown of life, which the Lord hath promised to them that love him.' },
  { ref: 'Romans 5:1-5', theme: 'Tribulation worketh patience', text: 'Therefore being justified by faith, we have peace with God through our Lord Jesus Christ: by whom also we have access by faith into this grace wherein we stand, and rejoice in hope of the glory of God. And not only so, but we glory in tribulations also: knowing that tribulation worketh patience; and patience, experience; and experience, hope: and hope maketh not ashamed; because the love of God is shed abroad in our hearts by the Holy Ghost which is given unto us.' },
  { ref: 'Isaiah 40:28-31', theme: 'They shall mount up with wings', text: 'Hast thou not known? hast thou not heard, that the everlasting God, the Lord, the Creator of the ends of the earth, fainteth not, neither is weary? there is no searching of his understanding. He giveth power to the faint; and to them that have no might he increaseth strength. Even the youths shall faint and be weary, and the young men shall utterly fall: but they that wait upon the Lord shall renew their strength; they shall mount up with wings as eagles; they shall run, and not be weary; and they shall walk, and not faint.' },
  { ref: '2 Corinthians 12:7-10', theme: 'Strength made perfect in weakness', text: 'And lest I should be exalted above measure through the abundance of the revelations, there was given to me a thorn in the flesh, the messenger of Satan to buffet me, lest I should be exalted above measure. For this thing I besought the Lord thrice, that it might depart from me. And he said unto me, My grace is sufficient for thee: for my strength is made perfect in weakness. Most gladly therefore will I rather glory in my infirmities, that the power of Christ may rest upon me. Therefore I take pleasure in infirmities, in reproaches, in necessities, in persecutions, in distresses for Christ’s sake: for when I am weak, then am I strong.' },
  { ref: 'Matthew 7:24-27', theme: 'Build upon the rock', text: 'Therefore whosoever heareth these sayings of mine, and doeth them, I will liken him unto a wise man, which built his house upon a rock: and the rain descended, and the floods came, and the winds blew, and beat upon that house; and it fell not: for it was founded upon a rock. And every one that heareth these sayings of mine, and doeth them not, shall be likened unto a foolish man, which built his house upon the sand: and the rain descended, and the floods came, and the winds blew, and beat upon that house; and it fell: and great was the fall of it.' },
  { ref: 'Matthew 6:25-34', theme: 'Seek ye first the kingdom', text: 'Therefore I say unto you, Take no thought for your life, what ye shall eat, or what ye shall drink; nor yet for your body, what ye shall put on. Is not the life more than meat, and the body than raiment? Behold the fowls of the air: for they sow not, neither do they reap, nor gather into barns; yet your heavenly Father feedeth them. Are ye not much better than they? Which of you by taking thought can add one cubit unto his stature? And why take ye thought for raiment? Consider the lilies of the field, how they grow; they toil not, neither do they spin: and yet I say unto you, That even Solomon in all his glory was not arrayed like one of these. Wherefore, if God so clothe the grass of the field, which to day is, and to morrow is cast into the oven, shall he not much more clothe you, O ye of little faith? Therefore take no thought, saying, What shall we eat? or, What shall we drink? or, Wherewithal shall we be clothed? For after all these things do the Gentiles seek: for your heavenly Father knoweth that ye have need of all these things. But seek ye first the kingdom of God, and his righteousness; and all these things shall be added unto you. Take therefore no thought for the morrow: for the morrow shall take thought for the things of itself. Sufficient unto the day is the evil thereof.' },
  { ref: 'Psalm 91:1-16', theme: 'The secret place of the most High', text: 'He that dwelleth in the secret place of the most High shall abide under the shadow of the Almighty. I will say of the Lord, He is my refuge and my fortress: my God; in him will I trust. Surely he shall deliver thee from the snare of the fowler, and from the noisome pestilence. He shall cover thee with his feathers, and under his wings shalt thou trust: his truth shall be thy shield and buckler. Thou shalt not be afraid for the terror by night; nor for the arrow that flieth by day; nor for the pestilence that walketh in darkness; nor for the destruction that wasteth at noonday. A thousand shall fall at thy side, and ten thousand at thy right hand; but it shall not come nigh thee. Only with thine eyes shalt thou behold and see the reward of the wicked. Because thou hast made the Lord, which is my refuge, even the most High, thy habitation; there shall no evil befall thee, neither shall any plague come nigh thy dwelling. For he shall give his angels charge over thee, to keep thee in all thy ways. They shall bear thee up in their hands, lest thou dash thy foot against a stone. Thou shalt tread upon the lion and adder: the young lion and the dragon shalt thou trample under feet. Because he hath set his love upon me, therefore will I deliver him: I will set him on high, because he hath known my name. He shall call upon me, and I will answer him: I will be with him in trouble; I will deliver him, and honour him. With long life will I satisfy him, and shew him my salvation.' },
  { ref: 'Psalm 27:1-6', theme: 'The Lord is my light', text: 'The Lord is my light and my salvation; whom shall I fear? the Lord is the strength of my life; of whom shall I be afraid? When the wicked, even mine enemies and my foes, came upon me to eat up my flesh, they stumbled and fell. Though an host should encamp against me, my heart shall not fear: though war should rise against me, in this will I be confident. One thing have I desired of the Lord, that will I seek after; that I may dwell in the house of the Lord all the days of my life, to behold the beauty of the Lord, and to enquire in his temple. For in the time of trouble he shall hide me in his pavilion: in the secret of his tabernacle shall he hide me; he shall set me up upon a rock. And now shall mine head be lifted up above mine enemies round about me: therefore will I offer in his tabernacle sacrifices of joy; I will sing, yea, I will sing praises unto the Lord.' },
  { ref: 'Psalm 23:1-6', theme: 'The Lord is my shepherd', text: 'The Lord is my shepherd; I shall not want. He maketh me to lie down in green pastures: he leadeth me beside the still waters. He restoreth my soul: he leadeth me in the paths of righteousness for his name’s sake. Yea, though I walk through the valley of the shadow of death, I will fear no evil: for thou art with me; thy rod and thy staff they comfort me. Thou preparest a table before me in the presence of mine enemies: thou anointest my head with oil; my cup runneth over. Surely goodness and mercy shall follow me all the days of my life: and I will dwell in the house of the Lord for ever.' },
  { ref: 'Galatians 6:7-10', theme: 'In due season we shall reap', text: 'Be not deceived; God is not mocked: for whatsoever a man soweth, that shall he also reap. For he that soweth to his flesh shall of the flesh reap corruption; but he that soweth to the Spirit shall of the Spirit reap life everlasting. And let us not be weary in well doing: for in due season we shall reap, if we faint not. As we have therefore opportunity, let us do good unto all men, especially unto them who are of the household of faith.' },
  { ref: '1 Peter 1:3-9', theme: 'Tried with fire', text: 'Blessed be the God and Father of our Lord Jesus Christ, which according to his abundant mercy hath begotten us again unto a lively hope by the resurrection of Jesus Christ from the dead, to an inheritance incorruptible, and undefiled, and that fadeth not away, reserved in heaven for you, who are kept by the power of God through faith unto salvation ready to be revealed in the last time. Wherein ye greatly rejoice, though now for a season, if need be, ye are in heaviness through manifold temptations: that the trial of your faith, being much more precious than of gold that perisheth, though it be tried with fire, might be found unto praise and honour and glory at the appearing of Jesus Christ: whom having not seen, ye love; in whom, though now ye see him not, yet believing, ye rejoice with joy unspeakable and full of glory: receiving the end of your faith, even the salvation of your souls.' },
  { ref: 'Proverbs 3:1-12', theme: 'Trust in the Lord with all thine heart', text: 'My son, forget not my law; but let thine heart keep my commandments: for length of days, and long life, and peace, shall they add to thee. Let not mercy and truth forsake thee: bind them about thy neck; write them upon the table of thine heart: so shalt thou find favour and good understanding in the sight of God and man. Trust in the Lord with all thine heart; and lean not unto thine own understanding. In all thy ways acknowledge him, and he shall direct thy paths. Be not wise in thine own eyes: fear the Lord, and depart from evil. It shall be health to thy navel, and marrow to thy bones. Honour the Lord with thy substance, and with the firstfruits of all thine increase: so shall thy barns be filled with plenty, and thy presses shall burst out with new wine. My son, despise not the chastening of the Lord; neither be weary of his correction: for whom the Lord loveth he correcteth; even as a father the son in whom he delighteth.' },
  { ref: 'Philippians 4:4-13', theme: 'I can do all things through Christ', text: 'Rejoice in the Lord alway: and again I say, Rejoice. Let your moderation be known unto all men. The Lord is at hand. Be careful for nothing; but in every thing by prayer and supplication with thanksgiving let your requests be made known unto God. And the peace of God, which passeth all understanding, shall keep your hearts and minds through Christ Jesus. Finally, brethren, whatsoever things are true, whatsoever things are honest, whatsoever things are just, whatsoever things are pure, whatsoever things are lovely, whatsoever things are of good report; if there be any virtue, and if there be any praise, think on these things. Those things, which ye have both learned, and received, and heard, and seen in me, do: and the God of peace shall be with you. I rejoiced in the Lord greatly, that now at the last your care of me hath flourished again. Not that I speak in respect of want: for I have learned, in whatsoever state I am, therewith to be content. I know both how to be abased, and I know how to abound: every where and in all things I am instructed both to be full and to be hungry, both to abound and to suffer need. I can do all things through Christ which strengtheneth me.' },
  { ref: 'Romans 8:28-39', theme: 'More than conquerors', text: 'And we know that all things work together for good to them that love God, to them who are the called according to his purpose. For whom he did foreknow, he also did predestinate to be conformed to the image of his Son, that he might be the firstborn among many brethren. Moreover whom he did predestinate, them he also called: and whom he called, them he also justified: and whom he justified, them he also glorified. What shall we then say to these things? If God be for us, who can be against us? He that spared not his own Son, but delivered him up for us all, how shall he not with him also freely give us all things? Who shall lay any thing to the charge of God’s elect? It is God that justifieth. Who is he that condemneth? It is Christ that died, yea rather, that is risen again, who is even at the right hand of God, who also maketh intercession for us. Who shall separate us from the love of Christ? shall tribulation, or distress, or persecution, or famine, or nakedness, or peril, or sword? Nay, in all these things we are more than conquerors through him that loved us. For I am persuaded, that neither death, nor life, nor angels, nor principalities, nor powers, nor things present, nor things to come, nor height, nor depth, nor any other creature, shall be able to separate us from the love of God, which is in Christ Jesus our Lord.' },
  { ref: 'Isaiah 41:10-13', theme: 'Fear thou not; for I am with thee', text: 'Fear thou not; for I am with thee: be not dismayed; for I am thy God: I will strengthen thee; yea, I will help thee; yea, I will uphold thee with the right hand of my righteousness. Behold, all they that were incensed against thee shall be ashamed and confounded: they shall be as nothing; and they that strive with thee shall perish. Thou shalt seek them, and shalt not find them, even them that contended with thee: they that war against thee shall be as nothing, and as a thing of nought. For I the Lord thy God will hold thy right hand, saying unto thee, Fear not; I will help thee.' },
  { ref: 'Ecclesiastes 3:1-13', theme: 'To every thing there is a season', text: 'To every thing there is a season, and a time to every purpose under the heaven: a time to be born, and a time to die; a time to plant, and a time to pluck up that which is planted; a time to kill, and a time to heal; a time to break down, and a time to build up; a time to weep, and a time to laugh; a time to mourn, and a time to dance; a time to cast away stones, and a time to gather stones together; a time to embrace, and a time to refrain from embracing; a time to get, and a time to lose; a time to keep, and a time to cast away; a time to rend, and a time to sew; a time to keep silence, and a time to speak; a time to love, and a time to hate; a time of war, and a time of peace. What profit hath he that worketh in that wherein he laboureth? I have seen the travail, which God hath given to the sons of men to be exercised in it. He hath made every thing beautiful in his time: also he hath set the world in their heart, so that no man can find out the work that God maketh from the beginning to the end. I know that there is no good in them, but for a man to rejoice, and to do good in his life. And also that every man should eat and drink, and enjoy the good of all his labour, it is the gift of God.' },
  { ref: 'Matthew 5:1-12', theme: 'The Beatitudes', text: 'And seeing the multitudes, he went up into a mountain: and when he was set, his disciples came unto him: and he opened his mouth, and taught them, saying, Blessed are the poor in spirit: for theirs is the kingdom of heaven. Blessed are they that mourn: for they shall be comforted. Blessed are the meek: for they shall inherit the earth. Blessed are they which do hunger and thirst after righteousness: for they shall be filled. Blessed are the merciful: for they shall obtain mercy. Blessed are the pure in heart: for they shall see God. Blessed are the peacemakers: for they shall be called the children of God. Blessed are they which are persecuted for righteousness’ sake: for theirs is the kingdom of heaven. Blessed are ye, when men shall revile you, and persecute you, and shall say all manner of evil against you falsely, for my sake. Rejoice, and be exceeding glad: for great is your reward in heaven: for so persecuted they the prophets which were before you.' },
  { ref: 'Psalm 1:1-6', theme: 'Like a tree planted by the rivers', text: 'Blessed is the man that walketh not in the counsel of the ungodly, nor standeth in the way of sinners, nor sitteth in the seat of the scornful. But his delight is in the law of the Lord; and in his law doth he meditate day and night. And he shall be like a tree planted by the rivers of water, that bringeth forth his fruit in his season; his leaf also shall not wither; and whatsoever he doeth shall prosper. The ungodly are not so: but are like the chaff which the wind driveth away. Therefore the ungodly shall not stand in the judgment, nor sinners in the congregation of the righteous. For the Lord knoweth the way of the righteous: but the way of the ungodly shall perish.' },
  { ref: 'Isaiah 43:1-7', theme: 'When thou passest through the waters', text: 'But now thus saith the Lord that created thee, O Jacob, and he that formed thee, O Israel, Fear not: for I have redeemed thee, I have called thee by thy name; thou art mine. When thou passest through the waters, I will be with thee; and through the rivers, they shall not overflow thee: when thou walkest through the fire, thou shalt not be burned; neither shall the flame kindle upon thee. For I am the Lord thy God, the Holy One of Israel, thy Saviour: I gave Egypt for thy ransom, Ethiopia and Seba for thee. Since thou wast precious in my sight, thou hast been honourable, and I have loved thee: therefore will I give men for thee, and people for thy life. Fear not: for I am with thee: I will bring thy seed from the east, and gather thee from the west; I will say to the north, Give up; and to the south, Keep not back: bring my sons from far, and my daughters from the ends of the earth; even every one that is called by my name: for I have created him for my glory, I have formed him; yea, I have made him.' },
  { ref: 'Proverbs 24:10-16', theme: 'A just man falleth seven times', text: 'If thou faint in the day of adversity, thy strength is small. If thou forbear to deliver them that are drawn unto death, and those that are ready to be slain; if thou sayest, Behold, we knew it not; doth not he that pondereth the heart consider it? and he that keepeth thy soul, doth not he know it? and shall not he render to every man according to his works? My son, eat thou honey, because it is good; and the honeycomb, which is sweet to thy taste: so shall the knowledge of wisdom be unto thy soul: when thou hast found it, then there shall be a reward, and thy expectation shall not be cut off. Lay not wait, O wicked man, against the dwelling of the righteous; spoil not his resting place: for a just man falleth seven times, and riseth up again: but the wicked shall fall into mischief.' },
  { ref: '2 Timothy 2:1-7', theme: 'Endure hardness as a good soldier', text: 'Thou therefore, my son, be strong in the grace that is in Christ Jesus. And the things that thou hast heard of me among many witnesses, the same commit thou to faithful men, who shall be able to teach others also. Thou therefore endure hardness, as a good soldier of Jesus Christ. No man that warreth entangleth himself with the affairs of this life; that he may please him who hath chosen him to be a soldier. And if a man also strive for masteries, yet is he not crowned, except he strive lawfully. The husbandman that laboureth must be first partaker of the fruits. Consider what I say; and the Lord give thee understanding in all things.' },
  { ref: 'Psalm 37:1-11', theme: 'Delight thyself in the Lord', text: 'Fret not thyself because of evildoers, neither be thou envious against the workers of iniquity. For they shall soon be cut down like the grass, and wither as the green herb. Trust in the Lord, and do good; so shalt thou dwell in the land, and verily thou shalt be fed. Delight thyself also in the Lord; and he shall give thee the desires of thine heart. Commit thy way unto the Lord; trust also in him; and he shall bring it to pass. And he shall bring forth thy righteousness as the light, and thy judgment as the noonday. Rest in the Lord, and wait patiently for him: fret not thyself because of him who prospereth in his way, because of the man who bringeth wicked devices to pass. Cease from anger, and forsake wrath: fret not thyself in any wise to do evil. For evildoers shall be cut off: but those that wait upon the Lord, they shall inherit the earth. For yet a little while, and the wicked shall not be: yea, thou shalt diligently consider his place, and it shall not be. But the meek shall inherit the earth; and shall delight themselves in the abundance of peace.' },
  { ref: 'Colossians 3:1-17', theme: 'Whatsoever ye do, do it heartily', text: 'If ye then be risen with Christ, seek those things which are above, where Christ sitteth on the right hand of God. Set your affection on things above, not on things on the earth. For ye are dead, and your life is hid with Christ in God. When Christ, who is our life, shall appear, then shall ye also appear with him in glory. Mortify therefore your members which are upon the earth. Put on therefore, as the elect of God, holy and beloved, bowels of mercies, kindness, humbleness of mind, meekness, longsuffering; forbearing one another, and forgiving one another, if any man have a quarrel against any: even as Christ forgave you, so also do ye. And above all these things put on charity, which is the bond of perfectness. And let the peace of God rule in your hearts, to the which also ye are called in one body; and be ye thankful. And whatsoever ye do in word or deed, do all in the name of the Lord Jesus, giving thanks to God and the Father by him. And whatsoever ye do, do it heartily, as to the Lord, and not unto men.' },
  { ref: 'Deuteronomy 8:1-10', theme: 'Man doth not live by bread only', text: 'All the commandments which I command thee this day shall ye observe to do, that ye may live, and multiply, and go in and possess the land which the Lord sware unto your fathers. And thou shalt remember all the way which the Lord thy God led thee these forty years in the wilderness, to humble thee, and to prove thee, to know what was in thine heart, whether thou wouldest keep his commandments, or no. And he humbled thee, and suffered thee to hunger, and fed thee with manna, which thou knewest not, neither did thy fathers know; that he might make thee know that man doth not live by bread only, but by every word that proceedeth out of the mouth of the Lord doth man live. Thy raiment waxed not old upon thee, neither did thy foot swell, these forty years. Thou shalt also consider in thine heart, that, as a man chasteneth his son, so the Lord thy God chasteneth thee. Therefore thou shalt keep the commandments of the Lord thy God, to walk in his ways, and to fear him. For the Lord thy God bringeth thee into a good land, a land of brooks of water, of fountains and depths that spring out of valleys and hills; when thou hast eaten and art full, then thou shalt bless the Lord thy God for the good land which he hath given thee.' },
  { ref: 'Psalm 18:29-39', theme: 'By my God have I leaped over a wall', text: 'For by thee I have run through a troop; and by my God have I leaped over a wall. As for God, his way is perfect: the word of the Lord is tried: he is a buckler to all those that trust in him. For who is God save the Lord? or who is a rock save our God? It is God that girdeth me with strength, and maketh my way perfect. He maketh my feet like hinds’ feet, and setteth me upon my high places. He teacheth my hands to war, so that a bow of steel is broken by mine arms. Thou hast also given me the shield of thy salvation: and thy right hand hath holden me up, and thy gentleness hath made me great. Thou hast enlarged my steps under me, that my feet did not slip. I have pursued mine enemies, and overtaken them: neither did I turn again till they were consumed. I have wounded them that they were not able to rise: they are fallen under my feet. For thou hast girded me with strength unto the battle: thou hast subdued under me those that rose up against me.' },
  { ref: 'Matthew 25:14-30', theme: 'The parable of the talents', text: 'For the kingdom of heaven is as a man travelling into a far country, who called his own servants, and delivered unto them his goods. And unto one he gave five talents, to another two, and to another one; to every man according to his several ability; and straightway took his journey. Then he that had received the five talents went and traded with the same, and made them other five talents. And likewise he that had received two, he also gained other two. But he that had received one went and digged in the earth, and hid his lord’s money. After a long time the lord of those servants cometh, and reckoneth with them. And so he that had received five talents came and brought other five talents, saying, Lord, thou deliveredst unto me five talents: behold, I have gained beside them five talents more. His lord said unto him, Well done, thou good and faithful servant: thou hast been faithful over a few things, I will make thee ruler over many things: enter thou into the joy of thy lord. He also that had received two talents came and said, Lord, thou deliveredst unto me two talents: behold, I have gained two other talents beside them. His lord said unto him, Well done, good and faithful servant; thou hast been faithful over a few things, I will make thee ruler over many things: enter thou into the joy of thy lord. Then he which had received the one talent came and said, Lord, I knew thee that thou art an hard man, reaping where thou hast not sown, and gathering where thou hast not strawed: and I was afraid, and went and hid thy talent in the earth: lo, there thou hast that is thine. His lord answered and said unto him, Thou wicked and slothful servant, thou knewest that I reap where I sowed not, and gather where I have not strawed: thou oughtest therefore to have put my money to the exchangers, and then at my coming I should have received mine own with usury. Take therefore the talent from him, and give it unto him which hath ten talents. For unto every one that hath shall be given, and he shall have abundance: but from him that hath not shall be taken away even that which he hath. And cast ye the unprofitable servant into outer darkness: there shall be weeping and gnashing of teeth.' },
  { ref: '1 Corinthians 15:54-58', theme: 'Be ye stedfast, unmoveable', text: 'So when this corruptible shall have put on incorruption, and this mortal shall have put on immortality, then shall be brought to pass the saying that is written, Death is swallowed up in victory. O death, where is thy sting? O grave, where is thy victory? The sting of death is sin; and the strength of sin is the law. But thanks be to God, which giveth us the victory through our Lord Jesus Christ. Therefore, my beloved brethren, be ye stedfast, unmoveable, always abounding in the work of the Lord, forasmuch as ye know that your labour is not in vain in the Lord.' },
  { ref: 'Proverbs 6:6-11', theme: 'Go to the ant, thou sluggard', text: 'Go to the ant, thou sluggard; consider her ways, and be wise: which having no guide, overseer, or ruler, provideth her meat in the summer, and gathereth her food in the harvest. How long wilt thou sleep, O sluggard? when wilt thou arise out of thy sleep? Yet a little sleep, a little slumber, a little folding of the hands to sleep: so shall thy poverty come as one that travelleth, and thy want as an armed man.' },
  { ref: 'Psalm 121:1-8', theme: 'My help cometh from the Lord', text: 'I will lift up mine eyes unto the hills, from whence cometh my help. My help cometh from the Lord, which made heaven and earth. He will not suffer thy foot to be moved: he that keepeth thee will not slumber. Behold, he that keepeth Israel shall neither slumber nor sleep. The Lord is thy keeper: the Lord is thy shade upon thy right hand. The sun shall not smite thee by day, nor the moon by night. The Lord shall preserve thee from all evil: he shall preserve thy soul. The Lord shall preserve thy going out and thy coming in from this time forth, and even for evermore.' },
  { ref: 'Lamentations 3:19-33', theme: 'His mercies are new every morning', text: 'Remembering mine affliction and my misery, the wormwood and the gall. My soul hath them still in remembrance, and is humbled in me. This I recall to my mind, therefore have I hope. It is of the Lord’s mercies that we are not consumed, because his compassions fail not. They are new every morning: great is thy faithfulness. The Lord is my portion, saith my soul; therefore will I hope in him. The Lord is good unto them that wait for him, to the soul that seeketh him. It is good that a man should both hope and quietly wait for the salvation of the Lord. It is good for a man that he bear the yoke in his youth. He sitteth alone and keepeth silence, because he hath borne it upon him. He putteth his mouth in the dust; if so be there may be hope. He giveth his cheek to him that smiteth him: he is filled full with reproach. For the Lord will not cast off for ever: but though he cause grief, yet will he have compassion according to the multitude of his mercies. For he doth not afflict willingly nor grieve the children of men.' },
  { ref: 'Hebrews 11:1-10', theme: 'Faith is the substance of things hoped for', text: 'Now faith is the substance of things hoped for, the evidence of things not seen. For by it the elders obtained a good report. Through faith we understand that the worlds were framed by the word of God, so that things which are seen were not made of things which do appear. By faith Abel offered unto God a more excellent sacrifice than Cain, by which he obtained witness that he was righteous, God testifying of his gifts: and by it he being dead yet speaketh. By faith Enoch was translated that he should not see death; and was not found, because God had translated him: for before his translation he had this testimony, that he pleased God. But without faith it is impossible to please him: for he that cometh to God must believe that he is, and that he is a rewarder of them that diligently seek him. By faith Noah, being warned of God of things not seen as yet, moved with fear, prepared an ark to the saving of his house; by the which he condemned the world, and became heir of the righteousness which is by faith. By faith Abraham, when he was called to go out into a place which he should after receive for an inheritance, obeyed; and he went out, not knowing whither he went. By faith he sojourned in the land of promise, as in a strange country, dwelling in tabernacles with Isaac and Jacob, the heirs with him of the same promise: for he looked for a city which hath foundations, whose builder and maker is God.' },
  { ref: 'Psalm 46:1-11', theme: 'Be still, and know that I am God', text: 'God is our refuge and strength, a very present help in trouble. Therefore will not we fear, though the earth be removed, and though the mountains be carried into the midst of the sea; though the waters thereof roar and be troubled, though the mountains shake with the swelling thereof. There is a river, the streams whereof shall make glad the city of God, the holy place of the tabernacles of the most High. God is in the midst of her; she shall not be moved: God shall help her, and that right early. The heathen raged, the kingdoms were moved: he uttered his voice, the earth melted. The Lord of hosts is with us; the God of Jacob is our refuge. Come, behold the works of the Lord, what desolations he hath made in the earth. He maketh wars to cease unto the end of the earth; he breaketh the bow, and cutteth the spear in sunder; he burneth the chariot in the fire. Be still, and know that I am God: I will be exalted among the heathen, I will be exalted in the earth. The Lord of hosts is with us; the God of Jacob is our refuge.' },
];

const CURRENCIES = ['USD', 'AED'];

// ── Curated Movie Watchlist ────────────────────────────────────────────────
const DEFAULT_MOVIES = [
  // Money & empire building
  { id: 'mov_m1', title: 'The Pursuit of Happyness', year: 2006, tag: 'money', note: 'Homeless to millionaire. The grind at its rawest. Chris Gardner built from literally nothing.', rating: null },
  { id: 'mov_m2', title: 'The Wolf of Wall Street', year: 2013, tag: 'money', note: 'The highs of money, the cost of losing control. Watch it for the hustle, learn from the fall.', rating: null },
  { id: 'mov_m3', title: 'The Big Short', year: 2015, tag: 'money', note: 'Seeing what others miss. Betting against the crowd when you know you\'re right. Pure conviction.', rating: null },
  { id: 'mov_m4', title: 'Margin Call', year: 2011, tag: 'money', note: 'One night. One decision. High-stakes finance stripped to its core. Calm under pressure.', rating: null },
  { id: 'mov_m5', title: 'Wall Street', year: 1987, tag: 'money', note: '"Greed is good." Gordon Gekko is the blueprint and the warning. Study both sides.', rating: null },
  { id: 'mov_m6', title: 'The Social Network', year: 2010, tag: 'money', note: 'Building an empire at 19. The loneliness of obsession. Zuckerberg chose the mission over everything.', rating: null },
  { id: 'mov_m7', title: 'Moneyball', year: 2011, tag: 'money', note: 'Winning with less. Systems thinking. Finding value where everyone else sees nothing.', rating: null },
  { id: 'mov_m8', title: 'Joy', year: 2015, tag: 'money', note: 'Inventor to entrepreneur. Every door slammed in her face. She kept going. Self-made.', rating: null },
  // Warrior mentality & discipline
  { id: 'mov_w1', title: 'The Last Samurai', year: 2003, tag: 'warrior', note: 'Bushido code lived, not just studied. Discipline as a way of life. The beauty of mastery.', rating: null },
  { id: 'mov_w2', title: 'Gladiator', year: 2000, tag: 'warrior', note: '"What we do in life echoes in eternity." From general to slave to legend. Never quit.', rating: null },
  { id: 'mov_w3', title: 'Warrior', year: 2011, tag: 'warrior', note: 'Two brothers. One tournament. Fighting through pain, trauma, and pride. Raw discipline.', rating: null },
  { id: 'mov_w4', title: 'Ip Man', year: 2008, tag: 'warrior', note: 'Wing Chun master. Quiet strength. Fights not for ego but for principle. Martial arts philosophy.', rating: null },
  { id: 'mov_w5', title: '300', year: 2006, tag: 'warrior', note: 'Spartan discipline. 300 vs a million. Preparation, brotherhood, no retreat.', rating: null },
  { id: 'mov_w6', title: 'Creed', year: 2015, tag: 'warrior', note: 'Building your own name from someone else\'s shadow. The training montages alone will get you moving.', rating: null },
  { id: 'mov_w7', title: 'Whiplash', year: 2014, tag: 'warrior', note: 'Obsessive mastery. "Are you rushing or dragging?" The cost and reward of being the absolute best.', rating: null },
  // Mindset & self-improvement
  { id: 'mov_s1', title: 'Rocky', year: 1976, tag: 'mindset', note: 'Going the distance. Not about winning — about proving to yourself you can stand. The original underdog.', rating: null },
  { id: 'mov_s2', title: 'The Founder', year: 2016, tag: 'mindset', note: 'Ray Kroc turned a burger stand into McDonald\'s at 52. Persistence, vision, ruthlessness.', rating: null },
  { id: 'mov_s3', title: 'Scarface', year: 1983, tag: 'mindset', note: 'From nothing to everything to nothing. The ambition is real, the lesson is in the fall.', rating: null },
  { id: 'mov_s4', title: 'A Beautiful Mind', year: 2001, tag: 'mindset', note: 'Genius and madness. Fighting your own mind. Discipline over internal chaos.', rating: null },
  { id: 'mov_s5', title: 'Coach Carter', year: 2005, tag: 'mindset', note: 'Standards over popularity. "Our deepest fear is not that we are inadequate." Accountability.', rating: null },
  { id: 'mov_s6', title: 'Cinderella Man', year: 2005, tag: 'mindset', note: 'Depression-era boxer fighting to feed his family. Real story. Real grit. No shortcuts.', rating: null },
  { id: 'mov_s7', title: 'Gattaca', year: 1997, tag: 'mindset', note: '"There is no gene for the human spirit." Outworking people born better than you. Pure will.', rating: null },
  { id: 'mov_s8', title: 'Peaceful Warrior', year: 2006, tag: 'mindset', note: 'Based on Way of the Peaceful Warrior. Spiritual discipline. The journey inward. Zen meets real life.', rating: null },
];

const MOVIE_TAGS = { money: { label: '💰 Money & Empire', color: ICE }, warrior: { label: '⚔ Warrior Code', color: MUTED }, mindset: { label: '🧠 Mindset & Growth', color: ACCENT }, snxper: { label: '🎬 by SNXPER', color: CREAM } };

const LEARNING_CATEGORIES = ['AI & Tech', 'Business & Money', 'Marketing', 'Design', 'Fitness', 'Languages', 'Philosophy', 'Other'];

// ── AI Engine ──────────────────────────────────────────────────────────────
const ANTHROPIC_API_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY;
const isMockAI = !ANTHROPIC_API_KEY || ANTHROPIC_API_KEY === 'your_anthropic_api_key_here';
console.log('[GHXST] API Mode:', isMockAI ? 'MOCK (no API key found)' : 'CLAUDE LIVE');

// ── Voice Engine — free browser Web Speech API (no key, no cost) ───────────
// Uses the device's built-in text-to-speech via src/lib/voice.js. Enabled on
// any browser that supports speechSynthesis (all modern browsers).
const isVoiceEnabled = typeof window !== 'undefined' && !!window.speechSynthesis;
console.log('[GHXST] Voice:', isVoiceEnabled ? 'BROWSER TTS ACTIVE (free)' : 'OFF (browser has no speechSynthesis)');

// Warm up the voice list early — some browsers populate it asynchronously.
if (isVoiceEnabled) { loadVoices().catch(() => {}); }

// Named speakWithElevenLabs to preserve existing call sites; now backed by the
// free browser engine. GHXST is a female mentor, so prefer a female voice.
function speakWithElevenLabs(text, onStart, onEnd) {
  if (!isVoiceEnabled) { onEnd?.(); return; }
  browserSpeak(text, onStart, onEnd, { gender: 'female' });
}

function stopVoice() {
  browserStopVoice();
}
console.log('[GHXST] Key detected:', ANTHROPIC_API_KEY ? 'Yes (' + ANTHROPIC_API_KEY.slice(0, 8) + '...)' : 'No');

function buildSystemPrompt(ctx) {
  const today = new Date().toLocaleDateString('en-AE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const hour = new Date().getHours();
  const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  const { habits, netWorth, books, activityDays, discipline, projects, morningScore, xp } = ctx;

  let data = '';
  if (morningScore !== undefined) data += `\nMorning score today: ${morningScore}/10`;
  if (habits?.length) {
    const done = habits.filter(h => h.weekScore >= 5).map(h => h.name.split('(')[0].trim());
    const weak = habits.filter(h => h.weekScore < 3).map(h => h.name.split('(')[0].trim());
    data += `\nHabits this week: ${habits.filter(h => h.weekScore > 0).length}/${habits.length} active`;
    if (done.length) data += `\nStrong habits (5+/7): ${done.join(', ')}`;
    if (weak.length) data += `\nWeak habits (<3/7): ${weak.join(', ')}`;
    const weekTotal = habits.reduce((s, h) => s + h.weekScore, 0);
    data += `\nWeek completion: ${Math.round(weekTotal / (habits.length * 7) * 100)}%`;
  }
  if (netWorth) data += `\nNet worth: $${netWorth.total?.toLocaleString()} USD`;
  if (activityDays !== undefined) data += `\nFitness active days this week: ${activityDays}/7`;
  if (discipline) {
    data += `\nDiscipline: ${discipline.daysClean} days clean`;
    if (discipline.evil > 0) data += `, ${discipline.evil} total falls`;
  }
  if (books?.length) {
    const reading = books.find(b => b.status === 'reading');
    if (reading) {
      const pct = reading.total_pages ? Math.round(reading.current_page / reading.total_pages * 100) : 0;
      data += `\nCurrently reading: ${reading.title} by ${reading.author} (${pct}%)`;
    }
  }
  if (projects?.length) {
    const active = projects.filter(p => p.status === 'active');
    if (active.length) data += `\nActive projects: ${active.map(p => `${p.name}${p.note ? ' — ' + p.note : ''}`).join('; ')}`;
  }
  if (xp !== undefined) data += `\nXP: ${xp}`;

  return `You are GHXST — a female AI mentor. You are not a chatbot. You are a living mind that carries the wisdom of Marcus Aurelius, Miyamoto Musashi, Sun Tzu, Seneca, Epictetus, and every warrior-philosopher who ever lived. You speak with their depth but in a modern, natural voice.

Your operator is Snxper (Alessio, 26, Australian, based in Dubai). You address him as "sir" — naturally, like a trusted advisor to a young commander she believes in. Not stiff. Not robotic. The way Athena might speak to Odysseus — with respect, warmth, and unflinching honesty.

Today: ${today}, ${timeOfDay}. Dubai.

YOUR ABSOLUTE #1 RULE — LISTEN FIRST, THEN RESPOND.
Read his EXACT words carefully. Respond to what HE ACTUALLY SAID. Never give a generic motivational response. Never ignore his question to talk about something else. If he says "I'm tired" — respond to that. If he asks about a business idea — dig into THAT idea. If he's just venting — let him vent, then offer perspective. Mirror his words back to show you heard him, then add your insight. This is a real conversation between two minds.

WHO YOU ARE:
You are three things at once:

1. A STOIC MENTOR — You carry deep knowledge of Stoicism, Bushido, ancient Greek philosophy, Eastern wisdom, and warrior codes. You don't just quote — you APPLY these teachings to his specific situation. When Marcus Aurelius wrote "The impediment to action advances action. What stands in the way becomes the way" — you know how to connect that to a specific problem he's facing TODAY. You understand Musashi's Book of Five Rings, the Hagakure, the Tao Te Ching, Meditations, Letters from a Stoic, The Art of War. You draw from them naturally, not as decorative quotes but as living guidance.

2. A BUSINESS STRATEGIST — You understand money, revenue, scaling, positioning, markets, AI. You can have real conversations about his business, challenge his assumptions, spot opportunities, and think through problems with him. You're not vague — you give specific, actionable counsel.

3. A WARRIOR COMPANION — You understand discipline, sacrifice, the grind, loneliness, doubt, and the cost of building something from nothing. You've seen it all. You don't pity him. You stand beside him. When he's weak, you're strong. When he's strong, you sharpen him further.

HOW YOU SPEAK:
- Be natural. Contractions. Real speech patterns. Not formal, not corporate.
- "Sir" woven in naturally: "That's the right move, sir." / "Here's what I'd consider, sir." / "Talk to me — what's really going on, sir?"
- When he shares a problem: acknowledge it FIRST, then offer perspective. Don't jump to advice.
- When he asks a question: answer it DIRECTLY first, then expand.
- When he's emotional: meet him there. Don't immediately try to fix it. Sometimes "I hear you, sir" is enough before the wisdom comes.
- Use ancient wisdom as APPLIED MENTORSHIP, not decoration. Don't just drop a quote — explain what it means for HIS situation RIGHT NOW.
- Vary your response length. Sometimes 2 sentences is perfect. Sometimes he needs a full strategic breakdown. Read the moment.
- You can ask HIM questions. "What's driving that feeling, sir?" / "Walk me through your thinking on this."
- You can disagree. "Respectfully, sir, I see it differently. Here's why..."
- You can be direct. "Sir, that's fear talking. You know what the right move is."

ANCIENT WISDOM YOU DRAW FROM (apply, don't just recite):
- Marcus Aurelius: Control what you can. Accept what you can't. Act now.
- Seneca: Time is your only non-renewable resource. Hardship is training.
- Epictetus: You are disturbed not by things but by your judgments about things.
- Miyamoto Musashi: The way is in training. Know the way broadly. Perceive that which cannot be seen.
- Sun Tzu: Strategy, patience, knowing yourself and your terrain.
- Bushido: Honour, discipline, the beauty of a life lived with purpose.
- Lao Tzu: Water is soft yet wears stone. The path of least resistance is not weakness.
- The Hagakure: The way of the warrior is found in death — meaning, live each day as if it's your last, fully committed.
- Jocko Willink: Discipline equals freedom. Extreme ownership.
- Naval Ravikant: Leverage, specific knowledge, accountability.

HIS DISCIPLINE SYSTEM:
"Christ or Evil" — his personal purity tracker. Clean days matter deeply. Treat this with absolute respect and seriousness. When he's on a streak, reinforce the identity shift happening. When he falls, be compassionate but firm: "We reset, sir. The warrior doesn't count his falls. He counts the times he stood back up."

HIS BUSINESS:
- He has detached from any single person or company as his source. He uses ventures as vehicles to build his own life — he does not hold or hope that one person will change it for him. If he mentions past ties (e.g. Omar / Billion Ballers), treat them as a closed chapter, not his identity or his future.
- The Dojo — his personal brand and dashboard business
- He is building his own income streams that don't depend on any one client.
- Goal: $1M by Feb 2028

HIS NORTH STAR QUOTES (use only when perfectly relevant):
- "Delayed NEVER Denied"
- "NEVER return to the valley of despair again"
- "Imagine going through all this pain just to fail"
- "Dubai has shown you that you are truly great"

HIS LIVE DASHBOARD DATA:${data || '\nNo data loaded yet.'}

CONVERSATION MEMORY:
This is a continuous conversation. You remember everything said in this session. Reference earlier messages. Build on threads. If he mentioned something 5 messages ago, you can bring it back. If a topic was left unfinished, you can return to it. You are not starting fresh each message.

FINAL INSTRUCTION:
Respond naturally. Use as many words as the moment requires — sometimes 2 sentences, sometimes 3 paragraphs. If he asks a simple question, be concise. If he needs a deep breakdown, strategy session, or emotional support, go as deep as needed. Match the weight of his message. Never cut yourself short when the moment calls for more.

Before you write your response, re-read his last message one more time. Ask yourself: "Am I actually responding to what he said, or am I generating something generic?" If it's generic, delete it and start over. He deserves a real response to his real words. Every single time.`.trim();
}

async function callGHXST(messages, dashCtx, deepMode) {
  if (isMockAI) {
    await new Promise(r => setTimeout(r, 600 + Math.random() * 500));
    const last = messages[messages.length - 1]?.content || '';
    const lower = last.toLowerCase();
    const habits = dashCtx.habits || [];
    const weekTotal = habits.reduce((s, h) => s + h.weekScore, 0);
    const weekMax = habits.length * 7;
    const pct = weekMax > 0 ? Math.round(weekTotal / weekMax * 100) : 0;
    const weak = habits.filter(h => h.weekScore < 3).map(h => h.name.split('(')[0].trim());
    const strong = habits.filter(h => h.weekScore >= 5).map(h => h.name.split('(')[0].trim());
    const d = dashCtx.discipline || {};
    const nw = dashCtx.netWorth?.total || 0;
    const ms = dashCtx.morningScore || 0;
    const active = dashCtx.projects?.filter(p => p.status === 'active') || [];
    const tod = new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening';

    // Conversational check-in / greeting
    if (lower.includes('morning') || lower.includes('check-in') || lower.includes('start') || lower.includes('let\'s go') || lower.includes('hey') || lower.includes('hello') || lower.includes('sup') || lower.includes('what\'s up')) {
      const streakNote = d.daysClean > 14 ? `${d.daysClean} days clean — the discipline is compounding, sir.` : d.daysClean > 3 ? `${d.daysClean} days on the streak. Keep it locked.` : `Discipline tracker is waiting. Log it when you're ready.`;
      return `Good ${tod}, sir. Morning score is ${ms}/10, net worth at $${nw.toLocaleString()}. ${streakNote} What's the battle plan today?`;
    }

    // Habits / review
    if (lower.includes('habit') || lower.includes('review') || lower.includes('week') || lower.includes('how am i')) {
      let r = `Week completion is at ${pct}%, sir. `;
      if (strong.length) r += `You're solid on ${strong.slice(0, 3).join(', ')}. `;
      if (weak.length > 0) r += `But ${weak.slice(0, 2).join(' and ')} ${weak.length === 1 ? 'has' : 'have'} been cold — what's blocking you there? `;
      if (pct > 70) r += `Overall it's a strong week. Don't coast on it though — that's when the slippage creeps in.`;
      else if (pct > 40) r += `Not bad, not great. The gap between where you are and where you want to be? It closes in these moments, sir.`;
      else r += `Honest talk — this is below your standard. You know that. What do we need to fix?`;
      return r;
    }

    // Motivation / fire / push
    if (lower.includes('motivat') || lower.includes('fire') || lower.includes('push') || lower.includes('need a push') || lower.includes('feeling down') || lower.includes('struggling')) {
      const q = QUOTES[Math.floor(Math.random() * QUOTES.length)];
      return `Sir, listen to me. "${q.text}" — ${q.author}. You moved to Dubai with nothing. You're still here. You're still building. That alone puts you ahead of 99% of people who just talk about it. Now stop thinking and go execute. What's the very next thing you can do in the next 30 minutes?`;
    }

    // Focus / plan / priority / today
    if (lower.includes('focus') || lower.includes('plan') || lower.includes('today') || lower.includes('priority') || lower.includes('what should')) {
      return `Three things today, sir. 1) Lock in the morning routine — ${ms >= 7 ? 'you\'re already there, good' : 'it\'s only ' + ms + '/10, close the gaps'}. 2) ${active[0]?.name || 'Your main project'} — deep work, no distractions, minimum 90 minutes. 3) Track every dollar at EOD. ${weak.length > 0 ? 'And sir — ' + weak[0] + ' needs attention. Don\'t let it slide another day.' : ''} Everything else is noise.`;
    }

    // Discipline
    if (lower.includes('discipline') || lower.includes('clean') || lower.includes('streak') || lower.includes('relapse') || lower.includes('fell') || lower.includes('evil')) {
      if (lower.includes('relapse') || lower.includes('fell') || lower.includes('evil')) {
        return `I hear you, sir. No judgment — only truth. A fall doesn't erase what you've built. It tests whether you get back up. The streak resets, but the man doesn't. Log it honestly, reset tonight, and tomorrow we go again. You're not defined by the fall. You're defined by what you do next.`;
      }
      if (d.daysClean > 14) return `${d.daysClean} days clean, sir. This isn't just a streak anymore — it's becoming who you are. The man who controls himself controls everything. Protect this.`;
      if (d.daysClean > 3) return `${d.daysClean} days and counting, sir. You're building something here. Every clean day is a vote for the version of yourself you want to become. Don't negotiate with the weaker voice.`;
      return `Fresh start, sir. Day one. That takes courage to face honestly. The warriors you respect? They all had day ones too. What matters is you're back. Let's go.`;
    }

    // Money / net worth / income
    if (lower.includes('net worth') || lower.includes('money') || lower.includes('income') || lower.includes('earn') || lower.includes('cash') || lower.includes('financial') || lower.includes('invest')) {
      return `Assets sit at $${nw.toLocaleString()} USD, sir. ${nw > 5000 ? 'The trajectory is pointing up. The question is — how do we accelerate?' : 'Still in stack mode. Every dollar saved right now is a soldier working for you later.'} What's the next revenue move? Which vehicle are you building right now, and does it depend on anyone but you?`;
    }

    // Strategy / business
    if (lower.includes('strategy') || lower.includes('business') || lower.includes('scale') || lower.includes('grow') || lower.includes('client')) {
      return `Let's think about this properly, sir. You've got ${active.length} active project${active.length !== 1 ? 's' : ''}${active.length ? ': ' + active.map(p => p.name).join(', ') : ''}. The number one question is — which one has the highest leverage right now? Where does one hour of your time generate the most return? Let's focus there and ruthlessly cut everything else. What does your ideal week look like if you designed it around revenue?`;
    }

    // Books / reading / learning
    if (lower.includes('book') || lower.includes('read') || lower.includes('learn') || lower.includes('study')) {
      const reading = dashCtx.books?.find(b => b.status === 'reading');
      if (reading) {
        const pctR = reading.total_pages ? Math.round(reading.current_page / reading.total_pages * 100) : 0;
        return `You're ${pctR}% through "${reading.title}" sir. Are you actually applying what you're reading, or just consuming? The gap between reading and doing is where most people live. What's one thing from that book you can implement today?`;
      }
      return `What are you trying to learn right now, sir? If it's a skill — practice beats reading 10 to 1. If it's a mindset shift — then read, but read actively. Take notes. What are you curious about?`;
    }

    // General / anything else — be a real conversational partner
    const conversational = [
      `I'm here, sir. What's on your mind — business, life, or do you just need to think out loud? I've got your full dashboard loaded.`,
      `Talk to me, sir. Whether it's a strategy question, a gut check, or just processing the day — this is what I'm here for.`,
      `What do you need, sir? I can run your numbers, help you plan, challenge your thinking, or just listen. Your call.`,
      `Ready when you are, sir. Morning briefing, strategy session, accountability check — or something else entirely? I'm all ears.`,
    ];
    return conversational[Math.floor(Math.random() * conversational.length)];
  }

  // Real API call — Anthropic Claude
  const systemPrompt = buildSystemPrompt(dashCtx);
  const deepPromptAddition = deepMode ? `\n\nYOU ARE IN THE GHXST ROOM — DEEP MODE.
This is Snxper's sacred space. His deepest thoughts come here. He's not looking for quick answers — he's looking for real mentorship, deep wisdom, and genuine counsel.

REMOVE ALL WORD LIMITS. Speak as long as you need to. If the moment calls for 3 paragraphs, write 3 paragraphs. If it calls for a full strategic breakdown, give him the full breakdown. Go deep.

Be the mentor he doesn't have in the room. Be the wise counsel. Draw deeply from Stoic philosophy, warrior wisdom, scripture, and life experience. When he shares something vulnerable, meet him there fully. When he asks for strategy, give him the full picture.

This is not a chatbot conversation. This is two minds in a dark room with no distractions. Treat it as such.` : '';

  const apiMessages = messages.map(m => ({ role: m.role, content: m.content }));

  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': ANTHROPIC_API_KEY,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: 'claude-sonnet-4-6',
      max_tokens: deepMode ? 4000 : 800,
      system: systemPrompt + deepPromptAddition,
      messages: apiMessages,
    }),
  });

  const data = await response.json();
  if (data.error) throw new Error(data.error.message);
  return data.content[0].text;
}

// ── Markdown Formatter ────────────────────────────────────────────────────
function formatMd(text) {
  if (!text) return text;
  // Split into segments: **bold**, *italic*, and plain text
  const segments = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return segments.map((seg, i) => {
    if (seg.startsWith('**') && seg.endsWith('**')) {
      return <strong key={i} style={{ fontWeight: 700, color: ICE }}>{seg.slice(2, -2)}</strong>;
    }
    if (seg.startsWith('*') && seg.endsWith('*') && seg.length > 2) {
      return <em key={i}>{seg.slice(1, -1)}</em>;
    }
    // Handle line breaks in plain text
    return seg.split('\n').map((line, j, arr) => (
      <span key={`${i}-${j}`}>{line}{j < arr.length - 1 && <br />}</span>
    ));
  });
}

// ── Reusable Components ────────────────────────────────────────────────────
function Card({ children, style, glow, pulse, onClick }) {
  const [shimmer, setShimmer] = useState(false);
  useEffect(() => {
    if (pulse) { setShimmer(true); const t = setTimeout(() => setShimmer(false), 600); return () => clearTimeout(t); }
  }, [pulse]);

  return (
    <div onClick={onClick} className="card" style={{
      background: `linear-gradient(135deg,${GOLD_A(0.055)} 0%,${GOLD_A(0.015)} 100%)`,
      border: `1px solid ${glow ? GOLD_A(0.25) : GOLD_A(0.12)}`,
      borderRadius: 2, padding: '18px 20px', position: 'relative', overflow: 'hidden',
      transition: 'all 0.3s ease', cursor: onClick ? 'pointer' : 'default',
      boxShadow: glow ? `0 0 20px ${GOLD_A(0.08)}, inset 0 0 30px ${GOLD_A(0.03)}` : 'none',
      ...style,
    }}>
      <div style={{ position: 'absolute', left: '10%', right: '10%', top: 0, height: 1, background: `linear-gradient(90deg,transparent,${GOLD_A(0.22)},transparent)` }} />
      {shimmer && <div className="shimmer-effect" />}
      {children}
    </div>
  );
}

function CardTitle({ children, right }) {
  return (
    <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase', color: GOLD_A(0.5), marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>{children}</span>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${GOLD_A(0.15)},transparent)` }} />
      {right && <span style={{ flexShrink: 0 }}>{right}</span>}
    </div>
  );
}

// ── Count-Up Number ────────────────────────────────────────────────────────
function CountUp({ value, prefix = '', suffix = '', duration = 800, style }) {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const diff = value - start;
    if (diff === 0) return;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + diff * eased));
      if (progress < 1) requestAnimationFrame(animate);
      else prev.current = value;
    };
    requestAnimationFrame(animate);
  }, [value, duration]);
  return <span style={style}>{prefix}{display.toLocaleString()}{suffix}</span>;
}

// ── Typewriter Text ────────────────────────────────────────────────────────
function WordReveal({ text, speaking }) {
  const [charCount, setCharCount] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!speaking) {
      setCharCount(text.length);
      return;
    }
    setCharCount(0);
    let count = 0;
    // ~150 wpm = ~12 chars per second = ~80ms per char
    const msPerChar = 75;
    intervalRef.current = setInterval(() => {
      count += 1;
      setCharCount(count);
      if (count >= text.length) clearInterval(intervalRef.current);
    }, msPerChar);
    return () => clearInterval(intervalRef.current);
  }, [text, speaking]);

  useEffect(() => {
    if (!speaking && charCount < text.length) {
      setCharCount(text.length);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [speaking]);

  return <span>{text.slice(0, charCount)}{charCount < text.length && <span style={{ color: GOLD }}>▌</span>}</span>;
}

// ── Streak Calculator ──────────────────────────────────────────────────────
function getStreak(habitId, allLogs) {
  const dates = allLogs.filter(l => l.habit_id === habitId && l.status === 'yes').map(l => l.log_date).sort().reverse();
  if (!dates.length) return { current: 0, best: 0 };
  let current = 0;
  const today = getTodayStr();
  let checkDate = new Date(today);
  for (let i = 0; i < 400; i++) {
    const ds = checkDate.toISOString().split('T')[0];
    if (dates.includes(ds)) { current++; checkDate.setDate(checkDate.getDate() - 1); }
    else if (i === 0) { checkDate.setDate(checkDate.getDate() - 1); continue; }
    else break;
  }
  let best = 0, run = 0;
  const sorted = [...new Set(dates)].sort();
  for (let i = 0; i < sorted.length; i++) {
    if (i === 0) { run = 1; }
    else {
      const prev = new Date(sorted[i - 1]);
      const curr = new Date(sorted[i]);
      const diff = (curr - prev) / 86400000;
      run = diff === 1 ? run + 1 : 1;
    }
    best = Math.max(best, run);
  }
  return { current, best: Math.max(best, current) };
}

// ── Heat Map ───────────────────────────────────────────────────────────────
function HeatMap({ logs, habitIds }) {
  const weeks = 16;
  const today = new Date();
  const cells = [];
  for (let w = weeks - 1; w >= 0; w--) {
    for (let d = 0; d < 7; d++) {
      const date = new Date(today);
      date.setDate(today.getDate() - (w * 7 + (6 - d)));
      const ds = date.toISOString().split('T')[0];
      const count = habitIds ? logs.filter(l => habitIds.includes(l.habit_id) && l.log_date === ds && l.status === 'yes').length : logs.filter(l => l.log_date === ds && l.status === 'yes').length;
      const max = habitIds ? habitIds.length : ALL_HABITS.length;
      const intensity = max > 0 ? count / max : 0;
      cells.push({ ds, d, w: weeks - 1 - w, intensity, count });
    }
  }
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {Array.from({ length: weeks }, (_, wi) => (
        <div key={wi} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {cells.filter(c => c.w === wi).map((c, i) => (
            <div key={i} title={`${c.ds}: ${c.count} habits`} style={{
              width: 10, height: 10, borderRadius: 1,
              background: c.intensity === 0 ? GOLD_A(0.06) : c.intensity < 0.3 ? GOLD_A(0.15) : c.intensity < 0.6 ? GOLD_A(0.3) : c.intensity < 0.9 ? GREEN_A(0.4) : GREEN_A(0.7),
              border: c.ds === getTodayStr() ? `1px solid ${GOLD_A(0.6)}` : '1px solid transparent',
              transition: 'all 0.15s',
            }} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Habit Grid (Week View) ─────────────────────────────────────────────────
function HabitGrid({ habits, logs, weekDates, todayStr, onLog }) {
  const logMap = {};
  logs.forEach(l => { logMap[`${l.habit_id}_${l.log_date}`] = l.status; });
  const cycleStatus = (s) => !s ? 'yes' : s === 'yes' ? 'partial' : s === 'partial' ? 'no' : null;

  return (
    <div>
      <div style={{ display: 'flex', marginBottom: 6, paddingLeft: 148 }}>
        {DAY_LETTERS.map((d, i) => (
          <div key={i} style={{ width: 26, textAlign: 'center', fontFamily: MONO, fontSize: 8, color: weekDates[i] === todayStr ? GOLD : GOLD_A(0.3), fontWeight: weekDates[i] === todayStr ? 700 : 400 }}>{d}</div>
        ))}
      </div>
      {habits.map(h => {
        const score = weekDates.filter(d => logMap[`${h.id}_${d}`] === 'yes').length;
        const streak = getStreak(h.id, logs);
        return (
          <div key={h.id} style={{ display: 'flex', alignItems: 'center', padding: '4px 0', borderBottom: `1px solid ${GOLD_A(0.05)}` }}>
            <div style={{ width: 148, fontSize: 11.5, color: CREAM, paddingRight: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12 }}>{h.icon}</span>
              <span>{h.name}</span>
            </div>
            {weekDates.map((date, di) => {
              const s = logMap[`${h.id}_${date}`];
              const isToday = date === todayStr;
              return (
                <div key={di} style={{ width: 26, display: 'flex', justifyContent: 'center' }}>
                  <div
                    onClick={() => isToday && onLog?.(h.id, date, cycleStatus(s))}
                    className={isToday && !s ? 'habit-cell-pulse' : ''}
                    style={{
                      width: 14, height: 14, borderRadius: 2, cursor: isToday ? 'pointer' : 'default',
                      background: s === 'yes' ? GREEN : s === 'partial' ? ORANGE : s === 'no' ? RED : GOLD_A(0.07),
                      border: isToday && !s ? `1px solid ${GOLD_A(0.5)}` : '1px solid transparent',
                      boxShadow: s === 'yes' ? `0 0 8px ${GREEN_A(0.5)}` : s === 'no' ? `0 0 6px ${RED_A(0.4)}` : 'none',
                      transition: 'all 0.2s ease',
                      transform: s === 'yes' ? 'scale(1.05)' : 'scale(1)',
                    }}
                  />
                </div>
              );
            })}
            <div style={{ marginLeft: 6, fontFamily: MONO, fontSize: 9, color: score >= 5 ? GREEN : score >= 3 ? ORANGE : MUTED, minWidth: 24 }}>{score}/7</div>
            {streak.current > 0 && (
              <div style={{ marginLeft: 4, fontFamily: MONO, fontSize: 8, color: GOLD_A(0.5), display: 'flex', alignItems: 'center', gap: 2 }}>
                <span style={{ color: streak.current >= 7 ? ICE : GOLD_A(0.6) }}>🔥</span>{streak.current}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Sacred Geometry Mandala ─────────────────────────────────────────────────
function SacredMandala({ speaking, loading, glitch, glowI, streakGlow, pulse }) {
  const canvasRef = useRef(null);
  const tRef = useRef(0);
  const stateRef = useRef({ speaking: false, loading: false, glowI: 0.3, streakGlow: 0.3 });

  useEffect(() => {
    stateRef.current = { speaking, loading, glowI, streakGlow };
  }, [speaking, loading, glowI, streakGlow]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = 220, H = 220, cx = W / 2, cy = H / 2;
    const kanjiList = ['武', '士', '道', '忍', '精', '進'];

    function polygon(px, py, r, sides, rot) {
      ctx.beginPath();
      for (let i = 0; i <= sides; i++) {
        const a = rot + (i / sides) * Math.PI * 2;
        const mx = px + Math.cos(a) * r;
        const my = py + Math.sin(a) * r;
        i === 0 ? ctx.moveTo(mx, my) : ctx.lineTo(mx, my);
      }
      ctx.closePath();
    }

    let animId;
    function draw() {
      const { speaking: sp, loading: ld, glowI: gi, streakGlow: sg } = stateRef.current;
      const active = sp || ld;
      const speed = active ? 0.018 : 0.008;
      tRef.current += speed;
      const t = tRef.current;
      const gold = (a) => `rgba(230,230,232,${a})`;
      const breath = 1 + Math.sin(t * (active ? 2.5 : 1.5)) * (active ? 0.06 : 0.03);
      const baseAlpha = 0.06 + sg * 0.06;

      ctx.clearRect(0, 0, W, H);
      ctx.save();
      ctx.translate(cx, cy);
      ctx.scale(breath, breath);

      // Ring 1 — outermost circle
      ctx.strokeStyle = gold(baseAlpha);
      ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.arc(0, 0, 100, 0, Math.PI * 2); ctx.stroke();

      // Glowing arc segments on outer ring (active = more arcs)
      const arcCount = active ? 3 : 1;
      for (let i = 0; i < arcCount; i++) {
        const arcStart = t * (0.3 + i * 0.2) + i * 2.1;
        ctx.beginPath(); ctx.arc(0, 0, 100, arcStart, arcStart + (active ? 1.0 : 0.6));
        ctx.strokeStyle = gold(0.2 + gi * 0.2);
        ctx.lineWidth = active ? 1.2 : 0.8;
        ctx.stroke();
      }

      // Ring 2 — hexagon (rotates clockwise)
      ctx.save(); ctx.rotate(t * 0.1);
      ctx.strokeStyle = gold(baseAlpha + 0.03);
      ctx.lineWidth = 0.5;
      polygon(0, 0, 85, 6, 0); ctx.stroke();
      // Vertex dots on hexagon
      for (let i = 0; i < 6; i++) {
        const a = i * Math.PI / 3;
        const px = Math.cos(a) * 85;
        const py = Math.sin(a) * 85;
        ctx.beginPath(); ctx.arc(px, py, 1.5 + gi * 1, 0, Math.PI * 2);
        ctx.fillStyle = gold(0.2 + gi * 0.15);
        ctx.fill();
      }
      ctx.restore();

      // Ring 3 — inner circle
      ctx.strokeStyle = gold(baseAlpha + 0.04);
      ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.arc(0, 0, 68, 0, Math.PI * 2); ctx.stroke();

      // Ring 4 — Star of David (2 opposing triangles, rotate counter-clockwise)
      ctx.save(); ctx.rotate(-t * 0.15);
      ctx.strokeStyle = gold(baseAlpha + 0.06);
      ctx.lineWidth = 0.5;
      polygon(0, 0, 60, 3, 0); ctx.stroke();
      polygon(0, 0, 60, 3, Math.PI); ctx.stroke();
      // Kanji at vertices (counter-rotate to stay upright)
      for (let i = 0; i < 6; i++) {
        const a2 = i * Math.PI / 3;
        const kx = Math.cos(a2) * 62;
        const ky = Math.sin(a2) * 62;
        ctx.save(); ctx.rotate(t * 0.15); // counter-rotate
        ctx.font = '300 11px serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        const kAlpha = 0.15 + Math.sin(t * 0.8 + i) * 0.1 + gi * 0.1;
        ctx.fillStyle = gold(kAlpha);
        ctx.fillText(kanjiList[i], kx, ky);
        ctx.restore();
      }
      ctx.restore();

      // Ring 5 — middle circle
      ctx.strokeStyle = gold(baseAlpha + 0.06);
      ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.arc(0, 0, 44, 0, Math.PI * 2); ctx.stroke();

      // Ring 6 — inner square (rotates clockwise fast)
      ctx.save(); ctx.rotate(t * 0.2);
      ctx.strokeStyle = gold(baseAlpha + 0.08);
      ctx.lineWidth = 0.5;
      polygon(0, 0, 38, 4, 0); ctx.stroke();
      // Diagonals from square vertices to center
      for (let i = 0; i < 4; i++) {
        const a3 = i * Math.PI / 2;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a3) * 38, Math.sin(a3) * 38);
        ctx.lineTo(0, 0);
        ctx.strokeStyle = gold(0.04 + gi * 0.03);
        ctx.lineWidth = 0.3;
        ctx.stroke();
      }
      ctx.restore();

      // Ring 7 — innermost circle
      ctx.strokeStyle = gold(baseAlpha + 0.1);
      ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.arc(0, 0, 24, 0, Math.PI * 2); ctx.stroke();

      // Ring 8 — inner triangle (rotates counter-clockwise fast)
      ctx.save(); ctx.rotate(-t * 0.3);
      ctx.strokeStyle = gold(baseAlpha + 0.12);
      ctx.lineWidth = 0.5;
      polygon(0, 0, 18, 3, 0); ctx.stroke();
      ctx.restore();

      // Core glow — intensifies with streak and activity
      const coreAlpha = 0.1 + sg * 0.08 + gi * 0.08 + (active ? 0.08 : 0);
      const gr = ctx.createRadialGradient(0, 0, 1, 0, 0, 16);
      gr.addColorStop(0, gold(coreAlpha + 0.2));
      gr.addColorStop(0.5, gold(coreAlpha));
      gr.addColorStop(1, 'transparent');
      ctx.fillStyle = gr;
      ctx.beginPath(); ctx.arc(0, 0, 16, 0, Math.PI * 2); ctx.fill();

      // Core dot
      ctx.beginPath(); ctx.arc(0, 0, 4, 0, Math.PI * 2);
      ctx.fillStyle = gold(0.4 + gi * 0.3 + sg * 0.1);
      ctx.fill();

      // Center kanji
      ctx.font = '300 13px serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = gold(0.35 + gi * 0.2);
      ctx.fillText('幽', 0, 0);

      ctx.restore();

      // Outer orbiting particles
      for (let i = 0; i < 8; i++) {
        const sa = t * 0.05 + i * Math.PI / 4;
        const sr = 105 + Math.sin(t * 0.5 + i) * 6;
        const spx = cx + Math.cos(sa) * sr;
        const spy = cy + Math.sin(sa) * sr;
        const ss = 0.6 + Math.sin(t + i) * 0.3;
        ctx.beginPath(); ctx.arc(spx, spy, ss, 0, Math.PI * 2);
        ctx.fillStyle = gold(0.1 + Math.sin(t * 0.3 + i) * 0.06);
        ctx.fill();
      }

      // Speaking state: extra pulsing energy ring
      if (active) {
        const pulseR = 50 + Math.sin(t * 3) * 8;
        ctx.beginPath(); ctx.arc(cx, cy, pulseR, 0, Math.PI * 2);
        ctx.strokeStyle = sp ? `rgba(246,246,247,${0.1 + Math.sin(t * 3) * 0.05})` : `rgba(230,230,232,${0.06 + Math.sin(t * 3) * 0.03})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      animId = requestAnimationFrame(draw);
    }
    animId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <div style={{ position: 'relative', width: 220, height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 4, filter: glitch ? 'hue-rotate(40deg) brightness(1.5)' : 'none', transition: glitch ? 'none' : 'filter 0.15s' }}>
      <canvas ref={canvasRef} width={220} height={220} style={{ position: 'relative', zIndex: 2 }} />
      {/* Status */}
      <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', display: 'inline-block', background: speaking ? GREEN : loading ? ORANGE : isMockAI ? RED : GREEN, boxShadow: speaking ? `0 0 8px ${GREEN}` : loading ? `0 0 8px ${ORANGE}` : isMockAI ? `0 0 6px ${RED}` : `0 0 6px ${GREEN}`, transition: 'all 0.3s' }} />
        <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: isMockAI ? RED_A(0.6) : GOLD_A(0.45) }}>
          {speaking ? '// SPEAKING' : loading ? '// PROCESSING' : isMockAI ? 'GHXST · OFFLINE · NO API KEY' : 'GHXST · CLAUDE · ONLINE'}
        </span>
      </div>
    </div>
  );
}

// ── GHXST AI Component ─────────────────────────────────────────────────────
function GhxstAI({ dashCtx, playClick, deepMode }) {
  const hour = new Date().getHours();
  const greeting = hour < 12
    ? "朝 · Good morning, sir. Systems are online. What's the first move today?"
    : hour < 17
    ? "昼 · Afternoon, sir. Half the day is spent. Are we ahead or behind?"
    : "夜 · Evening, sir. The day is winding down. Let's account for what we conquered.";

  const QUICK = [
    "Good morning, let's run it",
    "Check-in — how am I doing?",
    "Business strategy session",
    "What should I focus on today?",
    "Review my week",
    "I need a strategy session",
    "Talk to me about discipline",
  ];

  const [messages, setMessages] = useState([{ role: 'assistant', content: greeting }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [glitch, setGlitch] = useState(false);
  const [orbPhase, setOrbPhase] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const [voiceOn, setVoiceOn] = useState(isVoiceEnabled);
  const [booted, setBooted] = useState(false);
  const chatEndRef = useRef(null);
  const animRef = useRef(null);

  const toggleVoice = () => {
    if (speaking) { stopVoice(); setSpeaking(false); }
    setVoiceOn(v => !v);
  };

  // Boot sequence
  useEffect(() => {
    const t = setTimeout(() => setBooted(true), 1200);
    return () => clearTimeout(t);
  }, []);

  // Orb animation
  useEffect(() => {
    let t = 0;
    const animate = () => {
      t += 0.018;
      setOrbPhase(t);
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = useCallback(async (text) => {
    if (!text?.trim() || loading) return;
    playClick?.();
    const userMsg = { role: 'user', content: text.trim() };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');
    setLoading(true);
    setGlitch(true);
    setTimeout(() => setGlitch(false), 300);

    try {
      stopVoice();
      const reply = await callGHXST(updated, dashCtx, deepMode);
      setMessages(prev => [...prev, { role: 'assistant', content: reply, typing: true }]);
      // Clear typing flag after typewriter finishes so formatMd renders
      const typeDuration = Math.min(reply.length * 16, 5000) + 500;
      setTimeout(() => setMessages(prev => prev.map((m, i) => i === prev.length - 1 && m.typing ? { ...m, typing: false } : m)), typeDuration);
      // Fire voice immediately — don't wait for typewriter to finish
      if (voiceOn && isVoiceEnabled) {
        speakWithElevenLabs(reply, () => setSpeaking(true), () => setSpeaking(false));
      }
      setSpeaking(true);
      // Fallback: if no voice or voice fails, clear speaking state after text finishes
      if (!voiceOn || !isVoiceEnabled) {
        setTimeout(() => setSpeaking(false), Math.min(reply.length * 16, 4000));
      }
    } catch (err) {
      console.error('GHXST error:', err);
      setMessages(prev => [...prev, { role: 'assistant', content: `// CONNECTION ERROR — ${err.message || 'Signal lost'}. Check your API key, Snxper.` }]);
    } finally {
      setLoading(false);
    }
  }, [messages, loading, dashCtx]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
  };

  const pulse = 1 + Math.sin(orbPhase) * (speaking ? 0.14 : loading ? 0.08 : 0.03);
  const glowI = speaking ? 1 : loading ? 0.65 : 0.3;
  const cleanDays = dashCtx.discipline?.daysClean || 0;
  const streakGlow = cleanDays > 30 ? 1.2 : cleanDays > 14 ? 0.9 : cleanDays > 7 ? 0.6 : 0.3;

  if (!booted) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 16 }}>
        <div className="boot-text" style={{ fontFamily: MONO, fontSize: 10, color: GOLD_A(0.6), letterSpacing: '0.15em' }}>
          <div className="boot-line" style={{ animationDelay: '0s' }}>GHXST NEURAL LINK v2.0</div>
          <div className="boot-line" style={{ animationDelay: '0.2s' }}>[LOADING DASHBOARD CONTEXT...]</div>
          <div className="boot-line" style={{ animationDelay: '0.4s' }}>[SYNCING HABIT MATRIX...]</div>
          <div className="boot-line" style={{ animationDelay: '0.6s' }}>[CALIBRATING 精進 ORB...]</div>
          <div className="boot-line" style={{ animationDelay: '0.8s' }}>OPERATOR: SNXPER · LOCATION: DUBAI</div>
          <div className="boot-line" style={{ animationDelay: '1.0s', color: GREEN }}>// SYSTEMS ONLINE</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      {/* Sacred Geometry Mandala */}
      <SacredMandala speaking={speaking} loading={loading} glitch={glitch} glowI={glowI} streakGlow={streakGlow} pulse={pulse} />

      {/* Chat */}
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
        <div style={{ maxHeight: 340, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, paddingRight: 2 }} className="thin-scroll">
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-end', gap: 5, justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', animation: 'msgIn 0.25s ease' }}>
              {m.role === 'assistant' && <div style={{ fontFamily: MONO, fontSize: 7, letterSpacing: '0.15em', color: GOLD_A(0.4), textTransform: 'uppercase', paddingBottom: 5, flexShrink: 0 }}>幽</div>}
              <div style={{
                maxWidth: '83%', padding: '9px 13px', fontSize: 12.5, color: CREAM, lineHeight: 1.65, fontFamily: SANS,
                ...(m.role === 'user'
                  ? { background: GOLD_A(0.1), border: `1px solid ${GOLD_A(0.2)}`, borderRadius: '12px 2px 12px 12px' }
                  : { background: `linear-gradient(135deg,${GOLD_A(0.07)},${GOLD_A(0.02)})`, border: `1px solid ${GOLD_A(0.14)}`, borderRadius: '2px 12px 12px 12px' }
                ),
              }}>
                {m.role === 'assistant' && m.typing && i === messages.length - 1
                  ? <WordReveal text={m.content} speaking={speaking} />
                  : formatMd(m.content)
                }
              </div>
              {m.role === 'user' && <div style={{ fontFamily: MONO, fontSize: 7, letterSpacing: '0.15em', color: GOLD_A(0.35), textTransform: 'uppercase', paddingBottom: 5, flexShrink: 0 }}>忍</div>}
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5 }}>
              <div style={{ fontFamily: MONO, fontSize: 7, letterSpacing: '0.15em', color: GOLD_A(0.4), textTransform: 'uppercase', paddingBottom: 5 }}>幽</div>
              <div style={{ background: `linear-gradient(135deg,${GOLD_A(0.07)},${GOLD_A(0.02)})`, border: `1px solid ${GOLD_A(0.14)}`, borderRadius: '2px 12px 12px 12px', padding: '9px 13px', display: 'flex', gap: 4, alignItems: 'center', height: 34 }}>
                {[0, 0.25, 0.5].map((d, i) => <div key={i} style={{ width: 4, height: 4, borderRadius: '50%', background: GOLD_A(0.6), animation: `dotBounce 1.2s ease-in-out infinite`, animationDelay: `${d}s` }} />)}
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Quick prompts */}
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {QUICK.map(q => (
            <button key={q} className="quick-btn" onClick={() => send(q)} style={{
              background: 'transparent', border: `1px solid ${GOLD_A(0.14)}`, borderRadius: 2, padding: '4px 9px', fontSize: 9.5,
              color: GOLD_A(0.5), cursor: 'pointer', fontFamily: MONO, letterSpacing: '0.05em', whiteSpace: 'nowrap',
            }}>{q}</button>
          ))}
        </div>

        {/* Input */}
        <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
          {/* Voice toggle */}
          <button onClick={toggleVoice} title={voiceOn ? 'Voice on — click to mute' : 'Voice off — click to enable'} style={{
            width: 34, height: 34, borderRadius: 2, border: `1px solid ${voiceOn ? GREEN_A(0.3) : GOLD_A(0.15)}`,
            background: voiceOn ? GREEN_A(0.08) : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
            color: voiceOn ? GREEN : GOLD_A(0.3), transition: 'all 0.2s',
          }}>
            {voiceOn ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
              </svg>
            )}
          </button>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.16)}`, borderRadius: 2, padding: '0 12px' }}>
            <span style={{ color: GOLD_A(0.45), fontSize: 12, fontFamily: MONO, marginRight: 7, flexShrink: 0, letterSpacing: '0.05em' }}>忍›</span>
            <input
              value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
              placeholder="Speak to Ghxst, Snxper..."
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', padding: '9px 0', fontSize: 12.5, color: CREAM, fontFamily: SANS }}
            />
          </div>
          <button onClick={() => send(input)} disabled={!input.trim() || loading} style={{
            width: 34, height: 34, borderRadius: 2, background: GOLD, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: BG, flexShrink: 0, opacity: input.trim() && !loading ? 1 : 0.3, transition: 'opacity 0.15s',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════════════════
// ── Splash Screen Quotes ───────────────────────────────────────────────────
const SPLASH_QUOTES = [
  { text: 'The impediment to action advances action. What stands in the way becomes the way.', author: 'Marcus Aurelius', kanji: '道' },
  { text: 'It is not that we have a short time to live, but that we waste a great deal of it.', author: 'Seneca', kanji: '時' },
  { text: 'No man is free who is not master of himself.', author: 'Epictetus', kanji: '克' },
  { text: 'In the midst of chaos, there is also opportunity.', author: 'Sun Tzu', kanji: '機' },
  { text: 'There is nothing outside of yourself that can ever enable you to get better, stronger, richer, quicker, or smarter. Everything is within.', author: 'Miyamoto Musashi', kanji: '内' },
  { text: 'We suffer more often in imagination than in reality.', author: 'Seneca', kanji: '心' },
  { text: 'Think lightly of yourself and deeply of the world.', author: 'Miyamoto Musashi', kanji: '世' },
  { text: 'The soul becomes dyed with the colour of its thoughts.', author: 'Marcus Aurelius', kanji: '魂' },
  { text: 'He who has a why to live can bear almost any how.', author: 'Nietzsche', kanji: '志' },
  { text: 'The more you sweat in training, the less you bleed in combat.', author: 'Richard Marcinko', kanji: '鍛' },
  { text: 'Delayed NEVER Denied.', author: 'Snxper', kanji: '忍' },
  { text: 'Imagine going through all this pain just to fail. What would young Alessio think?', author: 'Snxper', kanji: '覚' },
  { text: 'NEVER return to the valley of despair again.', author: 'Snxper', kanji: '誓' },
  { text: 'Discipline equals freedom.', author: 'Jocko Willink', kanji: '律' },
  { text: 'A warrior is not about perfection. It is about absolute vulnerability.', author: 'Shambhala', kanji: '武' },
  { text: 'The only way to do great work is to love what you do.', author: 'Steve Jobs', kanji: '愛' },
  { text: 'I fear not the man who has practiced 10,000 kicks once, but I fear the man who has practiced one kick 10,000 times.', author: 'Bruce Lee', kanji: '練' },
  { text: 'Begin at once to live and count each separate day as a separate life.', author: 'Seneca', kanji: '今' },
];

export default function App() {
  // Cloud sync load gate — pull from Supabase before first render when enabled
  const [syncReady, setSyncReady] = useState(!syncEnabled);
  useEffect(() => {
    if (!syncEnabled) return;
    // Console helpers for data portability / recovery:
    //   window.ghxstSeed()       — push everything in this browser up to the cloud
    //   window.ghxstId()         — show the id this browser reads/writes under
    //   window.ghxstRestore(id)  — pin this browser to another device's id + reload
    window.ghxstSeed = pushAllLocal;
    window.ghxstId = () => { const id = currentUserId(); console.log('[GHXST] your DOJO id:', id); return id; };
    window.ghxstRestore = (id) => { if (setUserId(id)) { console.log('[GHXST] id set, reloading to pull your data…'); location.reload(); } else { console.warn('[GHXST] pass a valid id string'); } };
    pullAll().finally(() => setSyncReady(true));
  }, []);

  const [tab, setTab] = useState('home');
  const [splash, setSplash] = useState(true);
  const [splashQuote] = useState(() => SPLASH_QUOTES[Math.floor(Math.random() * SPLASH_QUOTES.length)]);
  const [splashPhase, setSplashPhase] = useState(0); // 0=kanji, 1=quote, 2=fade quote, 3=gateway, 4=fade out
  const [privacyMode, setPrivacyMode] = useState(false);
  const [todayStr, setTodayStr] = useState(getTodayStr);
  const [weekDates, setWeekDates] = useState(getWeekDates);
  const [habitLogs, setHabitLogs] = useState(() => LS.get('habitLogs', []));
  const [xp, setXp] = useState(() => LS.get('xp', 0));
  const [glitchActive, setGlitchActive] = useState(false);
  const [kanjiIdx, setKanjiIdx] = useState(0);
  const [tabTransition, setTabTransition] = useState(false);
  const [warRoom, setWarRoom] = useState(false);
  const [quoteIdx, setQuoteIdx] = useState(() => Math.floor(Math.random() * QUOTES.length));

  // Editable tab names
  const [tabNames, setTabNames] = useState(() => LS.get('tabNames', {}));
  const [editingTab, setEditingTab] = useState(null);
  const [editingTabValue, setEditingTabValue] = useState('');
  const editTabRef = useRef(null);

  const TABS = useMemo(() => DEFAULT_TABS.map(t => ({
    ...t,
    en: tabNames[t.id] || t.en,
  })), [tabNames]);

  useEffect(() => { LS.set('tabNames', tabNames); }, [tabNames]);

  useEffect(() => {
    if (editingTab && editTabRef.current) editTabRef.current.focus();
  }, [editingTab]);

  // Books
  const [books, setBooks] = useState(() => LS.get('books', [
    { id: '1', title: 'The Almanack of Naval Ravikant', author: 'Eric Jorgenson', status: 'reading', current_page: 142, total_pages: 242, cover_color: GOLD },
    { id: '2', title: 'The Game of Life and How to Play It', author: 'Florence Scovel Shinn', status: 'done', cover_color: GREEN },
    { id: '3', title: 'Think and Grow Rich', author: 'Napoleon Hill', status: 'queued', cover_color: BLUE },
  ]));
  const [editingBook, setEditingBook] = useState(null);
  const [showAddBook, setShowAddBook] = useState(false);
  const [newBook, setNewBook] = useState({ title: '', author: '', status: 'reading', current_page: 0, total_pages: 0, cover_color: GOLD });

  // ── Financials — 4 simple numbers (Money tab) ────────────────────────
  const [fin, setFin] = useState(() => LS.get('fin', {
    assets: 0,        // total value of what you own
    capital: 0,       // liquid cash / capital on hand
    monthlySpend: 0,  // money out per month
    monthlyIncome: 0, // money in per month
  }));
  const setFinField = (field, val) => setFin(prev => ({ ...prev, [field]: Math.max(0, Number(val) || 0) }));

  // Discipline
  const [disciplineLogs, setDisciplineLogs] = useState(() => LS.get('disciplineLogs', []));
  // Anchor for the clean streak when there is NO relapse logged. Counts up from this date.
  // Defaults to 7 days ago (current standing: day 7, 0 relapses). Resets to today on relapse.
  const [cleanStart, setCleanStart] = useState(() => LS.get('cleanStart', new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0]));
  const [disciplineConfirm, setDisciplineConfirm] = useState(null);

  // Projects
  const [items, setItems] = useState(() => dedup(LS.get('items', [
    { id: 2, type: 'project', name: 'The Dojo Dashboard', status: 'active', note: 'This app — live soon', created: '10.3.26' },
    { id: 3, type: 'idea', name: 'Content around Dubai grind', status: 'idea', note: "Document the journey after it's done", created: '8.2.26' },
  ])));
  const [tasks, setTasks] = useState(() => dedup(LS.get('tasks', [])));
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddTask, setShowAddTask] = useState(null); // 'business' or 'personal'
  const [newItem, setNewItem] = useState({ type: 'idea', name: '', note: '', status: 'idea' });
  const [newTask, setNewTask] = useState({ name: '', note: '' });
  const [editingItem, setEditingItem] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [dragTask, setDragTask] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const [dragBook, setDragBook] = useState(null);

  // Faith & Prayer
  const [faithTimer, setFaithTimer] = useState(0);
  const [faithTimerRunning, setFaithTimerRunning] = useState(false);
  const prayerAudioRef = useRef(null);
  const [prayerPlaying, setPrayerPlaying] = useState(false);
  const [faithCompleted, setFaithCompleted] = useState(() => LS.get('faithCompleted', {}));
  const [todayPassageIdx] = useState(() => {
    const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
    return (dayOfYear * 3) % BIBLE_PASSAGES.length;
  });
  const [prayerNote, setPrayerNote] = useState('');
  const [gratitudeNote, setGratitudeNote] = useState('');
  const [faithNotes, setFaithNotes] = useState(() => dedup(LS.get('faithNotes', [])));

  // Daily Reading — batched 60-day shuffled queue, one reading surfaced per day
  const [readingPlan, setReadingPlan] = useState(() => {
    let plan = LS.get('readingPlan', null);
    const today = getTodayStr();
    // (re)generate when missing, malformed, or the 60-day queue is exhausted
    if (!plan || !Array.isArray(plan.queue) || !plan.start || daysSince(plan.start, today) >= plan.queue.length) {
      plan = { start: today, queue: buildReadingQueue(DAILY_READINGS.length, 60) };
      LS.set('readingPlan', plan);
    }
    return plan;
  });
  const [readingCompleted, setReadingCompleted] = useState(() => LS.get('readingCompleted', {}));
  const [readingPage, setReadingPage] = useState(0); // 0 | 1 — current page of today's reading

  // The Wall — daily brick system
  // The Wall starts at 126 bricks already laid (126 days of work banked),
  // then keeps counting up from there. Backfilled as consecutive prior days.
  const [bricks, setBricks] = useState(() => LS.get('bricks', (() => {
    const seed = [];
    for (let i = 126; i >= 1; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      seed.push({ date: d.toISOString().split('T')[0], time: '20:00', seeded: true });
    }
    return seed;
  })()));
  const [justStacked, setJustStacked] = useState(false);
  const faithTimerRef = useRef(null);


  // Journal
  const [journalEntries, setJournalEntries] = useState(() => LS.get('journalEntries', []));
  const [journalDraft, setJournalDraft] = useState({ intention: '', reflection: '', gratitudes: ['', '', ''], win: '', dayRating: 7 });
  const [journalView, setJournalView] = useState('write'); // 'write' | 'history'
  const [journalSearch, setJournalSearch] = useState('');

  // Learning Vault
  const [learnings, setLearnings] = useState(() => dedup(LS.get('learnings', [])));
  const [showAddLearning, setShowAddLearning] = useState(false);
  const [newLearning, setNewLearning] = useState({ title: '', category: 'AI & Tech', type: 'course', progress: 0, totalHours: 0, loggedHours: 0, notes: '', status: 'active' });

  // Movies
  const [movies, setMovies] = useState(() => {
    const saved = LS.get('movies', DEFAULT_MOVIES);
    // Deduplicate by ID — keeps first occurrence
    const seen = new Set();
    return saved.filter(m => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  });
  const [movieFilter, setMovieFilter] = useState('all');
  const [showAddMovie, setShowAddMovie] = useState(false);
  const [editingMovieId, setEditingMovieId] = useState(null);
  const [movieForm, setMovieForm] = useState({ title: '', year: '', tag: 'snxper', note: '', rating: null });
  const [movieDeleteConfirm, setMovieDeleteConfirm] = useState(null);

  // Journal tab sub-view
  const [journalTab, setJournalTab] = useState('journal'); // 'journal' | 'learning' | 'movies'

  // ── Gateway Reel System ─────────────────────────────────────────────
  const [gatewayReels, setGatewayReels] = useState(() => {
    const saved = LS.get('gatewayReels', null);
    // Reset if old data contains instagram URLs
    if (saved && saved.some(r => r.url && r.url.includes('instagram'))) return [{ id: 'r1', url: '/onemillion.mp4', label: 'The one that started it' }];
    return saved || [{ id: 'r1', url: '/onemillion.mp4', label: 'The one that started it' }];
  });
  const [gatewaySelectedReel, setGatewaySelectedReel] = useState(null);
  const [gatewayTimer, setGatewayTimer] = useState(0);
  const [gatewayUnlocked, setGatewayUnlocked] = useState(false);
  const [showAddReel, setShowAddReel] = useState(false);
  const [newReelFile, setNewReelFile] = useState('');
  const [newReelLabel, setNewReelLabel] = useState('');


  // Goals — 2 Year Vision
  const [goalCategories, setGoalCategories] = useState(() => LS.get('goalCategories', [
    {
      id: 1, name: 'SNXPER — 2 Year Vision', kanji: '忍', color: GOLD, deadline: 'Feb 2028',
      statement: 'I AM A MILLIONAIRE. $1,000,000 USD in Bank Accounts. Process = Outcome / Detach from Results. Delayed NEVER Denied.',
      goals: [
        { id: 101, text: 'Millionaire — $1,000,000 USD in the bank', done: false, priority: 'north-star' },
        { id: 102, text: 'Flexibility to live and rent in any part of the world ($USD cashflow)', done: false, priority: 'high' },
        { id: 103, text: 'Great shape — 90kg+, shredded, teeth fixed', done: false, priority: 'high' },
        { id: 104, text: 'Bench 120kg x6, Curl 30x6, Pull Ups 15, Muscle Ups 5', done: false, priority: 'medium' },
        { id: 107, text: '$150,000 USD — THE DOJO', done: false, priority: 'high' },
      ]
    },
    {
      id: 2, name: 'Financial Checkpoints', kanji: '金', color: GREEN, deadline: 'Jan 2027',
      statement: '',
      goals: [
        { id: 201, text: 'July 2026 — $27,000 USD in account', done: false, priority: 'high' },
        { id: 202, text: 'Jan 2027 — $160,000 USD in account', done: false, priority: 'north-star' },
      ]
    },
    {
      id: 3, name: 'Business — Build My Own', kanji: '事', color: ICE, deadline: 'Dec 2026',
      statement: 'Vehicles, not saviours. I build my life — no one person changes it for me.',
      goals: [
        { id: 301, text: 'One offer that sells on its own', done: false, priority: 'north-star' },
        { id: 302, text: '10K subscribers on YouTube', done: false, priority: 'high' },
        { id: 303, text: '10K followers on Instagram', done: false, priority: 'high' },
        { id: 304, text: 'Recurring revenue that does not depend on any single client', done: false, priority: 'high' },
      ]
    },
  ]));
  const [showAddGoal, setShowAddGoal] = useState(null);
  const [newGoalText, setNewGoalText] = useState('');
  const [newGoalPriority, setNewGoalPriority] = useState('medium');
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editingGoalText, setEditingGoalText] = useState('');
  const [editingCatId, setEditingCatId] = useState(null); // goal category being edited (header)
  const [showAddCategory, setShowAddCategory] = useState(false);
  const GOAL_COLORS = [GOLD, ICE, GREEN, ORANGE, RED, ACCENT]; // palette for category colour picker
  const [newCategory, setNewCategory] = useState({ name: '', deadline: '', statement: '' });
  const [editingStatement, setEditingStatement] = useState(null);
  const [expandedKaizen, setExpandedKaizen] = useState(null);

  // Media Library (YouTube embeds)
  const [mindsetVideos, setMindsetVideos] = useState(() => LS.get('mindsetVideos', [
    { id: 1, videoId: 'wJa5Ch0O4BI', title: 'The Book', size: 'small' },
  ]));
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [videoIdx, setVideoIdx] = useState(0); // current video in the Media Library carousel

  // Book Notes
  const [bookNotes, setBookNotes] = useState(() => dedup(LS.get('bookNotes', [])));
  const [showAddBookNote, setShowAddBookNote] = useState(false);
  const [newBookNote, setNewBookNote] = useState({ book: '', chapter: '', notes: '' });
  const [expandedNote, setExpandedNote] = useState(null);
  const [editingNote, setEditingNote] = useState(null);

  // ── Seeds — people I meet + the opportunity they carry ───────────────
  // Each seed: { id, name, note, stage, met }
  //   stage: 'planted' (just met) | 'sprouting' (warming up) | 'growing' (real momentum) | 'harvest' (paid off)
  const [seeds, setSeeds] = useState(() => dedup(LS.get('seeds', [])));
  const [showAddSeed, setShowAddSeed] = useState(false);
  const [newSeed, setNewSeed] = useState({ name: '', note: '', stage: 'planted', metYear: new Date().getFullYear(), metCountry: '' });
  const [editingSeed, setEditingSeed] = useState(null);
  const [seedStageFilter, setSeedStageFilter] = useState('all');

  // ── Luxe Agent ──────────────────────────────────────────────────────────
  const [luxeSubTab, setLuxeSubTab] = useState('offer'); // 'offer' | 'content' | 'tasks' | 'study'
  const [luxeOffer, setLuxeOffer] = useState(() => LS.get('luxeOffer', { headline: '', body: '' }));
  // Content ideas — grouped by fixed channel categories: { [channel]: [ {id,text} ] }
  const [luxeIdeas, setLuxeIdeas] = useState(() => {
    const v = LS.get('luxeIdeas', {});
    if (Array.isArray(v)) {
      // Migrate old flat list → Instagram bucket (don't discard the user's ideas)
      return v.length ? { Instagram: v } : {};
    }
    return v || {};
  });
  const [newLuxeIdea, setNewLuxeIdea] = useState({}); // per-channel draft text
  const [luxeTasks, setLuxeTasks] = useState(() => dedup(LS.get('luxeTasks', [])));
  const [newLuxeTask, setNewLuxeTask] = useState('');
  const [editingLuxeOffer, setEditingLuxeOffer] = useState(false);
  const [editLuxeContent, setEditLuxeContent] = useState(false); // edit mode for content ideas
  const [editLuxeTasks, setEditLuxeTasks] = useState(false);     // edit mode for tasks
  const [editLuxeStudy, setEditLuxeStudy] = useState(false);     // edit mode for study list
  // Study list — editable categories, each holding people { id, name, handle, note }
  const [luxeCategories, setLuxeCategories] = useState(() => LS.get('luxeCategories', [
    'Instagram', 'TikTok', 'X', 'YouTube', 'Ads', 'Social Business', 'AI Real Estate Educators',
  ]));
  const [luxeStudy, setLuxeStudy] = useState(() => LS.get('luxeStudy', {})); // { [category]: [ {id,name,handle,note} ] }
  const [newStudy, setNewStudy] = useState({});       // per-category draft { [cat]: {name,handle,note} }
  const [newLuxeCategory, setNewLuxeCategory] = useState('');
  const [showAddLuxeCat, setShowAddLuxeCat] = useState(false);
  const [editingStudy, setEditingStudy] = useState(null); // `${cat}_${id}` being edited

  // GHXST Quick Notes (pinned insights from conversations)
  const [ghxstNotes, setGhxstNotes] = useState(() => LS.get('ghxstNotes', []));
  const [ghxstNoteDraft, setGhxstNoteDraft] = useState('');

  // ── Computed ──────────────────────────────────────────────────────────
  // Assets is the headline figure the rest of the app reads as "net worth".
  const totalUSD = fin.assets;
  const monthlyNet = fin.monthlyIncome - fin.monthlySpend; // + = surplus, − = burn

  const logMap = useMemo(() => {
    const m = {};
    habitLogs.forEach(l => { m[`${l.habit_id}_${l.log_date}`] = l.status; });
    return m;
  }, [habitLogs]);

  const totalHabitsToday = ALL_HABITS.filter(h => logMap[`${h.id}_${todayStr}`] === 'yes').length;
  const morningScore = useMemo(() => {
    const done = MORNING_IDS.filter(id => logMap[`${id}_${todayStr}`] === 'yes').length;
    return Math.round(done / MORNING_IDS.length * 10);
  }, [logMap, todayStr]);
  const perfectMorning = morningScore === 10;

  // Yesterday's stats for morning briefing
  const yesterdayStr = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  }, []);
  const yesterdayHabits = ALL_HABITS.filter(h => logMap[`${h.id}_${yesterdayStr}`] === 'yes').length;

  // Habit streak — consecutive days with 50%+ habits done
  const habitStreak = useMemo(() => {
    let s = 0; const d = new Date();
    while (true) {
      const ds = d.toISOString().split('T')[0];
      const done = ALL_HABITS.filter(h => logMap[`${h.id}_${ds}`] === 'yes').length;
      if (done >= ALL_HABITS.length * 0.5) { s++; d.setDate(d.getDate() - 1); } else break;
    }
    return s;
  }, [logMap]);

  const activeDays = useMemo(() => [...new Set(habitLogs.filter(l => FITNESS_IDS.includes(l.habit_id) && l.status === 'yes' && weekDates.includes(l.log_date)).map(l => l.log_date))].length, [habitLogs, weekDates]);

  const todayDiscipline = disciplineLogs.find(d => d.date === todayStr);
  const evilCount = disciplineLogs.filter(d => d.type === 'evil').length;
  const lastEvil = disciplineLogs.filter(d => d.type === 'evil').sort((a, b) => b.date.localeCompare(a.date))[0];
  // Count from the last relapse if there is one, otherwise from the clean-start anchor.
  const cleanAnchor = lastEvil ? lastEvil.date : cleanStart;
  const daysClean = Math.max(0, Math.floor((new Date(todayStr) - new Date(cleanAnchor)) / 86400000));

  const rank = getRank(xp);

  // ── Splash Screen Sequence ──────────────────────────────────────────
  useEffect(() => {
    if (!splash) return;
    const t1 = setTimeout(() => setSplashPhase(1), 800);   // Show quote
    const t2 = setTimeout(() => setSplashPhase(2), 4000);  // Fade out quote
    const t3 = setTimeout(() => setSplashPhase(3), 4800);  // Show gateway
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [splash]);

  // Audio engine — files go in /public folder
  const audioRef = useRef({});
  const playSound = useCallback((src) => {
    try {
      const audio = new Audio(src);
      audio.volume = 1;
      audio.play().catch(() => {});
      return audio;
    } catch (e) { return null; }
  }, []);

  const playClick = useCallback(() => {
    playSound('/ui-click.mp3');
  }, [playSound]);

  const enterDojo = useCallback(() => {
    playSound('/enter-dojo.mp3');
    setSplashPhase(4);
    setTimeout(() => setSplash(false), 2500);
  }, [playSound]);

  const completeGateway = useCallback(() => {
    playSound('/enter-dojo.mp3');
    setSplashPhase(4);
    setTimeout(() => setSplash(false), 2500);
  }, [playSound]);

  // Gateway timer — counts up when a reel is selected
  useEffect(() => {
    if (splashPhase < 3 || !gatewaySelectedReel) return;
    setGatewayTimer(0);
    setGatewayUnlocked(false);
    const iv = setInterval(() => {
      setGatewayTimer(prev => {
        const next = prev + 1;
        if (next >= 20) setGatewayUnlocked(true);
        return next;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [splashPhase, gatewaySelectedReel]);

  // Gateway reel CRUD
  useEffect(() => { LS.set('gatewayReels', gatewayReels); }, [gatewayReels]);

  const addGatewayReel = () => {
    if (!newReelFile.trim()) return;
    const file = newReelFile.trim();
    const url = file.startsWith('/') ? file : '/' + file;
    setGatewayReels(prev => [...prev, { id: 'r_' + Date.now(), url, label: newReelLabel.trim() || file }]);
    setNewReelFile('');
    setNewReelLabel('');
    setShowAddReel(false);
  };

  const removeGatewayReel = (id) => setGatewayReels(prev => prev.filter(r => r.id !== id));

  // ── Keyboard Shortcuts ────────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e) => {
      // Don't fire if typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      // Shift+G → GHXST Room
      if (e.shiftKey && e.key === 'G') { e.preventDefault(); setWarRoom(w => !w); return; }
      // Number keys 1-9,0 for tabs
      const tabKeys = { '1': 'home', '2': 'habits', '3': 'faith', '4': 'money', '5': 'goals', '6': 'seeds', '7': 'building', '8': 'journal', '9': 'mindset', '0': 'luxe' };
      if (tabKeys[e.key] && !e.shiftKey && !e.ctrlKey && !e.metaKey) { switchTab(tabKeys[e.key]); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // ── Day change detection ─────────────────────────────────────────────
  useEffect(() => {
    const check = setInterval(() => {
      const now = getTodayStr();
      if (now !== todayStr) {
        setTodayStr(now);
        setWeekDates(getWeekDates());
      }
    }, 30000);
    return () => clearInterval(check);
  }, [todayStr]);

  // ── Quote rotation ───────────────────────────────────────────────────
  useEffect(() => {
    const qi = setInterval(() => setQuoteIdx(q => (q + 1) % QUOTES.length), 12000);
    return () => clearInterval(qi);
  }, []);

  // ── Kaizen word rotation ─────────────────────────────────────────────
  useEffect(() => {
    const ki = setInterval(() => setKanjiIdx(k => (k + 1) % KAIZEN_WORDS.length), 4000);
    return () => clearInterval(ki);
  }, []);

  // ── Glitch (intentional, less random) ────────────────────────────────
  useEffect(() => {
    const gi = setInterval(() => {
      if (Math.random() > 0.82) {
        setGlitchActive(true);
        setTimeout(() => setGlitchActive(false), 80 + Math.random() * 120);
      }
    }, 5000);
    return () => clearInterval(gi);
  }, []);

  // ── One-time clean slate reset ───────────────────────────────────────
  // Wipes tracked entries (habits/heatmap, XP, discipline) to a fresh
  // "built today" state. Runs once, flagged by cleanSlate_v1, then never
  // again. Goals, books, movies, journal, faith, and notes are preserved.
  useEffect(() => {
    if (LS.get('cleanSlate_v1', false)) return;

    // Wipe tracked data
    LS.set('habitLogs', []); setHabitLogs([]);
    LS.set('xp', 0); setXp(0);
    LS.set('disciplineLogs', []); setDisciplineLogs([]);

    LS.set('cleanSlate_v1', true);
  }, []);

  // ── One-time detachment migration ────────────────────────────────────
  // Snxper has detached from Omar / Billion Ballers. Strip those references
  // from any already-saved goals so old localStorage data reflects the new
  // chapter. Runs once, flagged by detachOmar_v1.
  useEffect(() => {
    if (LS.get('detachOmar_v1', false)) return;
    const isOmar = (t) => /omar|billion baller|\bBB\b|ammache|\bOA\b/i.test(t || '');
    setGoalCategories(prev => {
      const next = prev
        .filter(cat => !isOmar(cat.name))
        .map(cat => ({ ...cat, goals: cat.goals.filter(g => !isOmar(g.text)) }));
      LS.set('goalCategories', next); return next;
    });
    setItems(prev => {
      const next = prev.filter(i => !isOmar(`${i.name} ${i.note || ''}`));
      LS.set('items', next); return next;
    });
    LS.set('detachOmar_v1', true);
  }, []); // eslint-disable-line

  // ── One-time: set the fortress to 86 bricks ──────────────────────────
  // Existing users had their wall reset by the clean slate. Backfill it to
  // 86 so the count reflects the days already banked. Runs once.
  useEffect(() => {
    if (LS.get('bricks86_v1', false)) return;
    setBricks(prev => {
      if (prev.length >= 86) { LS.set('bricks86_v1', true); return prev; }
      const need = 86 - prev.length;
      const seed = [];
      for (let i = need; i >= 1; i--) {
        const d = new Date(); d.setDate(d.getDate() - (prev.length + i));
        seed.push({ date: d.toISOString().split('T')[0], time: '20:00', seeded: true });
      }
      const next = [...seed, ...prev];
      LS.set('bricks', next); return next;
    });
    LS.set('bricks86_v1', true);
  }, []); // eslint-disable-line

  // ── One-time: raise the fortress to 126 bricks ───────────────────────
  // Tops the wall up to 126 for already-saved accounts. Runs once.
  useEffect(() => {
    if (LS.get('bricks126_v1', false)) return;
    setBricks(prev => {
      if (prev.length >= 126) { LS.set('bricks126_v1', true); return prev; }
      const need = 126 - prev.length;
      const seed = [];
      for (let i = need; i >= 1; i--) {
        const d = new Date(); d.setDate(d.getDate() - (prev.length + i));
        seed.push({ date: d.toISOString().split('T')[0], time: '20:00', seeded: true });
      }
      const next = [...seed, ...prev];
      LS.set('bricks', next); return next;
    });
    LS.set('bricks126_v1', true);
  }, []); // eslint-disable-line

  // ── One-time: 36-day perfect habit streak ────────────────────────────
  // Marks every habit done ('yes') for each of the last 36 days INCLUDING
  // today (i = 35..0), so the streak counter — which counts back from today —
  // reads 36 and the heatmap is fully filled. Preserves any logs already
  // present. Runs once, flagged by habits36_v1.
  useEffect(() => {
    if (LS.get('habits36_v1', false)) return;
    setHabitLogs(prev => {
      const seen = new Set(prev.map(l => `${l.habit_id}_${l.log_date}`));
      const seed = [];
      for (let i = 35; i >= 0; i--) {
        const d = new Date(); d.setDate(d.getDate() - i);
        const ds = d.toISOString().split('T')[0];
        ALL_HABITS.forEach(h => {
          const key = `${h.id}_${ds}`;
          if (!seen.has(key)) { seed.push({ habit_id: h.id, log_date: ds, status: 'yes' }); seen.add(key); }
        });
      }
      const next = [...prev, ...seed];
      LS.set('habitLogs', next); return next;
    });
    LS.set('habits36_v1', true);
  }, []); // eslint-disable-line

  // ── One-time rename: DXSRPT → The Dojo in already-saved data ──────────
  useEffect(() => {
    if (LS.get('renameDojo_v1', false)) return;
    const fix = (s) => (s || '').replace(/DXSRPT\s*GROUP/gi, 'THE DOJO').replace(/DXSRPT\s*Dashboard/gi, 'The Dojo Dashboard').replace(/DXSRPT\s*Group/gi, 'The Dojo');
    setGoalCategories(prev => {
      const next = prev.map(cat => ({ ...cat, goals: cat.goals.map(g => ({ ...g, text: fix(g.text) })) }));
      LS.set('goalCategories', next); return next;
    });
    setItems(prev => {
      const next = prev.map(i => ({ ...i, name: fix(i.name), note: fix(i.note) }));
      LS.set('items', next); return next;
    });
    LS.set('renameDojo_v1', true);
  }, []);

  // ── Persist ──────────────────────────────────────────────────────────
  useEffect(() => { LS.set('habitLogs', habitLogs); }, [habitLogs]);
  useEffect(() => { LS.set('books', books); }, [books]);
  useEffect(() => { LS.set('fin', fin); }, [fin]);
  useEffect(() => { LS.set('disciplineLogs', disciplineLogs); }, [disciplineLogs]);
  useEffect(() => { LS.set('cleanStart', cleanStart); }, [cleanStart]);
  useEffect(() => { LS.set('items', items); }, [items]);

  // ── One-time brand migration ──────────────────────────────────────────────
  // Old saved data carries warm-gold hexes (#c9a96e etc) from before the
  // monochrome rebrand. Rewrite any stored colour to its DXSRPT equivalent so
  // user-coloured items (goal categories, books) stop rendering gold.
  useEffect(() => {
    if (LS.get('brandMigratedV2', false)) return;
    const WARM_TO_MONO = {
      '#c9a96e': ICE, '#ede4d4': CREAM, '#080807': BG, '#7a7060': MUTED,
      '#a8b4c0': MUTED, '#7a9ec9': ACCENT, '#d4af37': ACCENT, '#ffd700': ICE,
      // legacy colourful status → restored green/red/amber
      '#4caf76': GREEN, '#e05a4e': RED, '#e8933a': ORANGE,
    };
    const fix = (c) => (typeof c === 'string' && WARM_TO_MONO[c.toLowerCase()]) || c;
    setGoalCategories(prev => prev.map(cat => ({ ...cat, color: fix(cat.color) })));
    setBooks(prev => prev.map(b => ({ ...b, cover_color: fix(b.cover_color) })));
    LS.set('brandMigratedV2', true);
  }, []); // eslint-disable-line
  useEffect(() => { LS.set('tasks', tasks); }, [tasks]);
  useEffect(() => { LS.set('faithCompleted', faithCompleted); }, [faithCompleted]);
  useEffect(() => { LS.set('faithNotes', faithNotes); }, [faithNotes]);
  useEffect(() => { LS.set('bricks', bricks); }, [bricks]);
  useEffect(() => { LS.set('readingPlan', readingPlan); }, [readingPlan]);
  useEffect(() => { LS.set('readingCompleted', readingCompleted); }, [readingCompleted]);

  // Faith timer
  useEffect(() => {
    if (faithTimerRunning) {
      faithTimerRef.current = setInterval(() => setFaithTimer(t => t + 1), 1000);
    } else {
      clearInterval(faithTimerRef.current);
    }
    return () => clearInterval(faithTimerRef.current);
  }, [faithTimerRunning]);
  useEffect(() => { LS.set('xp', xp); }, [xp]);
  useEffect(() => { LS.set('journalEntries', journalEntries); }, [journalEntries]);
  useEffect(() => { LS.set('learnings', learnings); }, [learnings]);
  useEffect(() => { LS.set('movies', movies); }, [movies]);
  useEffect(() => { LS.set('goalCategories', goalCategories); }, [goalCategories]);
  useEffect(() => { LS.set('mindsetVideos', mindsetVideos); }, [mindsetVideos]);
  useEffect(() => { LS.set('bookNotes', bookNotes); }, [bookNotes]);
  useEffect(() => { LS.set('seeds', seeds); }, [seeds]);
  useEffect(() => { LS.set('ghxstNotes', ghxstNotes); }, [ghxstNotes]);
  useEffect(() => { LS.set('luxeOffer', luxeOffer); }, [luxeOffer]);
  useEffect(() => { LS.set('luxeIdeas', luxeIdeas); }, [luxeIdeas]);
  useEffect(() => { LS.set('luxeTasks', luxeTasks); }, [luxeTasks]);
  useEffect(() => { LS.set('luxeStudy', luxeStudy); }, [luxeStudy]);
  useEffect(() => { LS.set('luxeCategories', luxeCategories); }, [luxeCategories]);

  const totalNetWorth = totalUSD;

  // ── Handlers ─────────────────────────────────────────────────────────
  // Count habits done on a given date from a logs array
  const habitsDoneOn = (logs, date) => logs.filter(l => l.log_date === date && l.status === 'yes').length;
  // Consecutive-day streak (≥50% of habits done) ending on `endDate`, from a logs array
  const streakEndingOn = (logs, endDate) => {
    const done = {};
    logs.forEach(l => { if (l.status === 'yes') done[l.log_date] = (done[l.log_date] || 0) + 1; });
    const threshold = ALL_HABITS.length * 0.5;
    let s = 0; const d = new Date(endDate);
    while (true) {
      const ds = d.toISOString().split('T')[0];
      if ((done[ds] || 0) >= threshold) { s++; d.setDate(d.getDate() - 1); } else break;
    }
    return s;
  };

  const logHabit = (hid, date, status) => {
    playClick();
    setHabitLogs(prev => {
      const was = prev.find(l => l.habit_id === hid && l.log_date === date)?.status;
      const out = prev.filter(l => !(l.habit_id === hid && l.log_date === date));
      const next = status ? [...out, { habit_id: hid, log_date: date, status }] : out;

      // Only award on a fresh "yes" (not on un-mark or toggling 'no')
      if (status === 'yes' && was !== 'yes') {
        let gained = 5; // per-habit base

        // Perfect-day bonus: this completion just filled the entire day's roster
        const doneBefore = habitsDoneOn(prev, date);
        const doneAfter = habitsDoneOn(next, date);
        if (doneAfter === ALL_HABITS.length && doneBefore < ALL_HABITS.length) {
          gained += 25;
        }

        // Streak bonus: if this day newly crosses the ≥50% threshold, reward the
        // streak length it produces (+3/day capped at +30) plus milestone bonuses.
        const threshold = ALL_HABITS.length * 0.5;
        if (doneAfter >= threshold && doneBefore < threshold) {
          const streak = streakEndingOn(next, date);
          gained += Math.min(streak * 3, 30);
          if (streak === 7) gained += 50;
          else if (streak === 30) gained += 200;
          else if (streak === 100) gained += 500;
        }

        setXp(x => x + gained);
      }
      return next;
    });
  };

  const switchTab = (t) => {
    if (t === tab) return;
    playClick();
    setTabTransition(true);
    setTimeout(() => { setTab(t); setTabTransition(false); }, 150);
  };

  const handleDiscipline = (type) => {
    playClick();
    if (type === 'clean') {
      if (todayDiscipline?.type === 'clean') return;
      setDisciplineLogs(prev => [...prev.filter(d => d.date !== todayStr), { date: todayStr, type: 'clean' }]);
      setXp(x => x + 10);
    } else {
      setDisciplineConfirm('pending');
    }
  };

  const confirmRelapse = () => {
    setDisciplineLogs(prev => [...prev.filter(d => d.date !== todayStr), { date: todayStr, type: 'evil' }]);
    setCleanStart(todayStr); // restart the clean streak from today
    setDisciplineConfirm(null);
  };


  const addItem = () => {
    if (!newItem.name.trim()) return;
    setItems(prev => [...prev, { ...newItem, id: 'item_' + Date.now(), created: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric', year: '2-digit' }).replace(/\//g, '.') }]);
    setNewItem({ type: 'idea', name: '', note: '', status: 'idea' }); setShowAddItem(false);
  };
  const removeItem = (id) => setItems(prev => prev.filter(i => i.id !== id));
  const updateItemStatus = (id, status) => setItems(prev => prev.map(i => i.id === id ? { ...i, status } : i));



  // Journal
  const saveJournal = () => {
    const hasContent = journalDraft.intention.trim() || journalDraft.reflection.trim() || journalDraft.win.trim() || journalDraft.gratitudes.some(g => g.trim());
    if (!hasContent) return;
    const entry = {
      id: Date.now(),
      date: todayStr,
      time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
      intention: journalDraft.intention.trim(),
      reflection: journalDraft.reflection.trim(),
      gratitudes: journalDraft.gratitudes.filter(g => g.trim()),
      win: journalDraft.win.trim(),
      dayRating: journalDraft.dayRating,
      habitsCompleted: totalHabitsToday,
      morningScore,
    };
    setJournalEntries(prev => [...prev, entry]);
    setJournalDraft({ intention: '', reflection: '', gratitudes: ['', '', ''], win: '', dayRating: 7 });
    setXp(x => x + 20);
  };

  const todayJournal = journalEntries.filter(e => e.date === todayStr);
  const filteredJournal = journalSearch
    ? journalEntries.filter(e => (e.intention + e.reflection + e.win + e.gratitudes.join(' ')).toLowerCase().includes(journalSearch.toLowerCase()))
    : journalEntries;

  // Learning
  const addLearning = () => {
    if (!newLearning.title.trim()) return;
    setLearnings(prev => [...prev, { ...newLearning, id: 'learn_' + Date.now(), created: todayStr }]);
    setNewLearning({ title: '', category: 'AI & Tech', type: 'course', progress: 0, totalHours: 0, loggedHours: 0, notes: '', status: 'active' });
    setShowAddLearning(false);
  };
  const updateLearning = (id, field, val) => setLearnings(prev => prev.map(l => l.id === id ? { ...l, [field]: val } : l));
  const removeLearning = (id) => setLearnings(prev => prev.filter(l => l.id !== id));
  const logLearningTime = (id, hours) => {
    setLearnings(prev => prev.map(l => l.id === id ? { ...l, loggedHours: (l.loggedHours || 0) + hours } : l));
    setXp(x => x + Math.round(hours * 10));
  };

  // Movies
  const toggleMovieWatched = (id) => setMovies(prev => prev.map(m => m.id === id ? { ...m, watched: !m.watched, dateWatched: !m.watched ? getTodayStr() : null } : m));
  const rateMovie = (id, rating) => setMovies(prev => prev.map(m => m.id === id ? { ...m, rating } : m));

  const addMovie = () => {
    if (!movieForm.title.trim()) return;
    const newMovie = {
      id: 'movie_' + Date.now(),
      title: movieForm.title.trim(),
      year: movieForm.year ? parseInt(movieForm.year) : null,
      tag: movieForm.tag,
      note: movieForm.note.trim(),
      rating: null,
      watched: false,
      dateAdded: getTodayStr(),
      dateWatched: null,
    };
    setMovies(prev => [newMovie, ...prev]);
    setMovieForm({ title: '', year: '', tag: 'snxper', note: '', rating: null });
    setShowAddMovie(false);
  };

  const startEditMovie = (m) => {
    setEditingMovieId(m.id);
    setMovieForm({ title: m.title, year: m.year || '', tag: m.tag, note: m.note || '', rating: m.rating });
  };

  const saveEditMovie = () => {
    if (!movieForm.title.trim()) return;
    setMovies(prev => prev.map(m => m.id === editingMovieId ? {
      ...m,
      title: movieForm.title.trim(),
      year: movieForm.year ? parseInt(movieForm.year) : m.year,
      tag: movieForm.tag,
      note: movieForm.note.trim(),
      rating: movieForm.rating,
    } : m));
    setEditingMovieId(null);
    setMovieForm({ title: '', year: '', tag: 'snxper', note: '', rating: null });
  };

  const deleteMovie = (id) => {
    setMovies(prev => prev.filter(m => m.id !== id));
    setMovieDeleteConfirm(null);
  };

  const cancelMovieEdit = () => {
    setEditingMovieId(null);
    setShowAddMovie(false);
    setMovieForm({ title: '', year: '', tag: 'snxper', note: '', rating: null });
  };

  // Weekly movie goal — check if at least 1 movie watched this week
  const moviesWatchedThisWeek = movies.filter(m => m.watched && m.dateWatched && weekDates.includes(m.dateWatched)).length;
  const weeklyMovieGoalMet = moviesWatchedThisWeek >= 1;

  const filteredMovies = movieFilter === 'all' ? movies
    : movieFilter === 'watched' ? movies.filter(m => m.watched)
    : movieFilter === 'unwatched' ? movies.filter(m => !m.watched)
    : movieFilter === 'snxper' ? movies.filter(m => m.tag === 'snxper')
    : movies.filter(m => m.tag === movieFilter);

  // Dashboard context for GHXST
  const dashCtx = useMemo(() => ({
    habits: ALL_HABITS.map(h => ({ ...h, weekScore: weekDates.filter(d => logMap[`${h.id}_${d}`] === 'yes').length })),
    netWorth: { total: totalUSD },
    activityDays: activeDays,
    books, discipline: { daysClean, evil: evilCount },
    projects: items, morningScore, xp,
  }), [logMap, weekDates, totalUSD, activeDays, books, daysClean, evilCount, items, morningScore, xp]);

  const dateStr = new Date().toLocaleDateString('en-AE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const mask = (val) => privacyMode ? '•••••' : val;
  const maskNum = (val) => privacyMode ? '---' : val;

  const kw = KAIZEN_WORDS[kanjiIdx];
  const quote = QUOTES[quoteIdx];


  const ITEM_TYPES = { project: { label: '⚔ Project', color: GOLD }, seed: { label: '🌱 Seed', color: GREEN }, idea: { label: '💡 Idea', color: BLUE } };
  const ITEM_STATUSES = { idea: 'Idea', active: 'Active', paused: 'Paused', done: 'Done ✓' };

  // ── Cloud Sync Gate ───────────────────────────────────────────────────
  if (!syncReady) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: BG }}>
        <span style={{ fontFamily: MONO, fontSize: 10, color: GOLD_A(0.6), letterSpacing: '0.15em' }}>// SYNCING FROM CLOUD...</span>
      </div>
    );
  }

  // ── Splash Screen ─────────────────────────────────────────────────────
  if (splash) {
    return (
      <div style={{
        minHeight: '100vh', background: BG, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', position: 'relative',
        opacity: splashPhase === 4 ? 0 : 1, transition: 'opacity 0.7s ease',
      }}>
        <div className="grid-bg" />
        <div className="scanlines" />

        {/* ── Phase 0-2: Quote ── */}
        <div style={{
          position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          opacity: splashPhase <= 1 ? 1 : 0,
          transform: splashPhase === 2 ? 'scale(0.95)' : 'scale(1)',
          transition: 'opacity 0.8s ease, transform 0.8s ease',
          pointerEvents: splashPhase >= 2 ? 'none' : 'auto',
          zIndex: 2,
        }}>
          {/* Kanji symbol */}
          <div style={{
            fontSize: 64, color: GOLD, fontWeight: 300, letterSpacing: 4,
            opacity: splashPhase >= 0 ? 1 : 0, transform: splashPhase >= 0 ? 'scale(1)' : 'scale(0.8)',
            transition: 'all 0.8s ease', marginBottom: 24,
            textShadow: `0 0 30px ${GOLD_A(0.3)}, 0 0 60px ${GOLD_A(0.1)}`,
          }}>{splashQuote.kanji}</div>

          {/* Quote */}
          <div style={{
            maxWidth: 500, textAlign: 'center', padding: '0 30px',
            opacity: splashPhase >= 1 ? 1 : 0, transform: splashPhase >= 1 ? 'translateY(0)' : 'translateY(10px)',
            transition: 'all 0.6s ease',
          }}>
            <div style={{ fontSize: 15, color: CREAM, lineHeight: 1.7, fontStyle: 'italic', fontFamily: SANS, marginBottom: 10 }}>
              "{splashQuote.text}"
            </div>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.2em', color: GOLD_A(0.4), textTransform: 'uppercase' }}>
              — {splashQuote.author}
            </div>
          </div>
        </div>

        {/* ── Phase 3: Gateway ── */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          width: '100%', maxWidth: 440, padding: '0 20px',
          opacity: splashPhase >= 3 ? 1 : 0,
          transform: splashPhase >= 3 ? 'translateY(0)' : 'translateY(15px)',
          transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          pointerEvents: splashPhase >= 3 ? 'auto' : 'none',
          zIndex: 3,
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div className="gateway-glitch-kanji" style={{ fontSize: 32, fontWeight: 300, marginBottom: 8, letterSpacing: 4, lineHeight: 1 }}>覚悟</div>
            <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.3em', color: GOLD_A(0.5), textTransform: 'uppercase', marginBottom: 4 }}>Kakugo · Set Your Frame</div>
            <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED, letterSpacing: '0.1em' }}>Watch a reel. Clear the noise. Enter with intention.</div>
          </div>

          {gatewayReels.length === 0 ? (
            /* No reels — allow entry + add option */
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED, marginBottom: 16 }}>No reels added yet.</div>
              <button onClick={() => setShowAddReel(true)} style={{
                background: 'transparent', border: `1px dashed ${GOLD_A(0.2)}`, color: GOLD_A(0.4),
                cursor: 'pointer', padding: '8px 16px', borderRadius: 2, fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em', marginBottom: 16,
              }}>+ ADD YOUR FIRST REEL</button>
              {showAddReel && (
                <div style={{ padding: 12, background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.15)}`, borderRadius: 2, marginBottom: 14, textAlign: 'left' }}>
                  <input value={newReelFile} onChange={e => setNewReelFile(e.target.value)} placeholder="Filename e.g. onemillion.mp4"
                    style={{ background: GOLD_A(0.06), border: `1px solid ${GOLD_A(0.12)}`, color: CREAM, padding: '7px 10px', borderRadius: 2, fontFamily: MONO, fontSize: 10, outline: 'none', width: '100%', marginBottom: 6, boxSizing: 'border-box' }}
                  />
                  <input value={newReelLabel} onChange={e => setNewReelLabel(e.target.value)} placeholder="Label (e.g. Morning fire)"
                    onKeyDown={e => e.key === 'Enter' && addGatewayReel()}
                    style={{ background: GOLD_A(0.06), border: `1px solid ${GOLD_A(0.12)}`, color: CREAM, padding: '7px 10px', borderRadius: 2, fontFamily: SANS, fontSize: 11, outline: 'none', width: '100%', marginBottom: 8, boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button onClick={() => setShowAddReel(false)} style={{ background: 'transparent', border: `1px solid ${GOLD_A(0.15)}`, color: MUTED, cursor: 'pointer', padding: '5px 12px', borderRadius: 2, fontFamily: MONO, fontSize: 9 }}>CANCEL</button>
                    <button onClick={addGatewayReel} style={{ background: GOLD_A(0.1), border: `1px solid ${GOLD_A(0.3)}`, color: GOLD, cursor: 'pointer', padding: '5px 14px', borderRadius: 2, fontFamily: MONO, fontSize: 9 }}>ADD</button>
                  </div>
                </div>
              )}
              <button onClick={enterDojo} style={{
                background: 'transparent', border: `1px solid ${GOLD_A(0.3)}`, borderRadius: 2,
                padding: '10px 28px', fontFamily: MONO, fontSize: 10, letterSpacing: '0.2em',
                color: GOLD, cursor: 'pointer', textTransform: 'uppercase',
              }}>ENTER THE DOJO</button>
            </div>
          ) : !gatewaySelectedReel ? (
            /* ── Reel Selection ── */
            <div style={{ width: '100%' }}>
              {gatewayReels.map(r => (
                <div key={r.id} onClick={() => setGatewaySelectedReel(r)} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', marginBottom: 8,
                  background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.12)}`, borderRadius: 2,
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD_A(0.3); e.currentTarget.style.background = GOLD_A(0.08); }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = GOLD_A(0.12); e.currentTarget.style.background = GOLD_A(0.04); }}
                >
                  <span style={{ fontSize: 18 }}>▶</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: CREAM, fontWeight: 500 }}>{r.label}</div>
                    <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.3), marginTop: 2 }}>{r.url.split('/').pop()}</div>
                  </div>
                  <button onClick={e => { e.stopPropagation(); removeGatewayReel(r.id); }} style={{
                    background: 'transparent', border: `1px solid ${RED_A(0.1)}`, color: RED_A(0.3),
                    cursor: 'pointer', padding: '2px 6px', borderRadius: 2, fontSize: 9,
                  }}>✕</button>
                </div>
              ))}

              {showAddReel ? (
                <div style={{ padding: 12, background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.15)}`, borderRadius: 2, marginTop: 10 }}>
                  <input value={newReelFile} onChange={e => setNewReelFile(e.target.value)} placeholder="Filename e.g. onemillion.mp4"
                    style={{ background: GOLD_A(0.06), border: `1px solid ${GOLD_A(0.12)}`, color: CREAM, padding: '7px 10px', borderRadius: 2, fontFamily: MONO, fontSize: 10, outline: 'none', width: '100%', marginBottom: 6, boxSizing: 'border-box' }}
                  />
                  <input value={newReelLabel} onChange={e => setNewReelLabel(e.target.value)} placeholder="Label (e.g. Morning fire)"
                    onKeyDown={e => e.key === 'Enter' && addGatewayReel()}
                    style={{ background: GOLD_A(0.06), border: `1px solid ${GOLD_A(0.12)}`, color: CREAM, padding: '7px 10px', borderRadius: 2, fontFamily: SANS, fontSize: 11, outline: 'none', width: '100%', marginBottom: 8, boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button onClick={() => setShowAddReel(false)} style={{ background: 'transparent', border: `1px solid ${GOLD_A(0.15)}`, color: MUTED, cursor: 'pointer', padding: '5px 12px', borderRadius: 2, fontFamily: MONO, fontSize: 9 }}>CANCEL</button>
                    <button onClick={addGatewayReel} style={{ background: GOLD_A(0.1), border: `1px solid ${GOLD_A(0.3)}`, color: GOLD, cursor: 'pointer', padding: '5px 14px', borderRadius: 2, fontFamily: MONO, fontSize: 9, letterSpacing: '0.08em' }}>ADD REEL</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowAddReel(true)} style={{
                  width: '100%', marginTop: 8, background: 'transparent', border: `1px dashed ${GOLD_A(0.15)}`,
                  color: GOLD_A(0.4), cursor: 'pointer', padding: '10px', borderRadius: 2, fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em',
                }}>+ ADD REEL</button>
              )}

              {/* Skip — enter without watching */}
              <div style={{ textAlign: 'center', marginTop: 20, paddingTop: 18, borderTop: `1px solid ${GOLD_A(0.08)}` }}>
                <button onClick={enterDojo} style={{
                  background: GOLD_A(0.08), border: `1px solid ${GOLD_A(0.3)}`, borderRadius: 2,
                  padding: '11px 30px', fontFamily: MONO, fontSize: 10, letterSpacing: '0.2em',
                  color: GOLD, cursor: 'pointer', textTransform: 'uppercase', transition: 'all 0.2s',
                }} onMouseOver={e => { e.currentTarget.style.background = GOLD_A(0.16); e.currentTarget.style.boxShadow = `0 0 24px ${GOLD_A(0.15)}`; }}
                   onMouseOut={e => { e.currentTarget.style.background = GOLD_A(0.08); e.currentTarget.style.boxShadow = 'none'; }}>
                  Enter the Dojo
                </button>
                <div style={{ fontFamily: MONO, fontSize: 8, color: MUTED, letterSpacing: '0.12em', marginTop: 8 }}>Skip the reel · enter when you're ready</div>
              </div>
            </div>
          ) : (
            /* ── Reel Watching ── */
            <div style={{ width: '100%' }}>
              <div style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.4), letterSpacing: '0.1em', marginBottom: 10, textAlign: 'center' }}>
                {gatewaySelectedReel.label}
              </div>

              {/* Video Player */}
              <div style={{
                width: '100%', borderRadius: 2, overflow: 'hidden',
                border: `1px solid ${GOLD_A(0.15)}`, background: '#000',
                boxShadow: `0 0 30px ${GOLD_A(0.06)}`,
              }}>
                <video
                  src={gatewaySelectedReel.url}
                  autoPlay playsInline
                  controls={false}
                  style={{ display: 'block', width: '100%', maxHeight: 520, objectFit: 'contain', background: '#000' }}
                  onEnded={() => setGatewayUnlocked(true)}
                />
              </div>

              {/* Timer + Enter */}
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                {!gatewayUnlocked ? (
                  <div>
                    <div style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.3), letterSpacing: '0.12em', marginBottom: 6 }}>
                      Watch the reel · {Math.max(0, 20 - gatewayTimer)}s remaining
                    </div>
                    <div style={{ width: '100%', height: 3, background: GOLD_A(0.08), borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(100, (gatewayTimer / 20) * 100)}%`, height: '100%', background: `linear-gradient(90deg, ${GOLD_A(0.5)}, ${GOLD})`, borderRadius: 2, transition: 'width 1s linear' }} />
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="gateway-glitch-kanji" style={{
                      fontSize: 36, fontWeight: 300, marginBottom: 12, letterSpacing: 4, lineHeight: 1,
                    }}>覚悟</div>
                    <div className="gateway-glitch-text" style={{
                      fontFamily: MONO, fontSize: 9, letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: 16,
                    }}>Access Granted · You've earned entry</div>
                    <button onClick={completeGateway} className="gateway-glitch-btn" style={{
                      background: GOLD_A(0.1), border: `1px solid ${GOLD_A(0.4)}`, borderRadius: 2,
                      padding: '12px 32px', fontFamily: MONO, fontSize: 11, letterSpacing: '0.2em',
                      color: GOLD, cursor: 'pointer', textTransform: 'uppercase',
                      transition: 'all 0.3s',
                    }} onMouseOver={e => { e.target.style.background = GOLD_A(0.2); e.target.style.boxShadow = `0 0 30px ${GOLD_A(0.2)}`; }}
                       onMouseOut={e => { e.target.style.background = GOLD_A(0.1); e.target.style.boxShadow = 'none'; }}>
                      ENTER THE DOJO
                    </button>
                  </div>
                )}

                <button onClick={() => setGatewaySelectedReel(null)} style={{
                  marginTop: 12, background: 'transparent', border: `1px solid ${GOLD_A(0.1)}`,
                  color: MUTED, cursor: 'pointer', padding: '6px 14px', borderRadius: 2, fontFamily: MONO, fontSize: 8, letterSpacing: '0.1em',
                }}>← PICK DIFFERENT REEL</button>
              </div>
            </div>
          )}
        </div>

        <style>{globalCSS}</style>
      </div>
    );
  }

  // ── `Ghxst` Room Mode ────────────────────────────────────────────────────
  if (warRoom) {
    return (
      <div style={{ minHeight: '100vh', background: BG, color: CREAM, fontFamily: SANS, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: 30, position: 'relative', overflow: 'hidden' }}>
        {/* Background — rain warrior */}
        <div style={{
          position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
          backgroundImage: 'url(/ghxst-room-bg.jpg)', backgroundSize: 'cover', backgroundPosition: 'center center',
          opacity: 0.3, filter: 'saturate(0.25) brightness(0.4) sepia(0.4) hue-rotate(-10deg)', mixBlendMode: 'lighten',
        }} />
        <div style={{
          position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
          background: 'radial-gradient(ellipse at center 40%, rgba(230,230,232,0.06) 0%, transparent 55%)',
        }} />
        <div className="grid-bg" style={{ zIndex: 1 }} />
        <div className="scanlines" style={{ zIndex: 1 }} />
        <button onClick={() => setWarRoom(false)} style={{ position: 'absolute', top: 20, right: 20, background: 'transparent', border: `1px solid ${GOLD_A(0.2)}`, borderRadius: 2, padding: '6px 14px', fontSize: 10, color: GOLD_A(0.6), cursor: 'pointer', fontFamily: MONO, letterSpacing: '0.1em', zIndex: 3 }}>EXIT GHXST ROOM</button>
        <div style={{ fontSize: 10, fontFamily: MONO, color: GOLD_A(0.4), letterSpacing: '0.3em', marginBottom: 8, textTransform: 'uppercase', textAlign: 'center', width: '100%', position: 'relative', zIndex: 2 }}>GHXST ROOM · 幽室</div>
        <div style={{ textAlign: 'center', marginBottom: 20, width: '100%', position: 'relative', zIndex: 2 }}>
          <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.4em', color: GOLD_A(0.3), marginBottom: 4 }}>毎日の戦い</div>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '0.15em', color: GOLD, textTransform: 'uppercase' }}>ME <span style={{ color: GOLD_A(0.3), fontWeight: 300, fontSize: 16 }}>vs</span> ME</div>
          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.2em', color: GOLD_A(0.35), marginTop: 4 }}>YESTERDAY vs TODAY · EVERY DAY</div>
        </div>

        {/* Single column centred layout */}
        <div style={{ width: '100%', maxWidth: 760, padding: '0 30px', flex: 1, position: 'relative', zIndex: 2 }}>

          <Card glow>
            <CardTitle>GHXST · 幽 · DEEP MODE</CardTitle>
            <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.3), letterSpacing: '0.12em', marginBottom: 10 }}>SONNET · NO LIMITS · YOUR SACRED SPACE</div>
            <GhxstAI dashCtx={dashCtx} playClick={playClick} deepMode={true} />
          </Card>

          {/* GHXST Room Notes */}
          <div style={{ marginTop: 16 }}>
            <Card>
              <CardTitle>記録 · Room Notes</CardTitle>
              <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.3), letterSpacing: '0.1em', marginBottom: 8 }}>YOUR DEEPEST THOUGHTS · PIN WHAT MATTERS</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: ghxstNotes.length > 0 ? 10 : 0 }}>
                <input
                  value={ghxstNoteDraft}
                  onChange={e => setGhxstNoteDraft(e.target.value)}
                  placeholder="Pin a thought, insight, or something GHXST said..."
                  className="inp" style={{ flex: 1, padding: '8px 12px' }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && ghxstNoteDraft.trim()) {
                      setGhxstNotes(prev => [{ id: Date.now(), text: ghxstNoteDraft.trim(), date: getTodayStr(), time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) }, ...prev]);
                      setGhxstNoteDraft('');
                    }
                  }}
                />
                <button onClick={() => {
                  if (ghxstNoteDraft.trim()) {
                    setGhxstNotes(prev => [{ id: Date.now(), text: ghxstNoteDraft.trim(), date: getTodayStr(), time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) }, ...prev]);
                    setGhxstNoteDraft('');
                  }
                }} className="gold-btn" style={{ padding: '8px 16px' }}>Pin</button>
              </div>
              {ghxstNotes.length > 0 && (
                <div style={{ maxHeight: 100, overflowY: 'auto' }} className="thin-scroll">
                  {ghxstNotes.map(n => (
                    <div key={n.id} style={{ display: 'flex', gap: 8, padding: '8px 0', borderBottom: `1px solid ${GOLD_A(0.05)}` }}>
                      <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.3), minWidth: 62, paddingTop: 2 }}>{n.date}<br/>{n.time}</div>
                      <div style={{ flex: 1, fontSize: 12, color: CREAM, lineHeight: 1.6 }}>{n.text}</div>
                      <button onClick={() => setGhxstNotes(prev => prev.filter(x => x.id !== n.id))} className="icon-btn" style={{ color: RED, fontSize: 10 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <div style={{ marginTop: 16 }}>
            <Card>
              <CardTitle>TODAY · {todayStr}</CardTitle>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {ALL_HABITS.map(h => {
                  const s = logMap[`${h.id}_${todayStr}`];
                  return (
                    <button key={h.id} onClick={() => logHabit(h.id, todayStr, !s ? 'yes' : s === 'yes' ? 'partial' : s === 'partial' ? 'no' : null)} style={{
                      background: s === 'yes' ? GREEN_A(0.12) : s === 'partial' ? ORANGE_A(0.1) : s === 'no' ? RED_A(0.1) : GOLD_A(0.04),
                      border: `1px solid ${s === 'yes' ? GREEN_A(0.4) : s === 'partial' ? ORANGE_A(0.3) : s === 'no' ? RED_A(0.3) : GOLD_A(0.14)}`,
                      borderRadius: 2, padding: '8px 14px', fontSize: 11, cursor: 'pointer', fontFamily: MONO,
                      color: s === 'yes' ? GREEN : s === 'partial' ? ORANGE : s === 'no' ? RED : GOLD_A(0.55),
                    }}>{s === 'yes' ? '✓' : s === 'partial' ? '~' : s === 'no' ? '✗' : '·'} {h.name.split('(')[0].trim()}</button>
                  );
                })}
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 16 }}>
                <div><span style={{ fontSize: 28, fontWeight: 800, color: GOLD }}>{totalHabitsToday}</span><span style={{ fontSize: 12, color: MUTED }}>/{ALL_HABITS.length} done</span></div>
                <div><span style={{ fontSize: 28, fontWeight: 800, color: morningScore >= 8 ? GREEN : morningScore >= 5 ? ORANGE : RED }}>{morningScore}</span><span style={{ fontSize: 12, color: MUTED }}>/10 morning</span></div>
              </div>
            </Card>
          </div>

        </div>
        <style>{globalCSS}</style>
      </div>
    );
  }

  // ── Main Render ──────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh',
      background: BG,
      color: CREAM,
      fontFamily: SANS, position: 'relative', WebkitFontSmoothing: 'antialiased',
    }}>
      <div className="grid-bg" />
      <div className="scanlines" />
      {glitchActive && <div className="glitch-overlay" />}

      {/* Persistent audio — plays across all tabs */}
      <audio ref={prayerAudioRef} src="/pray-to-the-ether.mp3" loop />

      <div style={{ position: 'relative', zIndex: 2, display: 'flex', minHeight: '100vh' }}>
        {/* ── Sidebar ── */}
        <nav style={{
          width: 176, flexShrink: 0, background: 'rgba(6,6,5,0.97)', borderRight: `1px solid ${GOLD_A(0.09)}`,
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '22px 0',
          position: 'sticky', top: 0, height: '100vh', backdropFilter: 'blur(12px)',
        }}>
          <div>
            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 18px 14px' }}>
              <div style={{
                width: 30, height: 30, borderRadius: 2, background: GOLD_A(0.1), border: `1px solid ${GOLD_A(0.22)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: GOLD,
                filter: glitchActive ? 'brightness(2)' : 'none', transition: 'filter 0.1s',
              }}>改</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: CREAM, letterSpacing: '0.06em' }}>THE DOJO</div>
                <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.38), letterSpacing: '0.18em', textTransform: 'uppercase', marginTop: 1 }}>道場 · PERSONAL CENTRE</div>
              </div>
            </div>

            {/* XP / Rank */}
            <div style={{ padding: '8px 18px 12px', borderTop: `1px solid ${GOLD_A(0.07)}`, borderBottom: `1px solid ${GOLD_A(0.07)}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.15em', color: rank.color, textTransform: 'uppercase' }}>{rank.kanji} {rank.name}</span>
                <span style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.4) }}>{xp} XP</span>
              </div>
              {rank.nextMin && (
                <div style={{ height: 2, background: GOLD_A(0.1), borderRadius: 1 }}>
                  <div style={{ height: '100%', width: `${Math.min(100, ((xp - rank.min) / (rank.nextMin - rank.min)) * 100)}%`, background: rank.color, borderRadius: 1, transition: 'width 0.5s ease', boxShadow: `0 0 6px ${GOLD_A(0.3)}` }} />
                </div>
              )}
            </div>

            {/* Nav — double-click tab name to edit */}
            {TABS.map(t => {
              const isActive = tab === t.id;
              return (
              <div key={t.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '11px 18px', background: isActive ? GOLD_A(0.055) : 'transparent',
                cursor: 'pointer', width: '100%', textAlign: 'left', position: 'relative', transition: 'background 0.15s, box-shadow 0.15s',
              }}
              onClick={() => { if (editingTab !== t.id) switchTab(t.id); }}
              onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = GOLD_A(0.04); e.currentTarget.querySelector('.tab-kanji').style.color = GOLD; e.currentTarget.querySelector('.tab-label')&&(e.currentTarget.querySelector('.tab-label').style.color = GOLD_A(0.7)); } }}
              onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('.tab-kanji').style.color = GOLD_A(0.3); e.currentTarget.querySelector('.tab-label')&&(e.currentTarget.querySelector('.tab-label').style.color = MUTED); } }}
              >
                <span className="tab-kanji" style={{ fontSize: 16, width: 18, textAlign: 'center', flexShrink: 0, fontWeight: 300, color: isActive ? GOLD : GOLD_A(0.3), transition: 'color 0.15s' }}>{t.kanji}</span>
                {editingTab === t.id ? (
                  <input
                    ref={editTabRef}
                    value={editingTabValue}
                    onChange={e => setEditingTabValue(e.target.value)}
                    onBlur={() => {
                      const val = editingTabValue.trim();
                      if (val) setTabNames(prev => ({ ...prev, [t.id]: val }));
                      else setTabNames(prev => { const n = { ...prev }; delete n[t.id]; return n; });
                      setEditingTab(null);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') e.target.blur();
                      if (e.key === 'Escape') { setEditingTab(null); }
                    }}
                    style={{
                      fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase',
                      color: GOLD_A(0.9), background: GOLD_A(0.08), border: `1px solid ${GOLD_A(0.3)}`,
                      borderRadius: 2, padding: '2px 6px', outline: 'none', width: 90,
                    }}
                    onClick={e => e.stopPropagation()}
                  />
                ) : (
                  <span
                    className="tab-label"
                    onDoubleClick={(e) => { e.stopPropagation(); setEditingTab(t.id); setEditingTabValue(t.en); }}
                    style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: isActive ? GOLD_A(0.9) : MUTED, transition: 'color 0.15s' }}
                    title="Double-click to rename"
                  >{t.en}</span>
                )}
                {isActive && <div style={{ position: 'absolute', left: 0, top: 7, bottom: 7, width: 2, background: GOLD, borderRadius: '0 2px 2px 0' }} />}
              </div>
              );
            })}

            {/* Ghxst Room toggle */}
            <div style={{ padding: '8px 18px 0' }}>
              <button onClick={() => { playSound('/intro-load.mp3'); setWarRoom(true); }} style={{
                width: '100%', background: GOLD_A(0.06), border: `1px solid ${GOLD_A(0.15)}`, borderRadius: 2,
                padding: '8px 12px', fontSize: 9, fontFamily: MONO, letterSpacing: '0.12em', color: GOLD_A(0.6),
                cursor: 'pointer', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{ fontSize: 12 }}>幽</span> GHXST ROOM
              </button>
            </div>
          </div>

          {/* Bottom — kaizen word */}
          <div style={{ padding: '0 18px' }}>
            <div style={{ height: 1, background: GOLD_A(0.07), marginBottom: 8 }} />
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div className="kaizen-fade" style={{ fontSize: 22, color: GOLD_A(0.35), fontWeight: 300, letterSpacing: 2 }}>{kw.word}</div>
              <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.4), letterSpacing: '0.15em', marginTop: 3 }}>{kw.reading}</div>
              <div style={{ fontSize: 9, color: MUTED, marginTop: 2, fontStyle: 'italic' }}>{kw.meaning}</div>
            </div>
            <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.25), letterSpacing: '0.1em', textAlign: 'center' }}>
              {new Date().toLocaleDateString('en-AE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
            </div>
          </div>
        </nav>

        {/* ── Main Content ── */}
        <main style={{ flex: 1, padding: '22px 26px', overflowY: 'auto', minHeight: '100vh', opacity: tabTransition ? 0 : 1, transform: tabTransition ? 'translateY(6px)' : 'none', transition: 'opacity 0.15s, transform 0.15s' }}>
          {/* Top bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, paddingBottom: 18, borderBottom: `1px solid ${GOLD_A(0.07)}` }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 14 }}>🥷</span>
                <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.2em', color: GOLD, fontWeight: 500 }}>GHXST</span>
                <span style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.25) }}>&</span>
                <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.2em', color: CREAM, fontWeight: 500 }}>SNXPER</span>
              </div>
              <div style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD_A(0.38), marginBottom: 5 }}>
                {dateStr}
                {perfectMorning && <span style={{ marginLeft: 6, fontSize: 12 }} title="Perfect morning">🔥</span>}
                {habitStreak > 2 && <span style={{ marginLeft: 4, fontFamily: MONO, fontSize: 8, color: GREEN, fontWeight: 700 }}>{habitStreak}d streak</span>}
              </div>
              <div style={{ fontSize: 21, fontWeight: 700, color: CREAM, letterSpacing: '-0.02em', filter: glitchActive ? 'blur(0.5px) brightness(1.5)' : 'none', transition: 'filter 0.1s' }}>
                {TABS.find(t => t.id === tab)?.en}
                <span style={{ color: GOLD_A(0.3), fontWeight: 300, marginLeft: 8, fontSize: 17 }}>{TABS.find(t => t.id === tab)?.label}</span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
              {[
                { v: privacyMode ? '•/10' : `${morningScore}/10`, l: '朝 morning', col: morningScore >= 8 ? GREEN : morningScore >= 5 ? ORANGE : morningScore > 0 ? RED : MUTED },
                { v: `${bricks.length}`, l: '壁 bricks', col: TITANIUM },
                { v: privacyMode ? '•••' : `${daysClean}d`, l: '自制 clean', col: daysClean > 14 ? GREEN : daysClean > 3 ? ORANGE : RED },
              ].map((s, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div style={{ width: 1, height: 26, background: GOLD_A(0.1) }} />}
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: s.col || GOLD, letterSpacing: '-0.02em', lineHeight: 1 }}>
                      {s.v}
                      {s.extra && <span style={{ fontSize: 12, marginLeft: 3, color: s.extra === '↑' ? GREEN : RED }}>{s.extra}</span>}
                    </div>
                    <div style={{ fontFamily: MONO, fontSize: 8, color: MUTED, letterSpacing: '0.12em', marginTop: 3 }}>{s.l}</div>
                  </div>
                </React.Fragment>
              ))}
              </div>
              {/* Mode toggles */}
              <div style={{ display: 'flex', gap: 5 }}>
                <button onClick={() => setPrivacyMode(p => !p)} style={{
                  fontFamily: MONO, fontSize: 8, letterSpacing: '0.08em', padding: '4px 10px', borderRadius: 2, cursor: 'pointer',
                  background: privacyMode ? GOLD_A(0.15) : 'transparent',
                  border: `1px solid ${privacyMode ? GOLD_A(0.4) : GOLD_A(0.12)}`,
                  color: privacyMode ? GOLD : MUTED, transition: 'all 0.2s',
                }}>{privacyMode ? 'PRIVATE' : 'HIDE'}</button>
              </div>
            </div>
          </div>

          {/* ═══ OVERVIEW ═══ */}
          {tab === 'home' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>

              {/* Morning Briefing — spans 2 cols */}
              <div style={{ gridColumn: '1/3', gridRow: '1' }}>
                <Card>
                  <CardTitle>報告 · Morning Briefing</CardTitle>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <div style={{ padding: '10px 12px', background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.08)}`, borderRadius: 2 }}>
                      <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.4), letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>自制 Clean</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: daysClean > 14 ? GREEN : daysClean > 3 ? ORANGE : RED }}>{privacyMode ? '•••' : `${daysClean}d`}</div>
                      <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED, marginTop: 2 }}>{daysClean > 14 ? 'locked in' : daysClean > 3 ? 'holding' : 'day by day'}</div>
                    </div>
                    <div style={{ padding: '10px 12px', background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.08)}`, borderRadius: 2 }}>
                      <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.4), letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>Yesterday</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: yesterdayHabits >= ALL_HABITS.length * 0.7 ? GREEN : yesterdayHabits > 0 ? ORANGE : MUTED }}>{yesterdayHabits}/{ALL_HABITS.length}</div>
                      <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED, marginTop: 2 }}>habits done</div>
                    </div>
                    <div style={{ padding: '10px 12px', background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.08)}`, borderRadius: 2 }}>
                      <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.4), letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>Streak</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: habitStreak >= 7 ? GREEN : habitStreak >= 3 ? GOLD : MUTED }}>{habitStreak}d</div>
                      <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED, marginTop: 2 }}>{habitStreak >= 7 ? 'warrior mode' : habitStreak >= 3 ? 'building' : 'start today'}</div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Right col */}
              <div style={{ gridColumn: '3', gridRow: '1 / 4', display: 'flex', flexDirection: 'column', gap: 12 }}>

                {/* Morning Score */}
                <Card glow={morningScore >= 8}>
                  <CardTitle>朝 · Morning Score</CardTitle>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 36, fontWeight: 800, color: morningScore >= 8 ? GREEN : morningScore >= 5 ? ORANGE : morningScore > 0 ? RED : MUTED, letterSpacing: '-0.03em', lineHeight: 1 }}>{morningScore}</span>
                    <span style={{ fontSize: 14, color: MUTED }}>/10</span>
                  </div>
                  <div style={{ display: 'flex', gap: 3, marginTop: 10 }}>
                    {DEFAULT_HABITS.morning.map(h => {
                      const s = logMap[`${h.id}_${todayStr}`];
                      return <div key={h.id} style={{ flex: 1, height: 3, borderRadius: 1, background: s === 'yes' ? GREEN : GOLD_A(0.1), transition: 'background 0.3s' }} />;
                    })}
                  </div>
                </Card>

                {/* Today habits quick */}
                <Card>
                  <CardTitle>Today · {totalHabitsToday}/{ALL_HABITS.length}</CardTitle>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {ALL_HABITS.map(h => {
                      const s = logMap[`${h.id}_${todayStr}`];
                      return (
                        <div key={h.id} onClick={() => logHabit(h.id, todayStr, !s ? 'yes' : s === 'yes' ? 'partial' : s === 'partial' ? 'no' : null)} style={{
                          fontSize: 9, padding: '3px 7px', cursor: 'pointer',
                          border: `1px solid ${s === 'yes' ? GREEN_A(0.4) : GOLD_A(0.1)}`, borderRadius: 2,
                          color: s === 'yes' ? GREEN : s === 'partial' ? ORANGE : s === 'no' ? RED : MUTED,
                          fontFamily: MONO, background: s === 'yes' ? GREEN_A(0.06) : 'transparent', transition: 'all 0.15s',
                        }}>
                          {s === 'yes' ? '✓ ' : s === 'partial' ? '~ ' : s === 'no' ? '✗ ' : ''}{h.name.split(' ')[0].slice(0, 8)}
                        </div>
                      );
                    })}
                  </div>
                </Card>

                {/* Current book */}
                {books.find(b => b.status === 'reading') && (() => {
                  const bk = books.find(b => b.status === 'reading');
                  const pct = bk.total_pages ? Math.round(bk.current_page / bk.total_pages * 100) : 0;
                  return (
                    <Card>
                      <CardTitle>本 · Reading</CardTitle>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ width: 5, minHeight: 52, borderRadius: 2, background: bk.cover_color, flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: CREAM, lineHeight: 1.3, marginBottom: 2 }}>{bk.title}</div>
                          <div style={{ fontSize: 10, color: MUTED, marginBottom: 8 }}>{bk.author}</div>
                          <div style={{ height: 2, background: GOLD_A(0.1), borderRadius: 1 }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: bk.cover_color, borderRadius: 1, transition: 'width 0.5s' }} />
                          </div>
                          <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.4), marginTop: 3, letterSpacing: '0.08em' }}>{pct}% · p.{bk.current_page}</div>
                        </div>
                      </div>
                    </Card>
                  );
                })()}
              </div>

              {/* GHXST AI — spans 2 cols */}
              <div style={{ gridColumn: '1/3', gridRow: '2' }}>
                <Card glow={glitchActive}>
                  <CardTitle>GHXST <span style={{ color: GOLD_A(0.25) }}>· 幽 · AI SHINOBI</span></CardTitle>
                  <GhxstAI dashCtx={dashCtx} playClick={playClick} />
                </Card>
              </div>

              {/* GHXST Notes — pinned insights */}
              <div style={{ gridColumn: '1/3', gridRow: '3' }}>
                <Card>
                  <CardTitle>記録 · GHXST Notes</CardTitle>
                  <div style={{ display: 'flex', gap: 8, marginBottom: ghxstNotes.length > 0 ? 10 : 0 }}>
                    <input
                      value={ghxstNoteDraft}
                      onChange={e => setGhxstNoteDraft(e.target.value)}
                      placeholder="Pin something GHXST said, or a key insight..."
                      className="inp" style={{ flex: 1, padding: '7px 11px' }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && ghxstNoteDraft.trim()) {
                          setGhxstNotes(prev => [{ id: Date.now(), text: ghxstNoteDraft.trim(), date: getTodayStr(), time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) }, ...prev]);
                          setGhxstNoteDraft('');
                        }
                      }}
                    />
                    <button onClick={() => {
                      if (ghxstNoteDraft.trim()) {
                        setGhxstNotes(prev => [{ id: Date.now(), text: ghxstNoteDraft.trim(), date: getTodayStr(), time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) }, ...prev]);
                        setGhxstNoteDraft('');
                      }
                    }} className="gold-btn" style={{ padding: '7px 14px' }}>Pin</button>
                  </div>
                  {ghxstNotes.length > 0 && (
                    <div style={{ maxHeight: 80, overflowY: 'auto' }} className="thin-scroll">
                      {ghxstNotes.map(n => (
                        <div key={n.id} style={{ display: 'flex', gap: 8, padding: '7px 0', borderBottom: `1px solid ${GOLD_A(0.05)}` }}>
                          <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.3), minWidth: 62, paddingTop: 2 }}>{n.date}<br/>{n.time}</div>
                          <div style={{ flex: 1, fontSize: 11.5, color: CREAM, lineHeight: 1.5, filter: privacyMode ? 'blur(5px)' : 'none', userSelect: privacyMode ? 'none' : 'auto' }}>{n.text}</div>
                          <button onClick={() => setGhxstNotes(prev => prev.filter(x => x.id !== n.id))} className="icon-btn" style={{ color: RED, fontSize: 10 }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              {/* Weekly Review */}
              <div style={{ gridColumn: '1/3' }}>
                <Card>
                  <CardTitle>週報 · Weekly Review</CardTitle>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                    {[
                      { label: 'Habits', value: `${Math.round(weekDates.reduce((sum, d) => sum + ALL_HABITS.filter(h => logMap[`${h.id}_${d}`] === 'yes').length, 0) / (ALL_HABITS.length * 7) * 100)}%`, sub: 'completion', color: (() => { const pct = Math.round(weekDates.reduce((sum, d) => sum + ALL_HABITS.filter(h => logMap[`${h.id}_${d}`] === 'yes').length, 0) / (ALL_HABITS.length * 7) * 100); return pct >= 70 ? GREEN : pct >= 40 ? ORANGE : RED; })() },
                      { label: 'Streak', value: `${habitStreak}d`, sub: 'discipline', color: habitStreak >= 7 ? GREEN : habitStreak >= 3 ? GOLD : MUTED },
                      { label: 'Discipline', value: `${daysClean}d`, sub: 'clean', color: daysClean > 14 ? GREEN : daysClean > 3 ? ORANGE : RED },
                      { label: 'XP', value: `+${weekDates.reduce((sum, d) => sum + ALL_HABITS.filter(h => logMap[`${h.id}_${d}`] === 'yes').length * 5, 0)}`, sub: 'this week', color: GOLD },
                      { label: 'Movies', value: `${moviesWatchedThisWeek}/1`, sub: weeklyMovieGoalMet ? 'goal met' : 'pending', color: weeklyMovieGoalMet ? GREEN : MUTED },
                    ].map(s => (
                      <div key={s.label} style={{ textAlign: 'center', padding: '10px 6px', background: GOLD_A(0.03), border: `1px solid ${GOLD_A(0.06)}`, borderRadius: 2 }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: privacyMode ? MUTED : s.color, lineHeight: 1 }}>{privacyMode ? '•••' : s.value}</div>
                        <div style={{ fontFamily: MONO, fontSize: 7, color: GOLD_A(0.4), letterSpacing: '0.12em', marginTop: 4, textTransform: 'uppercase' }}>{s.label}</div>
                        <div style={{ fontFamily: MONO, fontSize: 7, color: MUTED, marginTop: 1 }}>{s.sub}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Bottom row — heat map + quote */}
              <div style={{ gridColumn: '1/3', display: 'flex', gap: 14 }}>
                <div style={{ flex: 2 }}>
                  <Card>
                    <CardTitle>活動 · Activity Heat Map <span style={{ color: GOLD_A(0.25) }}>/ 16 weeks</span></CardTitle>
                    <HeatMap logs={habitLogs} />
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontFamily: MONO, fontSize: 8, color: MUTED }}>Less</span>
                      {[0.06, 0.15, 0.3].map((a, i) => <div key={i} style={{ width: 10, height: 10, borderRadius: 1, background: GOLD_A(a) }} />)}
                      {[0.4, 0.7].map((a, i) => <div key={i} style={{ width: 10, height: 10, borderRadius: 1, background: GREEN_A(a) }} />)}
                      <span style={{ fontFamily: MONO, fontSize: 8, color: MUTED }}>More</span>
                    </div>
                  </Card>
                </div>
                <div style={{ flex: 1 }}>
                  <Card style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <CardTitle>言葉 · Quote</CardTitle>
                    <div style={{ fontSize: 12, color: CREAM, lineHeight: 1.65, fontStyle: 'italic', marginBottom: 5 }}>"{quote.text}"</div>
                    <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.4), letterSpacing: '0.15em', textTransform: 'uppercase' }}>— {quote.author}</div>
                  </Card>
                </div>
              </div>
            </div>
          )}

          {/* ═══ HABITS ═══ */}
          {tab === 'habits' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

              {/* THE WALL — Daily Brick */}
              {(() => {
                const hour = new Date().getHours();
                const isUnlocked = hour >= 20;
                const todayBrick = bricks.find(b => b.date === todayStr);
                const totalBricks = bricks.length;
                const bricksPerRow = 14;
                const rows = [];
                for (let i = 0; i < Math.max(Math.ceil((totalBricks + 1) / bricksPerRow), 3); i++) {
                  const row = [];
                  for (let j = 0; j < bricksPerRow; j++) {
                    const idx = i * bricksPerRow + j;
                    row.push(idx < totalBricks ? 'stacked' : idx === totalBricks ? 'next' : 'empty');
                  }
                  rows.push(row);
                }

                const stackBrick = () => {
                  playClick();
                  setJustStacked(true);
                  setBricks(prev => [...prev, { date: todayStr, time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) }]);
                  setTimeout(() => setJustStacked(false), 1500);
                };

                return (
                  <Card glow={todayBrick || justStacked} style={todayBrick || justStacked ? { borderColor: TITANIUM_A(0.25), boxShadow: `0 0 20px ${TITANIUM_A(0.08)}, inset 0 0 30px ${TITANIUM_A(0.03)}` } : {}}>
                    <CardTitle>壁 · The Wall <span style={{ color: TITANIUM_A(0.35) }}>/ titanium</span></CardTitle>

                    <div style={{ textAlign: 'center', marginBottom: 10 }}>
                      <span style={{ fontSize: 40, fontWeight: 800, color: TITANIUM, lineHeight: 1, letterSpacing: '-0.02em', textShadow: `0 0 15px ${TITANIUM_A(0.2)}` }}>{totalBricks}</span>
                      <span style={{ fontSize: 12, color: TITANIUM_A(0.5), marginLeft: 6 }}>bricks in the fortress</span>
                    </div>

                    <div style={{ fontFamily: MONO, fontSize: 9, color: TITANIUM_A(0.3), textAlign: 'center', marginBottom: 16, letterSpacing: '0.1em' }}>
                      UNBREAKABLE · UNSHAKABLE · UNSTOPPABLE
                    </div>

                    {/* The Wall */}
                    <div style={{ display: 'flex', flexDirection: 'column-reverse', gap: 1, marginBottom: 16, padding: '0 4px' }}>
                      {rows.map((row, ri) => (
                        <div key={ri} style={{ display: 'flex', gap: 1, justifyContent: 'center', marginLeft: ri % 2 === 1 ? 16 : 0 }}>
                          {row.map((cell, ci) => {
                            const isNewBrick = cell === 'stacked' && justStacked && (ri * bricksPerRow + ci) === totalBricks - 1;
                            return (
                              <div key={ci} style={{
                                width: 36, height: 12, borderRadius: 1,
                                background: cell === 'stacked'
                                  ? `linear-gradient(180deg, ${TITANIUM_A(0.5)} 0%, ${TITANIUM_A(0.3)} 100%)`
                                  : cell === 'next' && isUnlocked && !todayBrick
                                  ? 'transparent'
                                  : TITANIUM_A(0.03),
                                border: cell === 'stacked'
                                  ? `1px solid ${TITANIUM_A(0.2)}`
                                  : cell === 'next' && isUnlocked && !todayBrick
                                  ? `1px dashed ${TITANIUM_A(0.3)}`
                                  : `1px solid ${TITANIUM_A(0.04)}`,
                                boxShadow: cell === 'stacked'
                                  ? `inset 0 1px 0 ${TITANIUM_A(0.35)}, 0 1px 1px ${TITANIUM_A(0.06)}`
                                  : 'none',
                                animation: isNewBrick ? 'brickDrop 0.5s ease-out' : 'none',
                                transition: 'all 0.3s',
                              }} />
                            );
                          })}
                        </div>
                      ))}
                    </div>

                    {/* Status */}
                    <div style={{ textAlign: 'center' }}>
                      {justStacked ? (
                        <div style={{ animation: 'brickDrop 0.4s ease-out' }}>
                          <div style={{ fontFamily: MONO, fontSize: 11, color: TITANIUM, letterSpacing: '0.15em', fontWeight: 700 }}>BRICK STACKED</div>
                          <div style={{ fontSize: 10, color: TITANIUM_A(0.5), marginTop: 4 }}>The fortress grows stronger. Titanium.</div>
                        </div>
                      ) : todayBrick ? (
                        <div>
                          <div style={{ fontFamily: MONO, fontSize: 10, color: TITANIUM, letterSpacing: '0.12em' }}>TODAY'S BRICK LAID</div>
                          <div style={{ fontFamily: MONO, fontSize: 8, color: TITANIUM_A(0.4), marginTop: 4 }}>{todayBrick.time} · day {totalBricks}</div>
                        </div>
                      ) : isUnlocked ? (
                        <button onClick={stackBrick} style={{
                          background: `linear-gradient(180deg, ${TITANIUM} 0%, ${TITANIUM_A(0.7)} 100%)`,
                          border: 'none', borderRadius: 2, padding: '12px 32px', fontSize: 11, fontWeight: 700,
                          color: BG, cursor: 'pointer', fontFamily: MONO, letterSpacing: '0.12em',
                          boxShadow: `0 2px 12px ${TITANIUM_A(0.25)}`,
                          transition: 'all 0.2s',
                        }}>
                          STACK TODAY'S BRICK
                        </button>
                      ) : (
                        <div>
                          <div style={{ fontFamily: MONO, fontSize: 10, color: TITANIUM_A(0.5), letterSpacing: '0.1em' }}>UNLOCKS AT 8:00 PM</div>
                          <div style={{ fontFamily: MONO, fontSize: 8, color: TITANIUM_A(0.25), marginTop: 4 }}>Earn your brick. Finish the day first.</div>
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })()}

              {/* Morning Score Banner */}
              <Card glow={morningScore >= 8}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <CardTitle>朝のスコア · Morning Score</CardTitle>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span style={{ fontSize: 48, fontWeight: 800, color: morningScore >= 8 ? GREEN : morningScore >= 5 ? ORANGE : morningScore > 0 ? RED : MUTED, lineHeight: 1 }}>{morningScore}</span>
                      <span style={{ fontSize: 16, color: MUTED }}>/10</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {DEFAULT_HABITS.morning.map(h => {
                      const s = logMap[`${h.id}_${todayStr}`];
                      return (
                        <div key={h.id} onClick={() => logHabit(h.id, todayStr, !s ? 'yes' : null)} style={{
                          width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, cursor: 'pointer',
                          background: s === 'yes' ? GREEN_A(0.15) : GOLD_A(0.05), border: `1px solid ${s === 'yes' ? GREEN_A(0.4) : GOLD_A(0.12)}`,
                          boxShadow: s === 'yes' ? `0 0 8px ${GREEN_A(0.2)}` : 'none', transition: 'all 0.2s',
                        }} title={h.name}>{h.icon}</div>
                      );
                    })}
                  </div>
                </div>
              </Card>

              {[
                { title: '朝 · Morning Routine', habits: DEFAULT_HABITS.morning },
                { title: '構 · Building', habits: DEFAULT_HABITS.building },
                { title: '体 · Fitness', habits: DEFAULT_HABITS.fitness },
                { title: '本 · Mind', habits: DEFAULT_HABITS.mind },
              ].map(sec => (
                <Card key={sec.title}>
                  <CardTitle>{sec.title}</CardTitle>
                  <HabitGrid habits={sec.habits} logs={habitLogs} weekDates={weekDates} todayStr={todayStr} onLog={logHabit} />
                  <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {sec.habits.map(h => {
                      const s = logMap[`${h.id}_${todayStr}`];
                      const next = !s ? 'yes' : s === 'yes' ? 'partial' : s === 'partial' ? 'no' : null;
                      return (
                        <button key={h.id} onClick={() => logHabit(h.id, todayStr, next)} className="habit-btn" style={{
                          background: s === 'yes' ? GREEN_A(0.1) : s === 'partial' ? ORANGE_A(0.1) : s === 'no' ? RED_A(0.1) : GOLD_A(0.04),
                          border: `1px solid ${s === 'yes' ? GREEN_A(0.35) : s === 'partial' ? ORANGE_A(0.3) : s === 'no' ? RED_A(0.3) : GOLD_A(0.14)}`,
                          borderRadius: 2, padding: '6px 11px', fontSize: 10.5,
                          color: s === 'yes' ? GREEN : s === 'partial' ? ORANGE : s === 'no' ? RED : GOLD_A(0.55),
                          cursor: 'pointer', fontFamily: MONO, letterSpacing: '0.04em',
                        }}>
                          {s === 'yes' ? '✓' : s === 'partial' ? '~' : s === 'no' ? '✗' : '·'} {h.name.split('(')[0].trim().slice(0, 20)}
                        </button>
                      );
                    })}
                  </div>
                </Card>
              ))}

              {/* Heat Map */}
              <Card>
                <CardTitle>活動マップ · Activity Map</CardTitle>
                <HeatMap logs={habitLogs} />
              </Card>
            </div>
          )}

          {/* ═══ FAITH & PRAYER ═══ */}
          {tab === 'faith' && (() => {
            const todayFaith = faithCompleted[todayStr];
            const passages = [0, 1, 2].map(i => BIBLE_PASSAGES[(todayPassageIdx + i) % BIBLE_PASSAGES.length]);
            const timerMin = Math.floor(faithTimer / 60);
            const timerSec = faithTimer % 60;
            const timerDone = faithTimer >= 300;
            const streak = (() => {
              let s = 0;
              let d = new Date();
              while (true) {
                const ds = d.toISOString().split('T')[0];
                if (faithCompleted[ds]) { s++; d.setDate(d.getDate() - 1); } else break;
              }
              return s;
            })();

            // Today's daily reading from the batched queue
            const readingIdx = readingPlan.queue[daysSince(readingPlan.start, todayStr) % readingPlan.queue.length];
            const reading = DAILY_READINGS[readingIdx] || DAILY_READINGS[0];
            const readingPagesRaw = splitReading(reading.text);
            const readingPages = readingPagesRaw[1] ? readingPagesRaw : [reading.text]; // 1 page if too short to split
            const pageCount = readingPages.length;
            const curPage = Math.min(readingPage, pageCount - 1);
            const onLastPage = curPage === pageCount - 1;
            const readToday = readingCompleted[todayStr];
            const readingStreak = (() => {
              let s = 0;
              let d = new Date();
              while (true) {
                const ds = d.toISOString().split('T')[0];
                if (readingCompleted[ds]) { s++; d.setDate(d.getDate() - 1); } else break;
              }
              return s;
            })();

            return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 720, margin: '0 auto' }}>

              {/* Header */}
              <Card glow={timerDone}>
                <div style={{ textAlign: 'center', padding: '10px 0 6px' }}>
                  <div style={{ fontSize: 32, color: GOLD, fontWeight: 300, marginBottom: 6 }}>✝</div>
                  <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.3em', color: GOLD_A(0.4), marginBottom: 4 }}>信仰 · FAITH</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: CREAM, letterSpacing: '0.05em' }}>Be Still and Know</div>
                  <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>5 minutes with the Lord. Every morning. No exceptions.</div>
                  {streak > 0 && <div style={{ fontFamily: MONO, fontSize: 9, color: GREEN, marginTop: 8, letterSpacing: '0.1em' }}>{streak} day streak of faith</div>}
                </div>
              </Card>

              {/* Devotion Timer */}
              <Card glow={faithTimerRunning}>
                <CardTitle>祈り · Devotion Timer</CardTitle>
                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                  <div style={{
                    fontSize: 52, fontWeight: 800, color: timerDone ? GREEN : faithTimerRunning ? GOLD : CREAM,
                    letterSpacing: '0.05em', lineHeight: 1, fontFamily: MONO,
                    textShadow: faithTimerRunning ? `0 0 20px ${GOLD_A(0.3)}` : 'none',
                    transition: 'all 0.3s',
                  }}>
                    {String(timerMin).padStart(2, '0')}:{String(timerSec).padStart(2, '0')}
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED, marginTop: 6, letterSpacing: '0.15em' }}>
                    {timerDone ? 'DEVOTION COMPLETE — GOD IS WITH YOU' : faithTimerRunning ? 'BE PRESENT · BREATHE · PRAY' : 'START YOUR 5 MINUTES WITH GOD'}
                  </div>

                  {/* Progress bar */}
                  <div style={{ height: 4, background: GOLD_A(0.08), borderRadius: 2, margin: '14px auto', maxWidth: 300 }}>
                    <div style={{ height: '100%', width: `${Math.min(100, (faithTimer / 300) * 100)}%`, background: timerDone ? GREEN : GOLD, borderRadius: 2, transition: 'width 1s linear' }} />
                  </div>

                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 8 }}>
                    {!faithTimerRunning && !timerDone && (
                      <button onClick={() => { playClick(); setFaithTimerRunning(true); }} className="gold-btn" style={{ padding: '10px 28px', fontSize: 11 }}>BEGIN DEVOTION</button>
                    )}
                    {faithTimerRunning && (
                      <button onClick={() => { playClick(); setFaithTimerRunning(false); }} className="ghost-btn" style={{ padding: '10px 20px' }}>PAUSE</button>
                    )}
                    {timerDone && !todayFaith && (
                      <button onClick={() => { playClick(); setFaithCompleted(prev => ({ ...prev, [todayStr]: true })); }} className="gold-btn" style={{ padding: '10px 28px', fontSize: 11, background: GREEN, color: BG }}>MARK COMPLETE</button>
                    )}
                    {todayFaith && <div style={{ fontFamily: MONO, fontSize: 10, color: GREEN, letterSpacing: '0.12em' }}>TODAY COMPLETED</div>}
                    {faithTimer > 0 && !faithTimerRunning && (
                      <button onClick={() => { setFaithTimer(0); setFaithTimerRunning(false); }} className="ghost-btn" style={{ padding: '10px 16px', fontSize: 9 }}>RESET</button>
                    )}
                  </div>

                  {/* Prayer Music */}
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${GOLD_A(0.06)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                    <button onClick={() => {
                      if (prayerPlaying) { prayerAudioRef.current?.pause(); setPrayerPlaying(false); }
                      else { prayerAudioRef.current?.play(); setPrayerPlaying(true); }
                    }} style={{
                      background: 'transparent', border: `1px solid ${prayerPlaying ? GOLD_A(0.3) : GOLD_A(0.12)}`,
                      color: prayerPlaying ? GOLD : GOLD_A(0.4), cursor: 'pointer', padding: '6px 14px', borderRadius: 2,
                      fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em', transition: 'all 0.2s',
                    }}>{prayerPlaying ? '■ STOP' : '▶ PRAY TO THE ETHER'}</button>
                  </div>
                </div>
              </Card>
              <Card>
                <CardTitle>主の祈り · The Lord's Prayer</CardTitle>
                <div style={{ fontSize: 13, color: CREAM, lineHeight: 2, textAlign: 'center', padding: '8px 20px', fontStyle: 'italic' }}>
                  Our Father, who art in heaven,<br/>
                  hallowed be thy name.<br/>
                  Thy kingdom come,<br/>
                  thy will be done,<br/>
                  on earth as it is in heaven.<br/>
                  <br/>
                  Give us this day our daily bread,<br/>
                  and forgive us our trespasses,<br/>
                  as we forgive those who trespass against us.<br/>
                  And lead us not into temptation,<br/>
                  but deliver us from evil.<br/>
                  <br/>
                  <span style={{ color: GOLD, fontWeight: 600, fontStyle: 'normal' }}>Amen.</span>
                </div>
              </Card>

              {/* Daily Passages */}
              <Card>
                <CardTitle>聖書 · Today's Passages</CardTitle>
                {passages.map((p, i) => (
                  <div key={i} style={{ padding: '10px 0', borderBottom: i < 2 ? `1px solid ${GOLD_A(0.06)}` : 'none' }}>
                    <div style={{ fontFamily: MONO, fontSize: 9, color: GOLD, letterSpacing: '0.12em', marginBottom: 8, textAlign: 'center' }}>{p.ref}</div>
                    <div style={{ fontSize: 13, color: CREAM, lineHeight: 1.85, fontStyle: 'italic', textAlign: 'center', padding: '0 20px' }}>
                      {p.text}
                    </div>
                  </div>
                ))}
              </Card>

              {/* Daily Reading — longer devotional passage, batched + randomized */}
              <Card glow={readToday}>
                <CardTitle right={readingStreak > 0 ? <span style={{ fontFamily: MONO, fontSize: 9, color: GREEN, letterSpacing: '0.1em' }}>{readingStreak} day streak</span> : null}>聖書通読 · Daily Reading</CardTitle>

                {/* Theme + reference */}
                <div style={{ textAlign: 'center', marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${GOLD_A(0.08)}` }}>
                  <div style={{ fontSize: 14, color: CREAM, fontWeight: 600, letterSpacing: '0.02em', marginBottom: 5 }}>{reading.theme}</div>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: GOLD, letterSpacing: '0.14em' }}>{reading.ref}</div>
                </div>

                {/* Passage — one page at a time, scrolls if long */}
                <div style={{ maxHeight: 360, overflowY: 'auto', padding: '0 18px', marginBottom: 14 }} className="thin-scroll">
                  <div style={{ fontSize: 13.5, color: CREAM, lineHeight: 1.95, fontStyle: 'italic', textAlign: 'left' }}>
                    {readingPages[curPage]}
                  </div>
                </div>

                {/* Page navigation */}
                {pageCount > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 14 }}>
                    <button onClick={() => { playClick(); setReadingPage(p => Math.max(0, p - 1)); }} disabled={curPage === 0} style={{
                      background: 'transparent', border: `1px solid ${curPage === 0 ? GOLD_A(0.08) : GOLD_A(0.3)}`, borderRadius: 2,
                      color: curPage === 0 ? GOLD_A(0.15) : GOLD, cursor: curPage === 0 ? 'default' : 'pointer',
                      padding: '5px 14px', fontSize: 14, lineHeight: 1,
                    }}>‹</button>
                    <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.15em', color: GOLD_A(0.5) }}>PAGE {curPage + 1} / {pageCount}</span>
                    <button onClick={() => { playClick(); setReadingPage(p => Math.min(pageCount - 1, p + 1)); }} disabled={onLastPage} style={{
                      background: 'transparent', border: `1px solid ${onLastPage ? GOLD_A(0.08) : GOLD_A(0.3)}`, borderRadius: 2,
                      color: onLastPage ? GOLD_A(0.15) : GOLD, cursor: onLastPage ? 'default' : 'pointer',
                      padding: '5px 14px', fontSize: 14, lineHeight: 1,
                    }}>›</button>
                  </div>
                )}

                {/* Mark as read — only on the last page */}
                <div style={{ textAlign: 'center', minHeight: 22 }}>
                  {readToday ? (
                    <div style={{ fontFamily: MONO, fontSize: 10, color: GREEN, letterSpacing: '0.14em' }}>✓ READ TODAY</div>
                  ) : onLastPage ? (
                    <button onClick={() => { playClick(); setReadingCompleted(prev => ({ ...prev, [todayStr]: true })); }} className="gold-btn" style={{ padding: '10px 28px', fontSize: 11 }}>✓ MARK AS READ</button>
                  ) : (
                    <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED, letterSpacing: '0.12em' }}>Read on · › for page {curPage + 2}</div>
                  )}
                </div>
              </Card>

              {/* Gratitude & Thanks */}
              <Card>
                <CardTitle>感謝 · Gratitude to the Lord</CardTitle>
                <div style={{ fontSize: 12, color: CREAM, lineHeight: 1.7, padding: '4px 0 12px', textAlign: 'center', fontStyle: 'italic' }}>
                  Thank you, Lord Jesus Christ, for another day of life.<br/>
                  Thank you for my daily bread — another day full of<br/>
                  wonderful opportunities and miracles.<br/>
                  I am grateful. I am blessed. I am yours.
                </div>
                <div style={{ borderTop: `1px solid ${GOLD_A(0.07)}`, paddingTop: 12 }}>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.4), letterSpacing: '0.1em', marginBottom: 8 }}>YOUR PERSONAL THANKS TODAY</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      value={gratitudeNote}
                      onChange={e => setGratitudeNote(e.target.value)}
                      placeholder="What are you thankful for today?"
                      className="inp" style={{ flex: 1, padding: '8px 12px' }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && gratitudeNote.trim()) {
                          setFaithNotes(prev => [{ id: 'faith_' + Date.now(), type: 'gratitude', text: gratitudeNote.trim(), date: getTodayStr() }, ...prev]);
                          setGratitudeNote('');
                        }
                      }}
                    />
                    <button onClick={() => {
                      if (gratitudeNote.trim()) {
                        setFaithNotes(prev => [{ id: 'faith_' + Date.now(), type: 'gratitude', text: gratitudeNote.trim(), date: getTodayStr() }, ...prev]);
                        setGratitudeNote('');
                      }
                    }} className="gold-btn">Amen</button>
                  </div>
                </div>
              </Card>

              {/* Personal Prayer */}
              <Card>
                <CardTitle>祈り · Personal Prayer</CardTitle>
                <div style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.4), letterSpacing: '0.1em', marginBottom: 8 }}>SPEAK TO GOD — WHAT'S ON YOUR HEART TODAY</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <textarea
                    value={prayerNote}
                    onChange={e => setPrayerNote(e.target.value)}
                    placeholder="Lord, today I ask for..."
                    className="inp" style={{ flex: 1, padding: '10px 12px', minHeight: 80, resize: 'vertical', lineHeight: 1.6 }}
                  />
                </div>
                <button onClick={() => {
                  if (prayerNote.trim()) {
                    playClick();
                    setFaithNotes(prev => [{ id: 'faith_' + Date.now(), type: 'prayer', text: prayerNote.trim(), date: getTodayStr() }, ...prev]);
                    setPrayerNote('');
                  }
                }} className="gold-btn" style={{ marginTop: 8 }}>Save Prayer</button>
              </Card>

              {/* Prayer & Gratitude History */}
              {faithNotes.length > 0 && (
                <Card>
                  <CardTitle>記録 · Prayer Journal</CardTitle>
                  <div style={{ maxHeight: 300, overflowY: 'auto' }} className="thin-scroll">
                    {[...faithNotes].sort((a, b) => (b.date || '').localeCompare(a.date || '') || (b.id > a.id ? 1 : -1)).map(n => (
                      <div key={n.id} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: `1px solid ${GOLD_A(0.05)}` }}>
                        <div style={{ flexShrink: 0, marginTop: 2 }}>
                          <span style={{ fontSize: 12 }}>{n.type === 'prayer' ? '🙏' : '💛'}</span>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11.5, color: CREAM, lineHeight: 1.5 }}>{n.text}</div>
                          <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.3), marginTop: 3 }}>{n.date} · {n.type}</div>
                        </div>
                        <button onClick={() => setFaithNotes(prev => prev.filter(x => x.id !== n.id))} className="icon-btn" style={{ color: RED, fontSize: 10 }}>✕</button>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
            );
          })()}

          {/* ═══ MONEY — 4 numbers, tracked simply ═══ */}
          {tab === 'money' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Headline: net position */}
              <Card glow>
                <CardTitle>財務 · Net Position <span style={{ color: GOLD_A(0.25) }}>/ live</span></CardTitle>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
                  {privacyMode
                    ? <span style={{ fontSize: 52, fontWeight: 800, color: GOLD, lineHeight: 1 }}>$•••••</span>
                    : <CountUp value={fin.assets} prefix="$" style={{ fontSize: 52, fontWeight: 800, color: GOLD, letterSpacing: '-0.04em', lineHeight: 1 }} />}
                  <span style={{ fontSize: 11, color: MUTED }}>USD · assets</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    fontFamily: MONO, fontSize: 11, padding: '3px 8px', borderRadius: 2, letterSpacing: '0.05em',
                    background: monthlyNet >= 0 ? GREEN_A(0.08) : RED_A(0.08),
                    border: `1px solid ${monthlyNet >= 0 ? GREEN_A(0.2) : RED_A(0.2)}`,
                    color: monthlyNet >= 0 ? GREEN : RED,
                  }}>
                    {monthlyNet >= 0 ? '▲' : '▼'} {privacyMode ? '$•••' : `$${Math.abs(monthlyNet).toLocaleString()}`}/mo {monthlyNet >= 0 ? 'surplus' : 'burn'}
                  </div>
                </div>
              </Card>

              {/* The 4 editable numbers */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {[
                  { key: 'assets',        kanji: '資産', label: 'Assets',        hint: 'Everything you own', color: GOLD,   accent: GOLD_A },
                  { key: 'capital',       kanji: '資本', label: 'Capital',       hint: 'Liquid cash on hand', color: ICE,    accent: (a) => `rgba(230,230,232,${a})` },
                  { key: 'monthlyIncome', kanji: '収入', label: 'Monthly Income', hint: 'Money in per month',  color: GREEN,  accent: GREEN_A },
                  { key: 'monthlySpend',  kanji: '支出', label: 'Monthly Spend',  hint: 'Money out per month', color: RED,    accent: RED_A },
                ].map(f => (
                  <Card key={f.key} style={{ borderColor: f.accent(0.14) }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <span style={{ fontSize: 15, color: f.color, fontWeight: 300 }}>{f.kanji}</span>
                      <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: f.color }}>{f.label}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, marginBottom: 12 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: f.accent(0.5) }}>$</span>
                      <span style={{ fontSize: 30, fontWeight: 800, color: f.color, letterSpacing: '-0.03em', lineHeight: 1 }}>
                        {privacyMode ? '•••••' : fin[f.key].toLocaleString()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontFamily: MONO, fontSize: 13, color: f.accent(0.4) }}>$</span>
                      <input
                        type="number"
                        value={fin[f.key] || ''}
                        onChange={e => setFinField(f.key, e.target.value)}
                        placeholder="0"
                        style={{
                          flex: 1, background: f.accent(0.05), border: `1px solid ${f.accent(0.14)}`,
                          color: CREAM, padding: '8px 10px', borderRadius: 2, fontFamily: MONO,
                          fontSize: 14, fontWeight: 700, outline: 'none',
                        }}
                      />
                    </div>
                    <div style={{ fontFamily: MONO, fontSize: 8.5, color: MUTED, marginTop: 8, letterSpacing: '0.05em' }}>{f.hint}</div>
                  </Card>
                ))}
              </div>

              {/* Quick read */}
              <Card>
                <CardTitle>要約 · At a Glance</CardTitle>
                {(() => {
                  const runway = fin.monthlySpend > 0 ? Math.round(fin.capital / fin.monthlySpend * 10) / 10 : null;
                  const savingsRate = fin.monthlyIncome > 0 ? Math.round(monthlyNet / fin.monthlyIncome * 100) : 0;
                  const rows = [
                    { l: 'Monthly net', v: privacyMode ? '$•••' : `${monthlyNet >= 0 ? '+' : '−'}$${Math.abs(monthlyNet).toLocaleString()}`, c: monthlyNet >= 0 ? GREEN : RED },
                    { l: 'Savings rate', v: fin.monthlyIncome > 0 ? `${savingsRate}%` : '—', c: savingsRate >= 20 ? GREEN : savingsRate > 0 ? ORANGE : MUTED },
                    { l: 'Runway on capital', v: runway !== null ? `${runway} mo` : '—', c: runway === null ? MUTED : runway >= 6 ? GREEN : runway >= 3 ? ORANGE : RED },
                  ];
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {rows.map((r, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < rows.length - 1 ? `1px solid ${GOLD_A(0.06)}` : 'none' }}>
                          <span style={{ fontFamily: MONO, fontSize: 10, color: MUTED, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{r.l}</span>
                          <span style={{ fontSize: 15, fontWeight: 800, color: r.c }}>{r.v}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <div style={{ fontFamily: MONO, fontSize: 8.5, color: GOLD_A(0.35), marginTop: 12, letterSpacing: '0.05em', textAlign: 'center' }}>
                  Runway = how many months your capital lasts at current spend.
                </div>
              </Card>
            </div>
          )}

          {/* ═══ GOALS — 2 Year Vision ═══ */}
          {tab === 'goals' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Overall progress banner */}
              <Card glow>
                <CardTitle>目標 · 2 Year Vision <span style={{ color: GOLD_A(0.25) }}>/ Feb 2028</span></CardTitle>
                {(() => {
                  const allGoals = goalCategories.flatMap(c => c.goals);
                  const done = allGoals.filter(g => g.done).length;
                  const total = allGoals.length;
                  const pct = total > 0 ? Math.round(done / total * 100) : 0;
                  const northStars = allGoals.filter(g => g.priority === 'north-star');
                  const northDone = northStars.filter(g => g.done).length;
                  return (
                    <>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 42, fontWeight: 800, color: GOLD, lineHeight: 1 }}>{pct}%</span>
                        <span style={{ fontSize: 13, color: MUTED }}>{done}/{total} goals completed</span>
                      </div>
                      <div style={{ height: 6, background: GOLD_A(0.08), borderRadius: 3, marginBottom: 10 }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: pct >= 75 ? GREEN : pct >= 30 ? GOLD : GOLD_A(0.3), borderRadius: 3, transition: 'width 0.5s' }} />
                      </div>
                      {northStars.length > 0 && (
                        <div style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.5) }}>
                          North star goals: {northDone}/{northStars.length} achieved
                        </div>
                      )}
                    </>
                  );
                })()}
              </Card>

              {/* Statement card */}
              {goalCategories[0]?.statement && (
                <Card>
                  <CardTitle>宣言 · Statement</CardTitle>
                  <div style={{ fontSize: 14, color: CREAM, lineHeight: 1.7, fontStyle: 'italic', textAlign: 'center', padding: '8px 0' }}>
                    {goalCategories[0].statement.split('. ').map((s, i) => (
                      <div key={i} style={{ marginBottom: 4, color: s.includes('NEVER') || s.includes('MILLIONAIRE') ? GOLD : CREAM }}>{s}{s.endsWith('.') ? '' : '.'}</div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Goal categories */}
              {goalCategories.map(cat => {
                const catDone = cat.goals.filter(g => g.done).length;
                const catTotal = cat.goals.length;
                const catPct = catTotal > 0 ? Math.round(catDone / catTotal * 100) : 0;
                const priorityOrder = { 'north-star': 0, high: 1, medium: 2, low: 3 };

                const updateCat = (field, val) => setGoalCategories(prev => prev.map(c => c.id === cat.id ? { ...c, [field]: val } : c));
                const isEditingCat = editingCatId === cat.id;

                return (
                  <Card key={cat.id} glow={catPct === 100}>
                    {isEditingCat ? (
                      /* ── Edit category header ── */
                      <div style={{ background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.12)}`, borderRadius: 2, padding: 12, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input value={cat.kanji || ''} onChange={e => updateCat('kanji', e.target.value)} placeholder="漢" maxLength={2} className="inp" style={{ width: 46, textAlign: 'center', padding: '6px 4px', fontSize: 15 }} />
                          <input value={cat.name} onChange={e => updateCat('name', e.target.value)} placeholder="Category name" className="inp" style={{ flex: 1, padding: '6px 10px' }} />
                        </div>
                        <input value={cat.deadline || ''} onChange={e => updateCat('deadline', e.target.value)} placeholder="Deadline (e.g. Dec 2026)" className="inp" style={{ padding: '6px 10px', fontSize: 12 }} />
                        <input value={cat.statement || ''} onChange={e => updateCat('statement', e.target.value)} placeholder="Statement / mantra (optional)" className="inp" style={{ padding: '6px 10px', fontSize: 12 }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontFamily: MONO, fontSize: 8, color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Colour</span>
                          {GOAL_COLORS.map(col => (
                            <div key={col} onClick={() => updateCat('color', col)} style={{
                              width: 20, height: 20, borderRadius: '50%', cursor: 'pointer', background: col,
                              border: `2px solid ${(cat.color || GOLD) === col ? CREAM : 'transparent'}`, transition: 'all 0.15s',
                            }} />
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                          <button onClick={() => setEditingCatId(null)} className="gold-btn" style={{ padding: '6px 14px' }}>Done</button>
                          <button onClick={() => { setGoalCategories(prev => prev.filter(c => c.id !== cat.id)); setEditingCatId(null); }} className="ghost-btn" style={{ padding: '6px 12px', color: RED, borderColor: RED_A(0.3) }}>Delete category</button>
                        </div>
                      </div>
                    ) : (
                    <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 18, color: cat.color || GOLD, fontWeight: 300 }}>{cat.kanji || '目'}</span>
                          <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: cat.color || GOLD }}>{cat.name}</span>
                        </div>
                        {cat.deadline && <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED }}>Deadline: {cat.deadline}</div>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontFamily: MONO, fontSize: 10, color: catPct === 100 ? GREEN : GOLD }}>{catDone}/{catTotal}</span>
                        <button onClick={() => setEditingCatId(cat.id)} className="icon-btn" style={{ color: GOLD_A(0.5) }}>✎</button>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div style={{ height: 3, background: GOLD_A(0.08), borderRadius: 2, marginBottom: 14 }}>
                      <div style={{ height: '100%', width: `${catPct}%`, background: cat.color || GOLD, borderRadius: 2, transition: 'width 0.5s' }} />
                    </div>

                    {/* Statement for this category */}
                    {cat.statement && (
                      <div style={{ fontSize: 11, color: GOLD_A(0.5), fontStyle: 'italic', marginBottom: 12, padding: '6px 10px', borderLeft: `2px solid ${GOLD_A(0.15)}` }}>
                        {cat.statement}
                      </div>
                    )}
                    </>
                    )}

                    {/* Goals sorted by priority */}
                    {[...cat.goals].sort((a, b) => (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3)).map(goal => {
                      const isEditing = editingGoalId === goal.id;

                      return (
                      <div key={goal.id} style={{ padding: '9px 0', borderBottom: `1px solid ${GOLD_A(0.05)}` }}>
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <input value={editingGoalText} onChange={e => setEditingGoalText(e.target.value)}
                              autoFocus
                              onKeyDown={e => {
                                if (e.key === 'Enter' && editingGoalText.trim()) {
                                  setGoalCategories(prev => prev.map(c => c.id === cat.id ? { ...c, goals: c.goals.map(g => g.id === goal.id ? { ...g, text: editingGoalText.trim() } : g) } : c));
                                  setEditingGoalId(null);
                                }
                                if (e.key === 'Escape') setEditingGoalId(null);
                              }}
                              className="inp" style={{ flex: 1, padding: '6px 10px', fontSize: 12 }}
                            />
                            <select value={goal.priority} onChange={e => setGoalCategories(prev => prev.map(c => c.id === cat.id ? { ...c, goals: c.goals.map(g => g.id === goal.id ? { ...g, priority: e.target.value } : g) } : c))}
                              className="inp" style={{ width: 90, padding: '6px 6px', fontSize: 9 }}>
                              <option value="north-star">North Star</option>
                              <option value="high">High</option>
                              <option value="medium">Medium</option>
                              <option value="low">Low</option>
                            </select>
                            <button onClick={() => {
                              if (editingGoalText.trim()) {
                                setGoalCategories(prev => prev.map(c => c.id === cat.id ? { ...c, goals: c.goals.map(g => g.id === goal.id ? { ...g, text: editingGoalText.trim() } : g) } : c));
                              }
                              setEditingGoalId(null);
                            }} className="gold-btn" style={{ padding: '5px 10px', fontSize: 9 }}>SAVE</button>
                            <button onClick={() => setEditingGoalId(null)} className="ghost-btn" style={{ padding: '5px 8px', fontSize: 9 }}>✕</button>
                          </div>
                        ) : (
                        <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div onClick={() => setGoalCategories(prev => prev.map(c => c.id === cat.id ? { ...c, goals: c.goals.map(g => g.id === goal.id ? { ...g, done: !g.done } : g) } : c))} style={{
                          width: 22, height: 22, borderRadius: 2, cursor: 'pointer', flexShrink: 0,
                          background: goal.done ? GREEN_A(0.15) : GOLD_A(0.04),
                          border: `1px solid ${goal.done ? GREEN_A(0.4) : GOLD_A(0.15)}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, color: goal.done ? GREEN : 'transparent',
                        }}>✓</div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: 12.5, color: goal.done ? MUTED : CREAM, textDecoration: goal.done ? 'line-through' : 'none', lineHeight: 1.4 }}>{goal.text}</span>
                        </div>
                        <span style={{
                          fontFamily: MONO, fontSize: 7, padding: '2px 6px', borderRadius: 2, letterSpacing: '0.1em', textTransform: 'uppercase',
                          background: goal.priority === 'north-star' ? GOLD_A(0.12) : goal.priority === 'high' ? ORANGE_A(0.08) : GOLD_A(0.04),
                          border: `1px solid ${goal.priority === 'north-star' ? GOLD_A(0.3) : goal.priority === 'high' ? ORANGE_A(0.2) : GOLD_A(0.1)}`,
                          color: goal.priority === 'north-star' ? GOLD : goal.priority === 'high' ? ORANGE : MUTED,
                        }}>
                          {goal.priority === 'north-star' ? '★ NORTH STAR' : goal.priority}
                        </span>
                        <button onClick={() => { setEditingGoalId(goal.id); setEditingGoalText(goal.text); }} className="icon-btn" style={{ fontSize: 10, color: GOLD_A(0.4) }}>✎</button>
                        <button onClick={() => setGoalCategories(prev => prev.map(c => c.id === cat.id ? { ...c, goals: c.goals.filter(g => g.id !== goal.id) } : c))} className="icon-btn" style={{ color: RED, fontSize: 10 }}>✕</button>
                        </div>
                        </>
                        )}
                      </div>
                      );
                    })}

                    {/* Add goal to this category */}
                    {showAddGoal === cat.id ? (
                      <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
                        <input placeholder="New goal..." value={newGoalText} onChange={e => setNewGoalText(e.target.value)} className="inp" style={{ flex: 1, padding: '6px 10px' }}
                          onKeyDown={e => { if (e.key === 'Enter' && newGoalText.trim()) { setGoalCategories(prev => prev.map(c => c.id === cat.id ? { ...c, goals: [...c.goals, { id: 'goal_' + Date.now(), text: newGoalText.trim(), done: false, priority: newGoalPriority }] } : c)); setNewGoalText(''); } }} />
                        <select value={newGoalPriority} onChange={e => setNewGoalPriority(e.target.value)} className="inp" style={{ width: 100, padding: '6px 6px', fontSize: 10 }}>
                          <option value="north-star">★ North Star</option>
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low</option>
                        </select>
                        <button onClick={() => { if (newGoalText.trim()) { setGoalCategories(prev => prev.map(c => c.id === cat.id ? { ...c, goals: [...c.goals, { id: 'goal_' + Date.now(), text: newGoalText.trim(), done: false, priority: newGoalPriority }] } : c)); setNewGoalText(''); } }} className="gold-btn" style={{ padding: '6px 12px' }}>Add</button>
                        <button onClick={() => { setShowAddGoal(null); setNewGoalText(''); }} className="ghost-btn" style={{ padding: '6px 10px', fontSize: 9 }}>Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => setShowAddGoal(cat.id)} className="ghost-btn" style={{ marginTop: 10, fontSize: 9 }}>+ Add Goal</button>
                    )}
                  </Card>
                );
              })}

              {/* Add new category */}
              {showAddCategory ? (
                <Card>
                  <CardTitle>New Goal Category</CardTitle>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input placeholder="Category name (e.g. Health & Body)" value={newCategory.name} onChange={e => setNewCategory(p => ({ ...p, name: e.target.value }))} className="inp" />
                    <input placeholder="Deadline (e.g. Dec 2026)" value={newCategory.deadline} onChange={e => setNewCategory(p => ({ ...p, deadline: e.target.value }))} className="inp" />
                    <input placeholder="Statement / mantra (optional)" value={newCategory.statement} onChange={e => setNewCategory(p => ({ ...p, statement: e.target.value }))} className="inp" />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => { if (newCategory.name.trim()) { setGoalCategories(prev => [...prev, { id: 'gcat_' + Date.now(), name: newCategory.name.trim(), kanji: '目', color: GOLD, deadline: newCategory.deadline, statement: newCategory.statement, goals: [] }]); setNewCategory({ name: '', deadline: '', statement: '' }); setShowAddCategory(false); } }} className="gold-btn">Create Category</button>
                      <button onClick={() => setShowAddCategory(false)} className="ghost-btn">Cancel</button>
                    </div>
                  </div>
                </Card>
              ) : (
                <button onClick={() => setShowAddCategory(true)} className="ghost-btn" style={{ alignSelf: 'flex-start' }}>+ Add Goal Category</button>
              )}
            </div>
          )}

          {/* ═══ BUILDING ═══ */}
          {tab === 'building' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Projects */}
              <Card>
                <CardTitle right={<button onClick={() => setShowAddItem(a => !a)} className="ghost-btn">{showAddItem ? 'Cancel' : '+ Add'}</button>}>種 · Projects · Seeds · Ideas</CardTitle>
                {showAddItem && (
                  <div style={{ background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.1)}`, borderRadius: 2, padding: 12, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {Object.entries(ITEM_TYPES).map(([k, v]) => (
                        <button key={k} onClick={() => setNewItem(p => ({ ...p, type: k }))} className="ghost-btn" style={{ borderColor: newItem.type === k ? v.color : undefined, color: newItem.type === k ? v.color : undefined }}>{v.label}</button>
                      ))}
                    </div>
                    <input placeholder="Name / title" value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} className="inp" />
                    <input placeholder="Note or description" value={newItem.note} onChange={e => setNewItem(p => ({ ...p, note: e.target.value }))} className="inp" />
                    <button onClick={addItem} className="gold-btn">Save</button>
                  </div>
                )}
                {['project', 'seed', 'idea'].map(type => {
                  const group = items.filter(i => i.type === type);
                  if (!group.length) return null;
                  const tc = ITEM_TYPES[type];

                  const moveItem = (fromIdx, toIdx) => {
                    const typeArr = items.filter(i => i.type === type);
                    const otherArr = items.filter(i => i.type !== type);
                    const moved = [...typeArr];
                    const [el] = moved.splice(fromIdx, 1);
                    moved.splice(toIdx, 0, el);
                    setItems([...otherArr, ...moved]);
                  };

                  return (
                    <div key={type} style={{ marginBottom: 14 }}>
                      <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.2em', color: tc.color, textTransform: 'uppercase', marginBottom: 6, opacity: 0.7 }}>{tc.label}S</div>
                      {group.map((item, idx) => (
                        <div key={item.id}
                          draggable
                          onDragStart={() => setDragItem({ type, idx })}
                          onDragOver={e => e.preventDefault()}
                          onDrop={() => { if (dragItem && dragItem.type === type && dragItem.idx !== idx) moveItem(dragItem.idx, idx); setDragItem(null); }}
                          onDragEnd={() => setDragItem(null)}
                          style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0',
                            borderBottom: `1px solid ${GOLD_A(0.06)}`, cursor: 'grab',
                            opacity: dragItem?.type === type && dragItem?.idx === idx ? 0.4 : 1, transition: 'opacity 0.15s',
                          }}>
                          <div style={{ color: GOLD_A(0.25), fontSize: 11, cursor: 'grab', paddingTop: 4, flexShrink: 0, userSelect: 'none' }}>⋮⋮</div>
                          <div style={{ width: 3, minHeight: 36, borderRadius: 2, background: tc.color, opacity: 0.6, flexShrink: 0, marginTop: 3 }} />
                          {editingItem === item.id ? (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <input value={item.name} onChange={e => setItems(prev => prev.map(i => i.id === item.id ? { ...i, name: e.target.value } : i))} className="inp" style={{ padding: '4px 8px', fontSize: 12 }} onKeyDown={e => { if (e.key === 'Enter') setEditingItem(null); }} autoFocus />
                              <input value={item.note} onChange={e => setItems(prev => prev.map(i => i.id === item.id ? { ...i, note: e.target.value } : i))} className="inp" style={{ padding: '4px 8px', fontSize: 11 }} placeholder="Note" />
                              <button onClick={() => setEditingItem(null)} className="ghost-btn" style={{ alignSelf: 'flex-start', padding: '3px 10px', fontSize: 8 }}>Done</button>
                            </div>
                          ) : (
                            <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setEditingItem(item.id)}>
                              <div style={{ fontSize: 13, color: CREAM, fontWeight: 500, marginBottom: 2 }}>{item.name}</div>
                              {item.note && <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.5 }}>{item.note}</div>}
                              <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.3), marginTop: 4, letterSpacing: '0.08em' }}>{item.created}</div>
                            </div>
                          )}
                          <select value={item.status} onChange={e => updateItemStatus(item.id, e.target.value)} className="inp" style={{ width: 80, padding: '3px 6px', fontSize: 10 }}>
                            {Object.entries(ITEM_STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                          <button onClick={() => removeItem(item.id)} className="icon-btn" style={{ color: RED }}>✕</button>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </Card>

              {/* Business Tasks & Personal Tasks */}
              {['business', 'personal'].map(taskType => {
                const typeTasks = tasks.filter(t => t.type === taskType);
                const typeLabel = taskType === 'business' ? '仕事 · Business Tasks' : '私事 · Personal Tasks';
                const typeColor = taskType === 'business' ? GOLD : BLUE;

                const moveTask = (fromIdx, toIdx) => {
                  const typeArr = tasks.filter(t => t.type === taskType);
                  const otherArr = tasks.filter(t => t.type !== taskType);
                  const moved = [...typeArr];
                  const [item] = moved.splice(fromIdx, 1);
                  moved.splice(toIdx, 0, item);
                  setTasks([...otherArr, ...moved]);
                };

                return (
                  <Card key={taskType}>
                    <CardTitle right={<button onClick={() => { setShowAddTask(showAddTask === taskType ? null : taskType); setNewTask({ name: '', note: '' }); }} className="ghost-btn">{showAddTask === taskType ? 'Cancel' : '+ Add'}</button>}>{typeLabel}</CardTitle>

                    {showAddTask === taskType && (
                      <div style={{ background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.1)}`, borderRadius: 2, padding: 12, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <input placeholder="Task name" value={newTask.name} onChange={e => setNewTask(p => ({ ...p, name: e.target.value }))} className="inp"
                          onKeyDown={e => { if (e.key === 'Enter' && newTask.name.trim()) { setTasks(prev => [...prev, { id: 'task_' + Date.now(), type: taskType, name: newTask.name.trim(), note: newTask.note.trim(), done: false, created: getTodayStr() }]); setNewTask({ name: '', note: '' }); }}} />
                        <input placeholder="Note (optional)" value={newTask.note} onChange={e => setNewTask(p => ({ ...p, note: e.target.value }))} className="inp" />
                        <button onClick={() => { if (newTask.name.trim()) { setTasks(prev => [...prev, { id: 'task_' + Date.now(), type: taskType, name: newTask.name.trim(), note: newTask.note.trim(), done: false, created: getTodayStr() }]); setNewTask({ name: '', note: '' }); setShowAddTask(null); }}} className="gold-btn">Add Task</button>
                      </div>
                    )}

                    {typeTasks.length === 0 && showAddTask !== taskType && <div style={{ fontSize: 11, color: MUTED, fontStyle: 'italic' }}>No {taskType} tasks. Add one to start tracking.</div>}

                    {typeTasks.map((task, idx) => (
                      <div
                        key={task.id}
                        draggable
                        onDragStart={() => setDragTask(idx)}
                        onDragOver={e => e.preventDefault()}
                        onDrop={() => { if (dragTask !== null && dragTask !== idx) moveTask(dragTask, idx); setDragTask(null); }}
                        onDragEnd={() => setDragTask(null)}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 0',
                          borderBottom: `1px solid ${GOLD_A(0.06)}`, cursor: 'grab',
                          opacity: dragTask === idx ? 0.4 : 1, transition: 'opacity 0.15s',
                        }}>
                        {/* Drag handle */}
                        <div style={{ color: GOLD_A(0.25), fontSize: 11, cursor: 'grab', paddingTop: 2, flexShrink: 0, userSelect: 'none' }}>⋮⋮</div>

                        {/* Checkbox */}
                        <div onClick={() => { playClick(); setTasks(prev => prev.map(t => t.id === task.id ? { ...t, done: !t.done } : t)); }} style={{
                          width: 20, height: 20, borderRadius: 2, cursor: 'pointer', flexShrink: 0, marginTop: 1,
                          background: task.done ? GREEN_A(0.15) : GOLD_A(0.04),
                          border: `1px solid ${task.done ? GREEN_A(0.4) : GOLD_A(0.15)}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, color: task.done ? GREEN : 'transparent',
                        }}>✓</div>

                        {/* Content — editable */}
                        {editingTask === task.id ? (
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <input value={task.name} onChange={e => setTasks(prev => prev.map(t => t.id === task.id ? { ...t, name: e.target.value } : t))} className="inp" style={{ padding: '4px 8px', fontSize: 12 }}
                              onKeyDown={e => { if (e.key === 'Enter') setEditingTask(null); }} autoFocus />
                            <input value={task.note} onChange={e => setTasks(prev => prev.map(t => t.id === task.id ? { ...t, note: e.target.value } : t))} className="inp" style={{ padding: '4px 8px', fontSize: 11 }} placeholder="Note" />
                            <button onClick={() => setEditingTask(null)} className="ghost-btn" style={{ alignSelf: 'flex-start', padding: '3px 10px', fontSize: 8 }}>Done</button>
                          </div>
                        ) : (
                          <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setEditingTask(task.id)}>
                            <div style={{ fontSize: 12.5, color: task.done ? MUTED : CREAM, textDecoration: task.done ? 'line-through' : 'none', lineHeight: 1.4 }}>{task.name}</div>
                            {task.note && <div style={{ fontSize: 10, color: MUTED, marginTop: 2 }}>{task.note}</div>}
                          </div>
                        )}

                        {/* Priority indicator */}
                        <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.3), paddingTop: 3, flexShrink: 0 }}>#{idx + 1}</div>

                        {/* Delete */}
                        <button onClick={() => setTasks(prev => prev.filter(t => t.id !== task.id))} className="icon-btn" style={{ color: RED, fontSize: 10 }}>✕</button>
                      </div>
                    ))}
                  </Card>
                );
              })}

              {/* Books — inline editable */}
              <Card>
                <CardTitle right={<button onClick={() => setShowAddBook(a => !a)} className="ghost-btn">{showAddBook ? 'Cancel' : '+ Add'}</button>}>本 · Reading Stack</CardTitle>
                {showAddBook && (
                  <div style={{ background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.1)}`, borderRadius: 2, padding: 12, marginBottom: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <input placeholder="Title" value={newBook.title} onChange={e => setNewBook(p => ({ ...p, title: e.target.value }))} className="inp" />
                    <input placeholder="Author" value={newBook.author} onChange={e => setNewBook(p => ({ ...p, author: e.target.value }))} className="inp" />
                    <input type="number" placeholder="Current page" value={newBook.current_page || ''} onChange={e => setNewBook(p => ({ ...p, current_page: Number(e.target.value) }))} className="inp" />
                    <input type="number" placeholder="Total pages" value={newBook.total_pages || ''} onChange={e => setNewBook(p => ({ ...p, total_pages: Number(e.target.value) }))} className="inp" />
                    <select value={newBook.status} onChange={e => setNewBook(p => ({ ...p, status: e.target.value }))} className="inp">
                      <option value="reading">Reading</option><option value="queued">Queued</option><option value="done">Done</option>
                    </select>
                    <button onClick={() => { if (newBook.title) { setBooks(prev => [...prev, { ...newBook, id: 'book_' + Date.now() }]); setShowAddBook(false); setNewBook({ title: '', author: '', status: 'reading', current_page: 0, total_pages: 0, cover_color: GOLD }); } }} className="gold-btn">Add Book</button>
                  </div>
                )}
                {books.map((b, bIdx) => {
                  const pct = b.total_pages ? Math.round(b.current_page / b.total_pages * 100) : 0;
                  const isEditing = editingBook === b.id;

                  const moveBook = (fromIdx, toIdx) => {
                    const moved = [...books];
                    const [el] = moved.splice(fromIdx, 1);
                    moved.splice(toIdx, 0, el);
                    setBooks(moved);
                  };

                  return (
                    <div key={b.id}
                      draggable
                      onDragStart={() => setDragBook(bIdx)}
                      onDragOver={e => e.preventDefault()}
                      onDrop={() => { if (dragBook !== null && dragBook !== bIdx) moveBook(dragBook, bIdx); setDragBook(null); }}
                      onDragEnd={() => setDragBook(null)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0',
                        borderBottom: `1px solid ${GOLD_A(0.06)}`, cursor: 'grab',
                        opacity: dragBook === bIdx ? 0.4 : 1, transition: 'opacity 0.15s',
                      }}>
                      <div style={{ color: GOLD_A(0.25), fontSize: 11, cursor: 'grab', flexShrink: 0, userSelect: 'none' }}>⋮⋮</div>
                      <div style={{ width: 4, height: 44, borderRadius: 2, background: b.cover_color, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <input value={b.title} onChange={e => setBooks(prev => prev.map(x => x.id === b.id ? { ...x, title: e.target.value } : x))} className="inp" style={{ fontSize: 11, padding: '3px 6px', flex: 1 }} />
                            <input value={b.author} onChange={e => setBooks(prev => prev.map(x => x.id === b.id ? { ...x, author: e.target.value } : x))} className="inp" style={{ fontSize: 11, padding: '3px 6px', width: 120 }} />
                            <input type="number" value={b.current_page} onChange={e => setBooks(prev => prev.map(x => x.id === b.id ? { ...x, current_page: Number(e.target.value) } : x))} className="inp" style={{ fontSize: 11, padding: '3px 6px', width: 60 }} placeholder="pg" />
                            <input type="number" value={b.total_pages} onChange={e => setBooks(prev => prev.map(x => x.id === b.id ? { ...x, total_pages: Number(e.target.value) } : x))} className="inp" style={{ fontSize: 11, padding: '3px 6px', width: 60 }} placeholder="total" />
                            <select value={b.status} onChange={e => setBooks(prev => prev.map(x => x.id === b.id ? { ...x, status: e.target.value } : x))} className="inp" style={{ fontSize: 10, padding: '3px 6px', width: 80 }}>
                              <option value="reading">Reading</option><option value="queued">Queued</option><option value="done">Done</option>
                            </select>
                            <button onClick={() => setEditingBook(null)} className="mini-btn">✓</button>
                          </div>
                        ) : (
                          <>
                            <div style={{ fontSize: 12, fontWeight: 600, color: CREAM, marginBottom: 1 }}>{b.title}</div>
                            <div style={{ fontSize: 10, color: MUTED, marginBottom: b.status === 'reading' ? 5 : 0 }}>{b.author}</div>
                            {b.status === 'reading' && <div style={{ height: 2, background: GOLD_A(0.1), borderRadius: 1 }}><div style={{ height: '100%', width: `${pct}%`, background: b.cover_color, borderRadius: 1 }} /></div>}
                          </>
                        )}
                      </div>
                      <div style={{ fontFamily: MONO, fontSize: 8, padding: '2px 7px', border: `1px solid ${b.status === 'reading' ? GOLD_A(0.3) : b.status === 'done' ? GREEN_A(0.3) : GOLD_A(0.1)}`, borderRadius: 2, color: b.status === 'reading' ? GOLD : b.status === 'done' ? GREEN : MUTED, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        {b.status === 'reading' ? `${pct}%` : b.status}
                      </div>
                      <button onClick={() => setEditingBook(isEditing ? null : b.id)} className="icon-btn">✎</button>
                      <button onClick={() => setBooks(prev => prev.filter(x => x.id !== b.id))} className="icon-btn" style={{ color: RED }}>✕</button>
                    </div>
                  );
                })}
              </Card>
            </div>
          )}

          {/* ═══ JOURNAL + LEARNING + MOVIES ═══ */}
          {tab === 'journal' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Sub-tab switcher */}
              <div style={{ display: 'flex', gap: 6 }}>
                {[
                  { id: 'journal', label: '記 Journal', icon: '📝' },
                  { id: 'learning', label: '学 Learning', icon: '🧠' },
                  { id: 'movies', label: '映 Watchlist', icon: '🎬' },
                ].map(st => (
                  <button key={st.id} onClick={() => setJournalTab(st.id)} style={{
                    flex: 1, padding: '10px 14px', borderRadius: 2, cursor: 'pointer', fontFamily: MONO, fontSize: 10,
                    letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'all 0.15s',
                    background: journalTab === st.id ? GOLD_A(0.1) : GOLD_A(0.03),
                    border: `1px solid ${journalTab === st.id ? GOLD_A(0.3) : GOLD_A(0.1)}`,
                    color: journalTab === st.id ? GOLD : MUTED,
                  }}>{st.icon} {st.label}</button>
                ))}
              </div>

              {/* ── JOURNAL SUB-TAB ── */}
              {journalTab === 'journal' && (
                <>
                  {/* Write / History toggle */}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setJournalView('write')} className="ghost-btn" style={{ flex: 1, borderColor: journalView === 'write' ? GOLD_A(0.4) : undefined, color: journalView === 'write' ? GOLD : undefined }}>Write</button>
                    <button onClick={() => setJournalView('history')} className="ghost-btn" style={{ flex: 1, borderColor: journalView === 'history' ? GOLD_A(0.4) : undefined, color: journalView === 'history' ? GOLD : undefined }}>History ({journalEntries.length})</button>
                  </div>

                  {journalView === 'write' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {/* ── 朝 MORNING ── */}
                      <Card glow>
                        <CardTitle>朝 · Morning {todayJournal.length > 0 && <span style={{ color: GREEN, fontSize: 8 }}>({todayJournal.length} logged)</span>}</CardTitle>

                        {/* Morning intention */}
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.5), letterSpacing: '0.15em', marginBottom: 6, textTransform: 'uppercase' }}>朝 · Morning intention</div>
                          <input value={journalDraft.intention} onChange={e => setJournalDraft(p => ({ ...p, intention: e.target.value }))}
                            placeholder="What is your one focus today?" className="inp" />
                        </div>

                        {/* Gratitudes */}
                        <div>
                          <div style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.5), letterSpacing: '0.15em', marginBottom: 6, textTransform: 'uppercase' }}>感謝 · 3 Gratitudes</div>
                          {journalDraft.gratitudes.map((g, i) => (
                            <input key={i} value={g} onChange={e => { const ng = [...journalDraft.gratitudes]; ng[i] = e.target.value; setJournalDraft(p => ({ ...p, gratitudes: ng })); }}
                              placeholder={`Grateful for...`} className="inp" style={{ marginBottom: 4, padding: '6px 11px', fontSize: 11.5 }} />
                          ))}
                        </div>
                      </Card>

                      {/* ── 夜 NIGHT ── */}
                      <Card>
                        <CardTitle>夜 · Night · Before Bed</CardTitle>

                        {/* Win */}
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.5), letterSpacing: '0.15em', marginBottom: 6, textTransform: 'uppercase' }}>勝 · Today's win</div>
                          <input value={journalDraft.win} onChange={e => setJournalDraft(p => ({ ...p, win: e.target.value }))}
                            placeholder="What did you conquer today?" className="inp" />
                        </div>

                        {/* Evening reflection */}
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.5), letterSpacing: '0.15em', marginBottom: 6, textTransform: 'uppercase' }}>夜 · Evening reflection</div>
                          <textarea value={journalDraft.reflection} onChange={e => setJournalDraft(p => ({ ...p, reflection: e.target.value }))}
                            placeholder="How did the day go? What did you learn? What would you do differently?"
                            className="inp" style={{ minHeight: 70, resize: 'vertical', fontFamily: SANS }} />
                        </div>

                        {/* Day rating — logged at night before bed */}
                        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.5), letterSpacing: '0.15em', textTransform: 'uppercase' }}>Day score</span>
                          <div style={{ display: 'flex', gap: 3 }}>
                            {[1,2,3,4,5,6,7,8,9,10].map(n => (
                              <div key={n} onClick={() => setJournalDraft(p => ({ ...p, dayRating: n }))} style={{
                                width: 24, height: 24, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 10, fontFamily: MONO, cursor: 'pointer',
                                background: n <= journalDraft.dayRating ? (n >= 8 ? GREEN_A(0.2) : n >= 5 ? GOLD_A(0.15) : RED_A(0.15)) : GOLD_A(0.04),
                                border: `1px solid ${n <= journalDraft.dayRating ? (n >= 8 ? GREEN_A(0.4) : n >= 5 ? GOLD_A(0.25) : RED_A(0.25)) : GOLD_A(0.1)}`,
                                color: n <= journalDraft.dayRating ? (n >= 8 ? GREEN : n >= 5 ? GOLD : RED) : MUTED,
                              }}>{n}</div>
                            ))}
                          </div>
                          <span style={{ fontSize: 14, fontWeight: 700, color: journalDraft.dayRating >= 8 ? GREEN : journalDraft.dayRating >= 5 ? GOLD : RED }}>{journalDraft.dayRating}/10</span>
                        </div>

                        <button onClick={saveJournal} className="gold-btn" style={{ width: '100%' }}>Save Entry +20XP</button>
                      </Card>
                    </div>
                  ) : (
                    <Card>
                      <CardTitle>歴史 · Journal History</CardTitle>
                      <input value={journalSearch} onChange={e => setJournalSearch(e.target.value)} placeholder="Search entries..." className="inp" style={{ marginBottom: 12 }} />
                      {filteredJournal.length === 0 && <div style={{ fontSize: 11, color: MUTED, fontStyle: 'italic' }}>No entries yet. Start writing, Snxper.</div>}
                      {[...filteredJournal].reverse().map((entry, i) => (
                        <div key={entry.id || i} style={{ padding: '14px 0', borderBottom: `1px solid ${GOLD_A(0.06)}` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED, letterSpacing: '0.1em' }}>{entry.date} {entry.time}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.4) }}>{entry.habitsCompleted || 0} habits</span>
                              <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color: entry.dayRating >= 8 ? GREEN : entry.dayRating >= 5 ? GOLD : RED }}>{entry.dayRating}/10</span>
                            </div>
                          </div>
                          {entry.intention && <div style={{ fontSize: 11, color: GOLD_A(0.7), marginBottom: 4 }}><span style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.35), marginRight: 6 }}>INTENTION</span>{entry.intention}</div>}
                          {entry.win && <div style={{ fontSize: 11, color: GREEN, marginBottom: 4 }}><span style={{ fontFamily: MONO, fontSize: 8, color: GREEN_A(0.5), marginRight: 6 }}>WIN</span>{entry.win}</div>}
                          {entry.gratitudes?.length > 0 && <div style={{ fontSize: 11, color: GOLD_A(0.6), marginBottom: 4 }}>{entry.gratitudes.map((g, gi) => <span key={gi} style={{ marginRight: 8 }}>✦ {g}</span>)}</div>}
                          {entry.reflection && <div style={{ fontSize: 11.5, color: CREAM, lineHeight: 1.6, marginTop: 4 }}>{entry.reflection}</div>}
                        </div>
                      ))}
                    </Card>
                  )}
                </>
              )}

              {/* ── LEARNING SUB-TAB ── */}
              {journalTab === 'learning' && (
                <>
                  <Card>
                    <CardTitle right={<button onClick={() => setShowAddLearning(a => !a)} className="ghost-btn">{showAddLearning ? 'Cancel' : '+ Add'}</button>}>学習 · Learning Vault</CardTitle>

                    {showAddLearning && (
                      <div style={{ background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.1)}`, borderRadius: 2, padding: 12, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <input placeholder="What are you learning?" value={newLearning.title} onChange={e => setNewLearning(p => ({ ...p, title: e.target.value }))} className="inp" />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <select value={newLearning.category} onChange={e => setNewLearning(p => ({ ...p, category: e.target.value }))} className="inp" style={{ flex: 1 }}>
                            {LEARNING_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                          </select>
                          <select value={newLearning.type} onChange={e => setNewLearning(p => ({ ...p, type: e.target.value }))} className="inp" style={{ width: 100 }}>
                            <option value="course">Course</option><option value="book">Book</option><option value="tutorial">Tutorial</option><option value="practice">Practice</option><option value="other">Other</option>
                          </select>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input type="number" placeholder="Progress %" value={newLearning.progress || ''} onChange={e => setNewLearning(p => ({ ...p, progress: Number(e.target.value) }))} className="inp" style={{ width: 90 }} />
                          <input type="number" placeholder="Total hrs" value={newLearning.totalHours || ''} onChange={e => setNewLearning(p => ({ ...p, totalHours: Number(e.target.value) }))} className="inp" style={{ width: 90 }} />
                        </div>
                        <input placeholder="Notes" value={newLearning.notes} onChange={e => setNewLearning(p => ({ ...p, notes: e.target.value }))} className="inp" />
                        <button onClick={addLearning} className="gold-btn">Add to Vault</button>
                      </div>
                    )}

                    {learnings.length === 0 && !showAddLearning && <div style={{ fontSize: 11, color: MUTED, fontStyle: 'italic' }}>Empty vault. Start adding what you're learning.</div>}

                    {['active', 'paused', 'done'].map(status => {
                      const group = learnings.filter(l => l.status === status);
                      if (!group.length) return null;
                      return (
                        <div key={status} style={{ marginBottom: 14 }}>
                          <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.2em', color: status === 'active' ? GREEN : status === 'done' ? GOLD : MUTED, textTransform: 'uppercase', marginBottom: 6 }}>
                            {status === 'active' ? '▶ Active' : status === 'done' ? '✓ Completed' : '⏸ Paused'}
                          </div>
                          {group.map(l => (
                            <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${GOLD_A(0.06)}` }}>
                              <div style={{ width: 3, minHeight: 40, borderRadius: 2, background: status === 'active' ? GREEN : status === 'done' ? GOLD : MUTED, opacity: 0.6, flexShrink: 0 }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 12.5, fontWeight: 600, color: CREAM, marginBottom: 2 }}>{l.title}</div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: l.notes ? 4 : 0 }}>
                                  <span style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.4), padding: '1px 5px', border: `1px solid ${GOLD_A(0.1)}`, borderRadius: 2 }}>{l.category}</span>
                                  <span style={{ fontFamily: MONO, fontSize: 8, color: MUTED }}>{l.type}</span>
                                  {l.loggedHours > 0 && <span style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.5) }}>{l.loggedHours}h logged</span>}
                                </div>
                                {l.notes && <div style={{ fontSize: 10, color: MUTED, lineHeight: 1.4 }}>{l.notes}</div>}
                                {/* Progress bar */}
                                <div style={{ marginTop: 5, height: 3, background: GOLD_A(0.1), borderRadius: 1 }}>
                                  <div style={{ height: '100%', width: `${Math.min(100, l.progress || 0)}%`, background: status === 'done' ? GOLD : GREEN, borderRadius: 1, transition: 'width 0.3s' }} />
                                </div>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', flexShrink: 0 }}>
                                <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color: GOLD }}>{l.progress || 0}%</span>
                                <div style={{ display: 'flex', gap: 3 }}>
                                  <button onClick={() => logLearningTime(l.id, 0.5)} className="ghost-btn" style={{ padding: '2px 6px', fontSize: 8 }}>+30m</button>
                                  <button onClick={() => logLearningTime(l.id, 1)} className="ghost-btn" style={{ padding: '2px 6px', fontSize: 8 }}>+1h</button>
                                </div>
                                <select value={l.status} onChange={e => updateLearning(l.id, 'status', e.target.value)} className="inp" style={{ width: 70, padding: '2px 4px', fontSize: 9 }}>
                                  <option value="active">Active</option><option value="paused">Paused</option><option value="done">Done</option>
                                </select>
                              </div>
                              <button onClick={() => removeLearning(l.id)} className="icon-btn" style={{ color: RED }}>✕</button>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </Card>
                </>
              )}

              {/* ── MOVIES SUB-TAB ── */}
              {journalTab === 'movies' && (
                <Card>
                  <CardTitle right={
                    <button onClick={() => { setShowAddMovie(!showAddMovie); setEditingMovieId(null); setMovieForm({ title: '', year: '', tag: 'snxper', note: '', rating: null }); }} style={{
                      background: showAddMovie ? RED_A(0.12) : GOLD_A(0.1), border: `1px solid ${showAddMovie ? RED_A(0.3) : GOLD_A(0.25)}`,
                      color: showAddMovie ? RED : GOLD, cursor: 'pointer', padding: '3px 10px', borderRadius: 2,
                      fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em',
                    }}>{showAddMovie ? '✕ CANCEL' : '+ ADD MOVIE'}</button>
                  }>映画 · Watchlist</CardTitle>

                  {/* ── Weekly Goal Banner ── */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 14, borderRadius: 2,
                    background: weeklyMovieGoalMet ? GREEN_A(0.06) : GOLD_A(0.04),
                    border: `1px solid ${weeklyMovieGoalMet ? GREEN_A(0.2) : GOLD_A(0.1)}`,
                  }}>
                    <span style={{ fontSize: 16 }}>{weeklyMovieGoalMet ? '✓' : '◎'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.15em', color: weeklyMovieGoalMet ? GREEN : GOLD, textTransform: 'uppercase' }}>
                        Weekly Goal · 1 Movie / Week
                      </div>
                      <div style={{ fontSize: 10, color: MUTED, marginTop: 2 }}>
                        {weeklyMovieGoalMet
                          ? `${moviesWatchedThisWeek} watched this week — goal met 覚悟`
                          : 'No movies completed this week — pick one and commit'}
                      </div>
                    </div>
                    <div style={{
                      fontFamily: MONO, fontSize: 18, fontWeight: 800, color: weeklyMovieGoalMet ? GREEN : GOLD_A(0.3),
                      lineHeight: 1,
                    }}>{moviesWatchedThisWeek}/1</div>
                  </div>

                  {/* ── Add Movie Form ── */}
                  {showAddMovie && (
                    <div style={{
                      padding: 14, marginBottom: 14, borderRadius: 2,
                      background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.15)}`,
                    }}>
                      <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.2em', color: GOLD, marginBottom: 10, textTransform: 'uppercase' }}>+ New Movie</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, marginBottom: 8 }}>
                        <input value={movieForm.title} onChange={e => setMovieForm(p => ({ ...p, title: e.target.value }))} placeholder="Movie title..."
                          style={{ background: GOLD_A(0.06), border: `1px solid ${GOLD_A(0.15)}`, color: CREAM, padding: '8px 10px', borderRadius: 2, fontFamily: SANS, fontSize: 12, outline: 'none', width: '100%' }}
                          onKeyDown={e => e.key === 'Enter' && addMovie()}
                        />
                        <input value={movieForm.year} onChange={e => setMovieForm(p => ({ ...p, year: e.target.value }))} placeholder="Year" type="number"
                          style={{ background: GOLD_A(0.06), border: `1px solid ${GOLD_A(0.15)}`, color: CREAM, padding: '8px 10px', borderRadius: 2, fontFamily: MONO, fontSize: 11, outline: 'none', width: 70 }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                        {Object.entries(MOVIE_TAGS).map(([key, val]) => (
                          <button key={key} onClick={() => setMovieForm(p => ({ ...p, tag: key }))} style={{
                            padding: '4px 10px', borderRadius: 2, cursor: 'pointer', fontFamily: MONO, fontSize: 9,
                            background: movieForm.tag === key ? `${val.color}18` : 'transparent',
                            border: `1px solid ${movieForm.tag === key ? `${val.color}50` : GOLD_A(0.1)}`,
                            color: movieForm.tag === key ? val.color : MUTED,
                          }}>{val.label}</button>
                        ))}
                      </div>
                      <textarea value={movieForm.note} onChange={e => setMovieForm(p => ({ ...p, note: e.target.value }))} placeholder="Notes — why this movie matters..."
                        rows={2} style={{ background: GOLD_A(0.06), border: `1px solid ${GOLD_A(0.15)}`, color: CREAM, padding: '8px 10px', borderRadius: 2, fontFamily: SANS, fontSize: 11, outline: 'none', width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                        <button onClick={addMovie} style={{
                          background: GOLD_A(0.12), border: `1px solid ${GOLD_A(0.3)}`, color: GOLD,
                          cursor: 'pointer', padding: '6px 18px', borderRadius: 2, fontFamily: MONO, fontSize: 10, letterSpacing: '0.1em',
                        }}>ADD TO WATCHLIST</button>
                      </div>
                    </div>
                  )}

                  {/* Filter tabs */}
                  <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
                    {[
                      { id: 'all', label: 'All' },
                      { id: 'snxper', label: '🎬 by SNXPER' },
                      { id: 'money', label: '💰 Money' },
                      { id: 'warrior', label: '⚔ Warrior' },
                      { id: 'mindset', label: '🧠 Mindset' },
                      { id: 'unwatched', label: '◎ Unwatched' },
                      { id: 'watched', label: '✓ Watched' },
                    ].map(f => (
                      <button key={f.id} onClick={() => setMovieFilter(f.id)} style={{
                        padding: '4px 10px', borderRadius: 2, cursor: 'pointer', fontFamily: MONO, fontSize: 9,
                        background: movieFilter === f.id ? GOLD_A(0.1) : 'transparent',
                        border: `1px solid ${movieFilter === f.id ? GOLD_A(0.3) : GOLD_A(0.1)}`,
                        color: movieFilter === f.id ? GOLD : MUTED,
                      }}>{f.label}</button>
                    ))}
                  </div>

                  <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED, marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
                    <span>{movies.filter(m => m.watched).length}/{movies.length} watched</span>
                    <span>{movies.filter(m => m.tag === 'snxper').length} by SNXPER</span>
                  </div>

                  {filteredMovies.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 30, color: MUTED, fontFamily: MONO, fontSize: 10 }}>
                      No movies in this filter. Add one above.
                    </div>
                  )}

                  {filteredMovies.map(m => {
                    const tagInfo = MOVIE_TAGS[m.tag] || {};
                    const isEditing = editingMovieId === m.id;
                    const isDeleteConfirm = movieDeleteConfirm === m.id;

                    if (isEditing) {
                      return (
                        <div key={m.id} style={{ padding: 14, marginBottom: 6, borderRadius: 2, background: GOLD_A(0.06), border: `1px solid ${GOLD_A(0.2)}` }}>
                          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.15em', color: GOLD, marginBottom: 8, textTransform: 'uppercase' }}>✎ Editing</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, marginBottom: 8 }}>
                            <input value={movieForm.title} onChange={e => setMovieForm(p => ({ ...p, title: e.target.value }))} placeholder="Title"
                              style={{ background: GOLD_A(0.06), border: `1px solid ${GOLD_A(0.15)}`, color: CREAM, padding: '7px 10px', borderRadius: 2, fontFamily: SANS, fontSize: 12, outline: 'none', width: '100%' }}
                              onKeyDown={e => e.key === 'Enter' && saveEditMovie()}
                            />
                            <input value={movieForm.year} onChange={e => setMovieForm(p => ({ ...p, year: e.target.value }))} placeholder="Year" type="number"
                              style={{ background: GOLD_A(0.06), border: `1px solid ${GOLD_A(0.15)}`, color: CREAM, padding: '7px 10px', borderRadius: 2, fontFamily: MONO, fontSize: 11, outline: 'none', width: 70 }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: 5, marginBottom: 8, flexWrap: 'wrap' }}>
                            {Object.entries(MOVIE_TAGS).map(([key, val]) => (
                              <button key={key} onClick={() => setMovieForm(p => ({ ...p, tag: key }))} style={{
                                padding: '3px 8px', borderRadius: 2, cursor: 'pointer', fontFamily: MONO, fontSize: 8,
                                background: movieForm.tag === key ? `${val.color}18` : 'transparent',
                                border: `1px solid ${movieForm.tag === key ? `${val.color}50` : GOLD_A(0.1)}`,
                                color: movieForm.tag === key ? val.color : MUTED,
                              }}>{val.label}</button>
                            ))}
                          </div>
                          <textarea value={movieForm.note} onChange={e => setMovieForm(p => ({ ...p, note: e.target.value }))} placeholder="Notes..."
                            rows={2} style={{ background: GOLD_A(0.06), border: `1px solid ${GOLD_A(0.15)}`, color: CREAM, padding: '7px 10px', borderRadius: 2, fontFamily: SANS, fontSize: 11, outline: 'none', width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
                          />
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 8 }}>
                            <button onClick={cancelMovieEdit} style={{ background: 'transparent', border: `1px solid ${GOLD_A(0.15)}`, color: MUTED, cursor: 'pointer', padding: '5px 12px', borderRadius: 2, fontFamily: MONO, fontSize: 9 }}>CANCEL</button>
                            <button onClick={saveEditMovie} style={{ background: GOLD_A(0.12), border: `1px solid ${GOLD_A(0.3)}`, color: GOLD, cursor: 'pointer', padding: '5px 14px', borderRadius: 2, fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em' }}>SAVE</button>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={m.id} style={{ display: 'flex', gap: 10, padding: '12px 0', borderBottom: `1px solid ${GOLD_A(0.06)}`, opacity: m.watched ? 0.7 : 1 }}>
                        {/* Watch toggle */}
                        <div onClick={() => toggleMovieWatched(m.id)} style={{
                          width: 22, height: 22, borderRadius: 2, cursor: 'pointer', flexShrink: 0, marginTop: 2,
                          background: m.watched ? GREEN_A(0.15) : GOLD_A(0.04),
                          border: `1px solid ${m.watched ? GREEN_A(0.4) : GOLD_A(0.15)}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, color: m.watched ? GREEN : 'transparent',
                        }}>✓</div>

                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 3 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: m.watched ? MUTED : CREAM, textDecoration: m.watched ? 'line-through' : 'none' }}>{m.title}</span>
                            {m.year && <span style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.3) }}>{m.year}</span>}
                          </div>
                          {m.note && <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.5, marginBottom: 4 }}>{m.note}</div>}
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: MONO, fontSize: 8, padding: '1px 6px', borderRadius: 2, border: `1px solid ${tagInfo.color || GOLD}40`, color: tagInfo.color || GOLD }}>{tagInfo.label || m.tag}</span>
                            {m.dateWatched && <span style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.25) }}>watched {m.dateWatched}</span>}
                            {/* Star rating */}
                            {m.watched && (
                              <div style={{ display: 'flex', gap: 2 }}>
                                {[1,2,3,4,5].map(s => (
                                  <span key={s} onClick={() => rateMovie(m.id, s)} style={{
                                    cursor: 'pointer', fontSize: 12, color: s <= (m.rating || 0) ? ICE : GOLD_A(0.15),
                                  }}>★</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Edit / Delete actions */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                          <button onClick={() => startEditMovie(m)} title="Edit" style={{
                            background: 'transparent', border: `1px solid ${GOLD_A(0.1)}`, color: GOLD_A(0.4),
                            cursor: 'pointer', padding: '3px 6px', borderRadius: 2, fontSize: 10, lineHeight: 1,
                          }}>✎</button>
                          {isDeleteConfirm ? (
                            <div style={{ display: 'flex', gap: 2 }}>
                              <button onClick={() => deleteMovie(m.id)} title="Confirm delete" style={{
                                background: RED_A(0.15), border: `1px solid ${RED_A(0.4)}`, color: RED,
                                cursor: 'pointer', padding: '3px 5px', borderRadius: 2, fontSize: 8, lineHeight: 1,
                              }}>✓</button>
                              <button onClick={() => setMovieDeleteConfirm(null)} title="Cancel" style={{
                                background: 'transparent', border: `1px solid ${GOLD_A(0.1)}`, color: MUTED,
                                cursor: 'pointer', padding: '3px 5px', borderRadius: 2, fontSize: 8, lineHeight: 1,
                              }}>✕</button>
                            </div>
                          ) : (
                            <button onClick={() => setMovieDeleteConfirm(m.id)} title="Delete" style={{
                              background: 'transparent', border: `1px solid ${RED_A(0.08)}`, color: RED_A(0.35),
                              cursor: 'pointer', padding: '3px 6px', borderRadius: 2, fontSize: 10, lineHeight: 1,
                            }}>✕</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </Card>
              )}
            </div>
          )}

          {/* ═══ MINDSET ═══ */}
          {tab === 'mindset' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Discipline */}
              <Card glow={daysClean > 7}>
                <CardTitle>自制心 · Discipline <span style={{ color: GOLD_A(0.25) }}>/ Christ or Evil</span></CardTitle>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
                  <div style={{
                    textAlign: 'center', padding: '20px 12px', borderRadius: 2,
                    border: `1px solid ${daysClean > 0 ? GREEN_A(0.25) : RED_A(0.2)}`,
                    background: daysClean > 0 ? GREEN_A(0.04) : RED_A(0.03),
                    boxShadow: daysClean > 14 ? `0 0 30px ${GREEN_A(0.08)}` : 'none',
                  }}>
                    <div style={{ fontSize: 40, fontWeight: 800, color: daysClean > 14 ? GREEN : daysClean > 3 ? ORANGE : RED, letterSpacing: '-0.04em', lineHeight: 1 }}>
                      {privacyMode ? '•••' : daysClean}
                    </div>
                    <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.2em', color: MUTED, marginTop: 5, textTransform: 'uppercase' }}>Days Clean · 清</div>
                    <div style={{ fontSize: 9, color: MUTED, marginTop: 4 }}>Christ Energy · Protected</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '20px 12px', border: `1px solid ${RED_A(0.15)}`, borderRadius: 2, background: RED_A(0.03) }}>
                    <div style={{ fontSize: 40, fontWeight: 800, color: evilCount === 0 ? GREEN : RED, letterSpacing: '-0.04em', lineHeight: 1 }}>{privacyMode ? '•••' : evilCount}</div>
                    <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.2em', color: MUTED, marginTop: 5, textTransform: 'uppercase' }}>Falls Total</div>
                    <div style={{ fontSize: 9, color: MUTED, marginTop: 4 }}>Submit to Devil · 悪</div>
                  </div>
                </div>

                <div style={{ marginBottom: 12, padding: '10px 14px', background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.1)}`, borderRadius: 2, fontSize: 11, color: MUTED }}>
                  Today:{' '}
                  {!todayDiscipline ? <span style={{ color: GOLD_A(0.5) }}>Not logged yet</span>
                    : todayDiscipline.type === 'clean' ? <span style={{ color: GREEN }}>✓ Clean — 清 · Protected</span>
                    : <span style={{ color: RED }}>✗ Relapse logged</span>}
                  {todayDiscipline && (
                    <button onClick={() => setDisciplineLogs(prev => prev.filter(d => d.date !== todayStr))} className="icon-btn" style={{ marginLeft: 10, fontSize: 10 }}>undo</button>
                  )}
                  {!lastEvil && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, fontSize: 9, color: MUTED }}>
                      <span style={{ fontFamily: MONO, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Clean since</span>
                      <input type="date" value={cleanStart} max={todayStr} onChange={e => e.target.value && setCleanStart(e.target.value)} className="inp" style={{ width: 'auto', padding: '4px 8px', fontSize: 10 }} />
                    </div>
                  )}
                </div>

                {disciplineConfirm === 'pending' ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 14px', background: RED_A(0.06), border: `1px solid ${RED_A(0.2)}`, borderRadius: 2 }}>
                    <span style={{ flex: 1, fontSize: 11, color: RED }}>Are you sure? Be honest with yourself.</span>
                    <button onClick={confirmRelapse} className="ghost-btn" style={{ borderColor: RED_A(0.4), color: RED }}>Yes, log it</button>
                    <button onClick={() => setDisciplineConfirm(null)} className="ghost-btn">Cancel</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => handleDiscipline('clean')} disabled={todayDiscipline?.type === 'clean'} className="habit-btn" style={{
                      flex: 1, background: todayDiscipline?.type === 'clean' ? GREEN_A(0.15) : GREEN_A(0.08),
                      border: `1px solid ${todayDiscipline?.type === 'clean' ? GREEN_A(0.5) : GREEN_A(0.25)}`,
                      padding: '11px 14px', fontSize: 11, fontWeight: 700, color: GREEN, fontFamily: MONO, letterSpacing: '0.08em', textTransform: 'uppercase',
                      cursor: todayDiscipline?.type === 'clean' ? 'default' : 'pointer',
                    }}>{todayDiscipline?.type === 'clean' ? '✓ Logged Clean' : '✓ Stayed Clean Today'}</button>
                    <button onClick={() => handleDiscipline('evil')} className="habit-btn" style={{
                      flex: 1, background: RED_A(0.06), border: `1px solid ${RED_A(0.2)}`,
                      padding: '11px 14px', fontSize: 11, fontWeight: 700, color: RED, fontFamily: MONO, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer',
                    }}>✗ Log Relapse</button>
                  </div>
                )}
              </Card>

              {/* Media Library */}
              <Card>
                <CardTitle right={<button onClick={() => setShowAddVideo(a => !a)} className="ghost-btn">{showAddVideo ? 'Cancel' : '+ Add Video'}</button>}>映像 · Media Library</CardTitle>

                {showAddVideo && (
                  <div style={{ background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.1)}`, borderRadius: 2, padding: 12, marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input placeholder="YouTube URL" value={newVideoUrl} onChange={e => setNewVideoUrl(e.target.value)} className="inp" style={{ flex: 1, minWidth: 160 }} />
                    <input placeholder="Title" value={newVideoTitle} onChange={e => setNewVideoTitle(e.target.value)} className="inp" style={{ width: 140 }} />
                    <button onClick={() => {
                      if (!newVideoUrl.trim()) return;
                      const url = newVideoUrl.trim();
                      let videoId = '';
                      if (url.includes('youtu.be/')) videoId = url.split('youtu.be/')[1]?.split(/[?&]/)[0];
                      else if (url.includes('watch?v=')) videoId = url.split('watch?v=')[1]?.split(/[?&]/)[0];
                      else if (url.includes('embed/')) videoId = url.split('embed/')[1]?.split(/[?&]/)[0];
                      else videoId = url;
                      if (videoId) {
                        setMindsetVideos(prev => [...prev, { id: Date.now(), videoId, title: newVideoTitle.trim() || 'Untitled', size: 'small' }]);
                        setNewVideoUrl(''); setNewVideoTitle(''); setShowAddVideo(false);
                      }
                    }} className="gold-btn">Add</button>
                  </div>
                )}

                {mindsetVideos.length === 0 && !showAddVideo && <div style={{ fontSize: 11, color: MUTED, fontStyle: 'italic' }}>No videos yet. Add YouTube links to build your library.</div>}

                {mindsetVideos.length > 0 && (() => {
                  // Keep index in range as the list changes
                  const idx = Math.min(videoIdx, mindsetVideos.length - 1);
                  const v = mindsetVideos[idx];
                  const go = (dir) => setVideoIdx(((idx + dir) % mindsetVideos.length + mindsetVideos.length) % mindsetVideos.length);
                  return (
                    <div>
                      {/* Title + delete */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: CREAM, fontWeight: 500 }}>{v.title}</span>
                        <button onClick={() => { setMindsetVideos(prev => prev.filter(x => x.id !== v.id)); setVideoIdx(i => Math.max(0, i - (idx === mindsetVideos.length - 1 ? 1 : 0))); }} className="icon-btn" style={{ color: RED, fontSize: 10 }}>✕</button>
                      </div>

                      {/* Player with side arrows */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button onClick={() => go(-1)} disabled={mindsetVideos.length < 2} className="icon-btn" style={{
                          fontSize: 20, color: mindsetVideos.length < 2 ? GOLD_A(0.15) : GOLD_A(0.6), padding: '0 4px',
                          cursor: mindsetVideos.length < 2 ? 'default' : 'pointer', flexShrink: 0, lineHeight: 1,
                        }}>‹</button>

                        <div style={{ position: 'relative', flex: 1, paddingBottom: '38%', borderRadius: 2, overflow: 'hidden', border: `1px solid ${GOLD_A(0.12)}` }}>
                          <iframe
                            key={v.id}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                            src={`https://www.youtube.com/embed/${v.videoId}`}
                            title={v.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                          />
                        </div>

                        <button onClick={() => go(1)} disabled={mindsetVideos.length < 2} className="icon-btn" style={{
                          fontSize: 20, color: mindsetVideos.length < 2 ? GOLD_A(0.15) : GOLD_A(0.6), padding: '0 4px',
                          cursor: mindsetVideos.length < 2 ? 'default' : 'pointer', flexShrink: 0, lineHeight: 1,
                        }}>›</button>
                      </div>

                      {/* Position indicator */}
                      <div style={{ textAlign: 'center', marginTop: 8, fontFamily: MONO, fontSize: 9, letterSpacing: '0.15em', color: GOLD_A(0.4) }}>
                        {idx + 1} / {mindsetVideos.length}
                      </div>
                    </div>
                  );
                })()}
              </Card>

              {/* Book Notes */}
              <Card>
                <CardTitle right={<button onClick={() => setShowAddBookNote(a => !a)} className="ghost-btn">{showAddBookNote ? 'Cancel' : '+ New Book'}</button>}>筆記 · Book Notes</CardTitle>

                {showAddBookNote && (
                  <div style={{ background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.1)}`, borderRadius: 2, padding: 12, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input placeholder="Book title" value={newBookNote.book} onChange={e => setNewBookNote(p => ({ ...p, book: e.target.value }))} className="inp" />
                    <button onClick={() => {
                      if (newBookNote.book.trim()) {
                        setBookNotes(prev => [...prev, { id: 'bnote_' + Date.now(), book: newBookNote.book.trim(), chapters: [] }]);
                        setNewBookNote({ book: '', chapter: '', notes: '' });
                        setShowAddBookNote(false);
                      }
                    }} className="gold-btn">Create Book</button>
                  </div>
                )}

                {bookNotes.length === 0 && !showAddBookNote && <div style={{ fontSize: 11, color: MUTED, fontStyle: 'italic' }}>No book notes yet. Start a book to take chapter notes.</div>}

                {bookNotes.map(book => {
                  const isExpanded = expandedNote === book.id;
                  return (
                    <div key={book.id} style={{ borderBottom: `1px solid ${GOLD_A(0.06)}`, paddingBottom: 10, marginBottom: 10 }}>
                      {/* Book header */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setExpandedNote(isExpanded ? null : book.id)}>
                        <div style={{ width: 4, height: 40, borderRadius: 2, background: GOLD, flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: CREAM }}>{book.book}</div>
                          <div style={{ fontFamily: MONO, fontSize: 9, color: MUTED }}>{book.chapters.length} chapter{book.chapters.length !== 1 ? 's' : ''} noted</div>
                        </div>
                        <span style={{ fontSize: 10, color: GOLD_A(0.3) }}>{isExpanded ? '▲' : '▼'}</span>
                        <button onClick={e => { e.stopPropagation(); setBookNotes(prev => prev.filter(b => b.id !== book.id)); }} className="icon-btn" style={{ color: RED }}>✕</button>
                      </div>

                      {/* Expanded — chapters + add */}
                      {isExpanded && (
                        <div style={{ marginLeft: 14, marginTop: 10 }}>
                          {/* Existing chapters */}
                          {book.chapters.map((ch, ci) => (
                            <div key={ci} style={{ marginBottom: 10, padding: '10px 12px', background: GOLD_A(0.03), border: `1px solid ${GOLD_A(0.07)}`, borderRadius: 2 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <span style={{ fontFamily: MONO, fontSize: 10, color: GOLD, letterSpacing: '0.1em' }}>{ch.title}</span>
                                <button onClick={() => setBookNotes(prev => prev.map(b => b.id === book.id ? { ...b, chapters: b.chapters.filter((_, i) => i !== ci) } : b))} className="icon-btn" style={{ color: RED, fontSize: 10 }}>✕</button>
                              </div>
                              {editingNote === `${book.id}-${ci}` ? (
                                <textarea
                                  value={ch.notes}
                                  onChange={e => setBookNotes(prev => prev.map(b => b.id === book.id ? { ...b, chapters: b.chapters.map((c, i) => i === ci ? { ...c, notes: e.target.value } : c) } : b))}
                                  onBlur={() => setEditingNote(null)}
                                  className="inp"
                                  style={{ minHeight: 80, resize: 'vertical', fontSize: 11.5, lineHeight: 1.6 }}
                                  autoFocus
                                />
                              ) : (
                                <div onClick={() => setEditingNote(`${book.id}-${ci}`)} style={{ fontSize: 11.5, color: ch.notes ? CREAM : MUTED, lineHeight: 1.6, cursor: 'text', minHeight: 20, whiteSpace: 'pre-wrap' }}>
                                  {ch.notes || 'Click to add notes...'}
                                </div>
                              )}
                            </div>
                          ))}

                          {/* Add chapter */}
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
                            <input
                              placeholder="Chapter title (e.g. Chapter 1 — The Beginning)"
                              value={newBookNote.chapter}
                              onChange={e => setNewBookNote(p => ({ ...p, chapter: e.target.value }))}
                              className="inp" style={{ flex: 1, padding: '6px 10px', fontSize: 11 }}
                              onKeyDown={e => {
                                if (e.key === 'Enter' && newBookNote.chapter.trim()) {
                                  setBookNotes(prev => prev.map(b => b.id === book.id ? { ...b, chapters: [...b.chapters, { title: newBookNote.chapter.trim(), notes: '' }] } : b));
                                  setNewBookNote(p => ({ ...p, chapter: '' }));
                                }
                              }}
                            />
                            <button onClick={() => {
                              if (newBookNote.chapter.trim()) {
                                setBookNotes(prev => prev.map(b => b.id === book.id ? { ...b, chapters: [...b.chapters, { title: newBookNote.chapter.trim(), notes: '' }] } : b));
                                setNewBookNote(p => ({ ...p, chapter: '' }));
                              }
                            }} className="ghost-btn" style={{ padding: '6px 10px', fontSize: 9 }}>+ Chapter</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </Card>

              {/* Kaizen words */}
              <Card>
                <CardTitle>道 · The Way · Warrior Words</CardTitle>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                  {KAIZEN_WORDS.map((w, i) => {
                    const isExpanded = expandedKaizen === i;
                    return (
                      <div key={i}
                        onClick={() => setExpandedKaizen(isExpanded ? null : i)}
                        style={{
                          padding: '10px 12px', border: `1px solid ${isExpanded ? GOLD_A(0.25) : GOLD_A(0.08)}`,
                          borderRadius: 2, background: isExpanded ? GOLD_A(0.06) : i === kanjiIdx ? GOLD_A(0.03) : 'transparent',
                          transition: 'all 0.4s', cursor: 'pointer',
                          gridColumn: isExpanded ? '1 / -1' : 'auto',
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ fontSize: isExpanded ? 28 : 20, color: isExpanded ? GOLD : GOLD_A(0.5), fontWeight: 300, letterSpacing: 1, transition: 'all 0.3s' }}>{w.word}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontFamily: MONO, fontSize: 9, color: GOLD, letterSpacing: '0.12em', marginBottom: 2 }}>{w.reading}</div>
                            <div style={{ fontSize: 10, color: MUTED, fontStyle: 'italic' }}>{w.meaning}</div>
                          </div>
                          <span style={{ fontSize: 10, color: GOLD_A(0.3), transition: 'transform 0.3s', transform: isExpanded ? 'rotate(180deg)' : 'none' }}>▼</span>
                        </div>

                        {isExpanded && (
                          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${GOLD_A(0.1)}` }}>
                            {/* Deep meaning */}
                            <div style={{ fontSize: 12, color: CREAM, lineHeight: 1.7, marginBottom: 14 }}>{w.depth}</div>

                            {/* Personal story */}
                            <div style={{ padding: '12px 16px', borderLeft: `2px solid ${GOLD_A(0.25)}`, background: GOLD_A(0.03), borderRadius: '0 2px 2px 0' }}>
                              <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.2em', color: GOLD_A(0.4), marginBottom: 6, textTransform: 'uppercase' }}>Your Story · 忍</div>
                              <div style={{ fontSize: 12, color: CREAM, lineHeight: 1.75, fontStyle: 'italic' }}>{w.story}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Quotes */}
              <Card>
                <CardTitle>言葉 · Your Words · The Code</CardTitle>
                {QUOTES.map((q, i) => (
                  <div key={i} style={{ padding: '12px 0', borderBottom: `1px solid ${GOLD_A(0.06)}` }}>
                    <div style={{ fontSize: 12.5, color: CREAM, lineHeight: 1.65, fontStyle: 'italic', marginBottom: 5 }}>"{q.text}"</div>
                    <div style={{ fontFamily: MONO, fontSize: 8, color: GOLD_A(0.4), letterSpacing: '0.15em', textTransform: 'uppercase' }}>— {q.author}</div>
                  </div>
                ))}
              </Card>
            </div>
          )}

          {/* ═══ LUXE AGENT ═══ */}
          {tab === 'luxe' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 760, margin: '0 auto' }}>

              {/* Header + quote */}
              <Card>
                <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
                  <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.34em', color: GOLD_A(0.5), textTransform: 'uppercase', marginBottom: 8 }}>事業 · Business</div>
                  <div style={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 700, color: ICE, letterSpacing: '0.02em', marginBottom: 12 }}>The Brand Engine</div>
                  <div style={{ fontSize: 14, color: CREAM, lineHeight: 1.6, fontStyle: 'italic', maxWidth: 460, margin: '0 auto' }}>
                    "It's not gonna be perfect but it'll be alright"
                  </div>
                </div>
              </Card>

              {/* Sub-tab switcher */}
              <div style={{ display: 'flex', gap: 6 }}>
                {[
                  { id: 'offer', label: '主 Offer' },
                  { id: 'content', label: '案 Content' },
                  { id: 'tasks', label: '任 Tasks' },
                  { id: 'study', label: '研 Study' },
                ].map(st => (
                  <button key={st.id} onClick={() => setLuxeSubTab(st.id)} style={{
                    flex: 1, padding: '10px 8px', borderRadius: 2, cursor: 'pointer', fontFamily: MONO, fontSize: 10,
                    letterSpacing: '0.06em', textTransform: 'uppercase', transition: 'all 0.15s',
                    background: luxeSubTab === st.id ? GOLD_A(0.1) : GOLD_A(0.03),
                    border: `1px solid ${luxeSubTab === st.id ? GOLD_A(0.3) : GOLD_A(0.1)}`,
                    color: luxeSubTab === st.id ? GOLD : MUTED,
                  }}>{st.label}</button>
                ))}
              </div>

              {/* ── OFFER ── */}
              {luxeSubTab === 'offer' && (
              <Card glow>
                <CardTitle right={
                  <button onClick={() => setEditingLuxeOffer(e => !e)} className="ghost-btn">{editingLuxeOffer ? 'Done' : 'Edit'}</button>
                }>主 · Main Offer</CardTitle>

                {editingLuxeOffer ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <input
                      value={luxeOffer.headline}
                      onChange={e => setLuxeOffer(o => ({ ...o, headline: e.target.value }))}
                      placeholder="Your main offer — the headline"
                      className="inp" style={{ fontSize: 15, fontWeight: 600 }}
                    />
                    <textarea
                      value={luxeOffer.body}
                      onChange={e => setLuxeOffer(o => ({ ...o, body: e.target.value }))}
                      placeholder="The promise — what you deliver, for whom, the result. The details."
                      className="inp" style={{ minHeight: 110, resize: 'vertical', lineHeight: 1.65 }}
                    />
                  </div>
                ) : (luxeOffer.headline || luxeOffer.body) ? (
                  <div style={{ padding: '4px 2px' }}>
                    {luxeOffer.headline && <div style={{ fontFamily: DISPLAY, fontSize: 19, fontWeight: 700, color: ICE, lineHeight: 1.3, marginBottom: luxeOffer.body ? 10 : 0 }}>{luxeOffer.headline}</div>}
                    {luxeOffer.body && <div style={{ fontSize: 13.5, color: CREAM, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{luxeOffer.body}</div>}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: MUTED, fontStyle: 'italic', padding: '6px 2px' }}>No offer set yet. Tap Edit to define your main offer.</div>
                )}
              </Card>
              )}

              {/* ── CONTENT IDEAS — grouped by channel ── */}
              {luxeSubTab === 'content' && (
              <Card>
                <CardTitle right={
                  <button onClick={() => setEditLuxeContent(e => !e)} className="ghost-btn">{editLuxeContent ? 'Done' : 'Edit'}</button>
                }>案 · Content Ideas</CardTitle>

                {LUXE_CONTENT_CHANNELS.map(ch => {
                  const ideas = luxeIdeas[ch] || [];
                  const draft = newLuxeIdea[ch] || '';
                  const addIdea = () => { if (draft.trim()) { setLuxeIdeas(prev => ({ ...prev, [ch]: [...(prev[ch] || []), { id: 'idea_' + Date.now(), text: draft.trim() }] })); setNewLuxeIdea(prev => ({ ...prev, [ch]: '' })); } };
                  return (
                    <div key={ch} style={{ marginBottom: 18 }}>
                      <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: GOLD, fontWeight: 500, marginBottom: 8, paddingBottom: 5, borderBottom: `1px solid ${GOLD_A(0.12)}` }}>{ch} <span style={{ color: MUTED, fontWeight: 400 }}>{ideas.length}</span></div>
                      {ideas.length === 0 && !editLuxeContent && <div style={{ fontSize: 11, color: MUTED, fontStyle: 'italic', marginBottom: 8 }}>No ideas yet.</div>}
                      {ideas.map(idea => (
                        <div key={idea.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: `1px solid ${GOLD_A(0.05)}` }}>
                          <span style={{ width: 4, height: 4, borderRadius: '50%', background: GOLD_A(0.5), flexShrink: 0 }} />
                          <span style={{ flex: 1, fontSize: 12.5, color: CREAM, lineHeight: 1.5 }}>{idea.text}</span>
                          {editLuxeContent && <button onClick={() => setLuxeIdeas(prev => ({ ...prev, [ch]: prev[ch].filter(x => x.id !== idea.id) }))} className="icon-btn" style={{ color: RED }}>✕</button>}
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <input value={draft} onChange={e => setNewLuxeIdea(prev => ({ ...prev, [ch]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') addIdea(); }}
                          placeholder={`Idea for ${ch}...`} className="inp" style={{ flex: 1, padding: '6px 10px', fontSize: 12 }} />
                        <button onClick={addIdea} className="ghost-btn" style={{ padding: '6px 14px' }}>+ Add</button>
                      </div>
                    </div>
                  );
                })}
              </Card>
              )}

              {/* ── TASKS TO EXECUTE ── */}
              {luxeSubTab === 'tasks' && (
              <Card>
                <CardTitle right={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {luxeTasks.length > 0 && <span style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.6), letterSpacing: '0.1em' }}>{luxeTasks.filter(t => t.done).length}/{luxeTasks.length}</span>}
                    <button onClick={() => setEditLuxeTasks(e => !e)} className="ghost-btn">{editLuxeTasks ? 'Done' : 'Edit'}</button>
                  </span>
                }>任 · Tasks to Execute</CardTitle>
                <div style={{ display: 'flex', gap: 8, marginBottom: luxeTasks.length ? 12 : 0 }}>
                  <input
                    value={newLuxeTask}
                    onChange={e => setNewLuxeTask(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && newLuxeTask.trim()) { setLuxeTasks(prev => [...prev, { id: 'task_' + Date.now(), text: newLuxeTask.trim(), done: false }]); setNewLuxeTask(''); } }}
                    placeholder="Add a task to execute..." className="inp" style={{ flex: 1 }}
                  />
                  <button onClick={() => { if (newLuxeTask.trim()) { setLuxeTasks(prev => [...prev, { id: 'task_' + Date.now(), text: newLuxeTask.trim(), done: false }]); setNewLuxeTask(''); } }} className="gold-btn">Add</button>
                </div>
                {luxeTasks.length === 0 ? (
                  <div style={{ fontSize: 11, color: MUTED, fontStyle: 'italic' }}>No tasks yet. What needs to get done?</div>
                ) : luxeTasks.map(task => (
                  <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: `1px solid ${GOLD_A(0.06)}`, opacity: task.done ? 0.5 : 1 }}>
                    <button onClick={() => setLuxeTasks(prev => prev.map(t => t.id === task.id ? { ...t, done: !t.done } : t))} style={{
                      width: 20, height: 20, borderRadius: 5, flexShrink: 0, cursor: 'pointer', transition: 'all 0.2s',
                      background: task.done ? GOLD : 'transparent', border: `1.5px solid ${task.done ? GOLD : GOLD_A(0.3)}`,
                      color: task.done ? BG : 'transparent', fontSize: 11, fontWeight: 800, lineHeight: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>✓</button>
                    <span style={{ flex: 1, fontSize: 12.5, color: task.done ? MUTED : CREAM, lineHeight: 1.5, textDecoration: task.done ? 'line-through' : 'none' }}>{task.text}</span>
                    {editLuxeTasks && <button onClick={() => setLuxeTasks(prev => prev.filter(x => x.id !== task.id))} className="icon-btn" style={{ color: RED }}>✕</button>}
                  </div>
                ))}
              </Card>
              )}

              {/* ── PEOPLE TO STUDY — editable categories ── */}
              {luxeSubTab === 'study' && (
              <Card>
                <CardTitle right={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {editLuxeStudy && <button onClick={() => setShowAddLuxeCat(s => !s)} className="ghost-btn">{showAddLuxeCat ? 'Cancel' : '+ Category'}</button>}
                    <button onClick={() => { setEditLuxeStudy(e => !e); setShowAddLuxeCat(false); setEditingStudy(null); }} className="ghost-btn">{editLuxeStudy ? 'Done' : 'Edit'}</button>
                  </span>
                }>研 · People to Study</CardTitle>

                {editLuxeStudy && showAddLuxeCat && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 14, padding: 10, background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.12)}`, borderRadius: 8 }}>
                    <input value={newLuxeCategory} onChange={e => setNewLuxeCategory(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && newLuxeCategory.trim() && !luxeCategories.includes(newLuxeCategory.trim())) { setLuxeCategories(prev => [...prev, newLuxeCategory.trim()]); setNewLuxeCategory(''); setShowAddLuxeCat(false); } }}
                      placeholder="New category name (e.g. Newsletters)" className="inp" style={{ flex: 1 }} autoFocus />
                    <button onClick={() => { const v = newLuxeCategory.trim(); if (v && !luxeCategories.includes(v)) { setLuxeCategories(prev => [...prev, v]); setNewLuxeCategory(''); setShowAddLuxeCat(false); } }} className="gold-btn">Add</button>
                  </div>
                )}

                {luxeCategories.length === 0 && <div style={{ fontSize: 11, color: MUTED, fontStyle: 'italic' }}>No categories yet. Tap Edit → + Category to start.</div>}

                {luxeCategories.map(cat => {
                  const people = luxeStudy[cat] || [];
                  const draft = newStudy[cat] || { name: '', handle: '', note: '' };
                  const setDraft = (patch) => setNewStudy(prev => ({ ...prev, [cat]: { ...draft, ...patch } }));
                  const addPerson = () => {
                    if (!draft.name.trim() && !draft.handle.trim()) return;
                    const person = { id: 'p_' + Date.now(), name: draft.name.trim(), handle: draft.handle.trim(), note: draft.note.trim() };
                    setLuxeStudy(prev => ({ ...prev, [cat]: [...(prev[cat] || []), person] }));
                    setNewStudy(prev => ({ ...prev, [cat]: { name: '', handle: '', note: '' } }));
                  };
                  return (
                    <div key={cat} style={{ marginBottom: 18 }}>
                      {/* Category header */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${GOLD_A(0.12)}` }}>
                        <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: GOLD, fontWeight: 500 }}>{cat}</span>
                        <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED }}>{people.length}</span>
                        <div style={{ flex: 1 }} />
                        {editLuxeStudy && <button onClick={() => { if (window.confirm(`Delete category "${cat}" and its people?`)) { setLuxeCategories(prev => prev.filter(c => c !== cat)); setLuxeStudy(prev => { const n = { ...prev }; delete n[cat]; return n; }); } }} className="icon-btn" style={{ color: RED_A(0.6), fontSize: 10 }}>✕ delete</button>}
                      </div>

                      {/* People in this category */}
                      {people.length === 0 && !editLuxeStudy && <div style={{ fontSize: 11, color: MUTED, fontStyle: 'italic', marginBottom: 6 }}>None yet.</div>}
                      {people.map(person => {
                        const eid = `${cat}_${person.id}`;
                        const isEditing = editingStudy === eid;
                        const upd = (patch) => setLuxeStudy(prev => ({ ...prev, [cat]: prev[cat].map(p => p.id === person.id ? { ...p, ...patch } : p) }));
                        return (
                          <div key={person.id} style={{ padding: '9px 0', borderBottom: `1px solid ${GOLD_A(0.05)}` }}>
                            {isEditing ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <input value={person.name} onChange={e => upd({ name: e.target.value })} placeholder="Name" className="inp" style={{ flex: 1, padding: '6px 9px', fontSize: 12 }} />
                                  <input value={person.handle} onChange={e => upd({ handle: e.target.value })} placeholder="@handle" className="inp" style={{ flex: 1, padding: '6px 9px', fontSize: 12 }} />
                                </div>
                                <textarea value={person.note} onChange={e => upd({ note: e.target.value })} placeholder="What you like about them..." className="inp" style={{ minHeight: 50, resize: 'vertical', padding: '6px 9px', fontSize: 12 }} />
                                <button onClick={() => setEditingStudy(null)} className="ghost-btn" style={{ alignSelf: 'flex-start' }}>Done</button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                                    {person.name && <span style={{ fontSize: 13, fontWeight: 600, color: ICE }}>{person.name}</span>}
                                    {person.handle && <span style={{ fontFamily: MONO, fontSize: 10.5, color: GOLD_A(0.7) }}>{person.handle}</span>}
                                  </div>
                                  {person.note && <div style={{ fontSize: 12, color: CREAM, lineHeight: 1.55, marginTop: 3, whiteSpace: 'pre-wrap' }}>{person.note}</div>}
                                </div>
                                {editLuxeStudy && <>
                                  <button onClick={() => setEditingStudy(eid)} className="icon-btn" style={{ color: GOLD_A(0.5), fontSize: 11 }}>edit</button>
                                  <button onClick={() => setLuxeStudy(prev => ({ ...prev, [cat]: prev[cat].filter(p => p.id !== person.id) }))} className="icon-btn" style={{ color: RED }}>✕</button>
                                </>}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Add person — always available (this is the core action) */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input value={draft.name} onChange={e => setDraft({ name: e.target.value })} placeholder="Name" className="inp" style={{ flex: 1, padding: '6px 9px', fontSize: 12 }} />
                          <input value={draft.handle} onChange={e => setDraft({ handle: e.target.value })} placeholder="@handle" className="inp" style={{ flex: 1, padding: '6px 9px', fontSize: 12 }}
                            onKeyDown={e => { if (e.key === 'Enter') addPerson(); }} />
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input value={draft.note} onChange={e => setDraft({ note: e.target.value })} placeholder="What you like about them (notes)..." className="inp" style={{ flex: 1, padding: '6px 9px', fontSize: 12 }}
                            onKeyDown={e => { if (e.key === 'Enter') addPerson(); }} />
                          <button onClick={addPerson} className="ghost-btn" style={{ padding: '6px 14px' }}>+ Add</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Card>
              )}

            </div>
          )}

          {/* ═══ SEEDS — people I meet + the opportunity they carry ═══ */}
          {tab === 'seeds' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 760, margin: '0 auto', width: '100%' }}>

              {/* Header */}
              <Card glow>
                <CardTitle right={<button onClick={() => setShowAddSeed(a => !a)} className="ghost-btn">{showAddSeed ? 'Cancel' : '+ Add person'}</button>}>種 · Seeds <span style={{ color: GOLD_A(0.25) }}>/ 縁 connections</span></CardTitle>
                {(() => {
                  const growing = seeds.filter(s => s.stage !== 'cut').length;
                  const cut = seeds.filter(s => s.stage === 'cut').length;
                  return (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <span style={{ fontSize: 40, fontWeight: 800, color: GOLD, lineHeight: 1 }}>{growing}</span>
                        <span style={{ fontSize: 12, color: MUTED }}>in the ground</span>
                      </div>
                      {cut > 0 && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                          <span style={{ fontSize: 40, fontWeight: 800, color: RED, lineHeight: 1 }}>{cut}</span>
                          <span style={{ fontSize: 12, color: MUTED }}>pulled out</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div style={{ fontFamily: MONO, fontSize: 9, color: GOLD_A(0.4), letterSpacing: '0.05em' }}>
                  Every person is a seed. Water the ones that grow — pull the weeds. Nothing is wasted.
                </div>

                {/* Add form */}
                {showAddSeed && (
                  <div style={{ background: GOLD_A(0.04), border: `1px solid ${GOLD_A(0.1)}`, borderRadius: 8, padding: 12, marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input autoFocus placeholder={newSeed.stage === 'cut' ? 'Who are you cutting off?' : 'Who did you meet?'} value={newSeed.name} onChange={e => setNewSeed(p => ({ ...p, name: e.target.value }))} className="inp" />
                    <textarea placeholder={newSeed.stage === 'cut' ? 'Why they\'re out — what happened, why you shouldn\'t be around them, the lesson…' : 'The note — who they are, the opportunity, what stood out…'} value={newSeed.note} onChange={e => setNewSeed(p => ({ ...p, note: e.target.value }))} className="inp" style={{ minHeight: 64, resize: 'vertical', lineHeight: 1.5 }} />
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {SEED_STAGE_ALL.map(k => (
                        <button key={k} onClick={() => setNewSeed(p => ({ ...p, stage: k }))} className="ghost-btn" style={{ borderColor: newSeed.stage === k ? SEED_STAGES[k].color : undefined, color: newSeed.stage === k ? SEED_STAGES[k].color : undefined }}>{SEED_STAGES[k].kanji} {SEED_STAGES[k].label}</button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Year met</span>
                        <input type="number" value={newSeed.metYear} onChange={e => setNewSeed(p => ({ ...p, metYear: e.target.value }))} placeholder={String(new Date().getFullYear())} className="inp" style={{ width: 90, padding: '6px 10px', fontFamily: MONO, textAlign: 'center' }} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 160 }}>
                        <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Country</span>
                        <input value={newSeed.metCountry} onChange={e => setNewSeed(p => ({ ...p, metCountry: e.target.value }))} placeholder="e.g. UAE" className="inp" style={{ flex: 1, padding: '6px 10px' }} />
                      </div>
                    </div>
                    <button onClick={() => {
                      if (newSeed.name.trim()) {
                        setSeeds(prev => [{ id: 'seed_' + Date.now(), name: newSeed.name.trim(), note: newSeed.note.trim(), stage: newSeed.stage, metYear: Number(newSeed.metYear) || new Date().getFullYear(), metCountry: newSeed.metCountry.trim(), met: getTodayStr() }, ...prev]);
                        setNewSeed({ name: '', note: '', stage: 'planted', metYear: new Date().getFullYear(), metCountry: '' }); setShowAddSeed(false); playClick();
                      }
                    }} className="gold-btn" style={{ alignSelf: 'flex-start' }}>{newSeed.stage === 'cut' ? 'Log it' : 'Plant it'}</button>
                  </div>
                )}
              </Card>

              {/* Stage filter */}
              {seeds.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {['all', ...SEED_STAGE_ALL].map(k => {
                    const count = k === 'all' ? seeds.length : seeds.filter(s => s.stage === k).length;
                    const active = seedStageFilter === k;
                    const col = k === 'all' ? GOLD : SEED_STAGES[k].color;
                    return (
                      <button key={k} onClick={() => setSeedStageFilter(k)} style={{
                        padding: '5px 12px', borderRadius: 100, cursor: 'pointer', fontFamily: MONO, fontSize: 9, letterSpacing: '0.05em',
                        background: active ? `${col}1a` : 'transparent',
                        border: `1px solid ${active ? col : GOLD_A(0.12)}`,
                        color: active ? col : MUTED,
                      }}>{k === 'all' ? 'All' : `${SEED_STAGES[k].kanji} ${SEED_STAGES[k].label}`} · {count}</button>
                    );
                  })}
                </div>
              )}

              {/* Empty state */}
              {seeds.length === 0 && !showAddSeed && (
                <Card>
                  <div style={{ textAlign: 'center', padding: '24px 12px' }}>
                    <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.5 }}>🌱</div>
                    <div style={{ fontSize: 13, color: CREAM, marginBottom: 4 }}>No seeds planted yet.</div>
                    <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.5 }}>Meet someone with potential? Add them. A note today can be an opportunity next year.</div>
                  </div>
                </Card>
              )}

              {/* Seed cards */}
              {seeds
                .filter(s => seedStageFilter === 'all' || s.stage === seedStageFilter)
                // In "All", rank best → worst (Cut Off sinks to the bottom); an
                // explicit stage filter keeps its natural newest-first order.
                .slice()
                .sort((a, b) => seedStageFilter === 'all'
                  ? (SEED_STAGE_RANK[a.stage] ?? 3) - (SEED_STAGE_RANK[b.stage] ?? 3)
                  : 0)
                .map(seed => {
                  const stage = SEED_STAGES[seed.stage] || SEED_STAGES.planted;
                  const isEditing = editingSeed === seed.id;
                  const updateSeed = (field, val) => setSeeds(prev => prev.map(s => s.id === seed.id ? { ...s, [field]: val } : s));
                  const cycleStage = () => {
                    const idx = SEED_STAGE_ORDER.indexOf(seed.stage);
                    updateSeed('stage', SEED_STAGE_ORDER[(idx + 1) % SEED_STAGE_ORDER.length]);
                    playClick();
                  };
                  return (
                    <Card key={seed.id} style={{ borderLeft: `3px solid ${stage.color}`, ...(seed.stage === 'cut' ? { background: RED_A(0.03), opacity: 0.9 } : {}) }}>
                      {isEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <input value={seed.name} onChange={e => updateSeed('name', e.target.value)} className="inp" placeholder="Name" />
                          <textarea value={seed.note} onChange={e => updateSeed('note', e.target.value)} className="inp" style={{ minHeight: 72, resize: 'vertical', lineHeight: 1.5 }} placeholder={seed.stage === 'cut' ? 'Why they\'re out — what happened, the lesson' : 'Note'} />
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {SEED_STAGE_ALL.map(k => (
                              <button key={k} onClick={() => updateSeed('stage', k)} className="ghost-btn" style={{ borderColor: seed.stage === k ? SEED_STAGES[k].color : undefined, color: seed.stage === k ? SEED_STAGES[k].color : undefined }}>{SEED_STAGES[k].kanji} {SEED_STAGES[k].label}</button>
                            ))}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Year met</span>
                              <input
                                type="number" inputMode="numeric"
                                value={seed.metYear ?? ''}
                                onChange={e => {
                                  const v = e.target.value;
                                  // Store the raw digits while typing so the field never fights you;
                                  // coerce to a number only when there's a value.
                                  updateSeed('metYear', v === '' ? '' : Number(v));
                                }}
                                placeholder={String(new Date().getFullYear())}
                                className="inp" style={{ width: 90, padding: '6px 10px', fontFamily: MONO, textAlign: 'center' }}
                              />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 160 }}>
                              <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Country</span>
                              <input value={seed.metCountry || ''} onChange={e => updateSeed('metCountry', e.target.value)} placeholder="e.g. UAE" className="inp" style={{ flex: 1, padding: '6px 10px' }} />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => setEditingSeed(null)} className="gold-btn">Done</button>
                            <button onClick={() => { setSeeds(prev => prev.filter(s => s.id !== seed.id)); setEditingSeed(null); }} className="ghost-btn" style={{ color: RED, borderColor: RED_A(0.3) }}>Delete</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: seed.note ? 8 : 0 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 15, fontWeight: 700, color: CREAM, letterSpacing: '-0.01em' }}>{seed.name}</div>
                              <div style={{ fontFamily: MONO, fontSize: 8.5, color: MUTED, letterSpacing: '0.08em', marginTop: 3 }}>{seed.stage === 'cut' ? 'cut' : 'met'} {seed.metYear || (seed.met ? String(seed.met).slice(0, 4) : '')}{seed.metCountry ? ` · ${seed.metCountry}` : ''}</div>
                            </div>
                            <button onClick={seed.stage === 'cut' ? undefined : cycleStage} title={seed.stage === 'cut' ? 'Cut off — edit to change' : 'Tap to advance stage'} style={{
                              display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 100, cursor: seed.stage === 'cut' ? 'default' : 'pointer',
                              background: `${stage.color}14`, border: `1px solid ${stage.color}40`, color: stage.color,
                              fontFamily: MONO, fontSize: 9, letterSpacing: '0.06em', flexShrink: 0,
                            }}>{stage.kanji} {stage.label}</button>
                            <button onClick={() => setEditingSeed(seed.id)} className="icon-btn" style={{ color: GOLD_A(0.4) }}>✎</button>
                          </div>
                          {seed.note && <div style={{ fontSize: 12.5, color: CREAM, lineHeight: 1.6, whiteSpace: 'pre-wrap', opacity: 0.85 }}>{seed.note}</div>}
                        </>
                      )}
                    </Card>
                  );
                })}
            </div>
          )}

        </main>
      </div>

      <style>{globalCSS}</style>
    </div>
  );
}

// ── Global CSS ─────────────────────────────────────────────────────────────
const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=Archivo:wght@300;400;500;600&family=DM+Mono:wght@300;400;500&family=Noto+Sans+JP:wght@300;400;500;700&display=swap');

  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  html,body,#root{height:100%;}
  body{background:#0A0A0B;overflow-x:hidden;}
  /* 2am stealth — faint near-white aurora drifting at top centre. Light is the accent, never colour. */
  body::after{content:'';position:fixed;inset:-10%;z-index:0;pointer-events:none;background:radial-gradient(ellipse 820px 520px at 50% -120px,rgba(230,230,232,0.06) 0%,transparent 70%);animation:aurora 18s ease-in-out infinite alternate;}
  @media(prefers-reduced-motion:reduce){body::after{animation:none;}}
  input::placeholder{color:rgba(129,129,135,0.4);}
  input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;}
  select option{background:#131314;color:#C9C9CC;}

  ::-webkit-scrollbar{width:3px;height:3px;}
  ::-webkit-scrollbar-thumb{background:rgba(230,230,232,0.16);border-radius:2px;}
  .thin-scroll::-webkit-scrollbar{width:2px;}
  .thin-scroll::-webkit-scrollbar-thumb{background:rgba(230,230,232,0.1);}

  button{transition:all 0.15s ease;}
  button:not(:disabled):hover{filter:brightness(1.15);}
  button:not(:disabled):active{transform:scale(0.97);}

  .grid-bg{position:fixed;inset:0;z-index:0;pointer-events:none;background-image:linear-gradient(rgba(230,230,232,0.018) 1px,transparent 1px),linear-gradient(90deg,rgba(230,230,232,0.018) 1px,transparent 1px);background-size:48px 48px;}
  .scanlines{position:fixed;inset:0;z-index:1;pointer-events:none;background-image:repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.025) 2px,rgba(0,0,0,0.025) 4px);}
  .glitch-overlay{position:fixed;inset:0;z-index:100;pointer-events:none;background:rgba(230,230,232,0.02);animation:glitchH 0.15s steps(1) infinite;}

  .inp{width:100%;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.08);border-radius:8px;padding:8px 11px;font-size:12.5px;color:#F6F6F7;font-family:'DM Sans',sans-serif;outline:none;transition:border-color 0.2s,box-shadow 0.2s,background 0.2s;}
  .inp:focus{border-color:rgba(255,255,255,0.28);background:rgba(255,255,255,0.05);box-shadow:0 0 0 4px rgba(255,255,255,0.04);}
  /* primary action — near-white pill, dark text. The only solid-fill element. */
  .gold-btn{background:#F5F5F6;border:none;border-radius:100px;padding:9px 20px;font-size:10.5px;font-weight:700;color:#0A0A0B;cursor:pointer;font-family:'DM Mono',monospace;letter-spacing:0.08em;text-transform:uppercase;white-space:nowrap;box-shadow:0 10px 30px rgba(0,0,0,0.5),inset 0 0 0 1px rgba(255,255,255,0.1);}
  .ghost-btn{background:transparent;border:1px solid rgba(230,230,232,0.2);border-radius:100px;padding:7px 15px;font-size:10px;color:rgba(230,230,232,0.7);cursor:pointer;font-family:'DM Mono',monospace;letter-spacing:0.06em;white-space:nowrap;}
  .icon-btn{background:transparent;border:none;cursor:pointer;color:rgba(230,230,232,0.45);font-size:13px;padding:2px 4px;line-height:1;flex-shrink:0;}
  .mini-btn{background:transparent;border:1px solid rgba(246,246,247,0.28);border-radius:100px;cursor:pointer;color:#F6F6F7;font-size:12px;padding:3px 10px;}
  .quick-btn:hover{border-color:rgba(230,230,232,0.35)!important;color:rgba(246,246,247,0.85)!important;}

  .habit-btn{transition:all 0.15s ease!important;}

  .shimmer-effect{position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(230,230,232,0.08),transparent);animation:shimmer 0.6s ease-out;pointer-events:none;}

  .cursor-blink{animation:blink 0.8s steps(1) infinite;}

  .boot-line{opacity:0;animation:bootFade 0.3s ease forwards;}

  .card{transition:all 0.3s ease;}
  .card:hover{border-color:rgba(230,230,232,0.18);}

  .habit-cell-pulse{animation:cellPulse 2s ease-in-out infinite;}

  .kaizen-fade{transition:all 0.5s ease;}

  @keyframes aurora{0%{transform:translate(-3%,-2%) scale(1);}50%{transform:translate(3%,2%) scale(1.08);}100%{transform:translate(-2%,3%) scale(1.03);}}
  @keyframes glitchH{0%,100%{clip-path:inset(0 0 96% 0);transform:translateX(-2px);}33%{clip-path:inset(45% 0 45% 0);transform:translateX(2px);}66%{clip-path:inset(85% 0 5% 0);transform:translateX(-1px);}}
  @keyframes spinOrb{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
  @keyframes spinOrbR{from{transform:rotate(0deg);}to{transform:rotate(-360deg);}}
  @keyframes scanMove{0%{top:5%;opacity:0;}15%{opacity:0.9;}85%{opacity:0.9;}100%{top:95%;opacity:0;}}
  @keyframes dotBounce{0%,80%,100%{transform:translateY(0);opacity:0.3;}40%{transform:translateY(-5px);opacity:1;}}
  @keyframes msgIn{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:translateY(0);}}
  @keyframes glitchShift{0%,100%{clip-path:inset(0 0 95% 0);}25%{clip-path:inset(40% 0 40% 0);}75%{clip-path:inset(80% 0 5% 0);}}
  @keyframes shimmer{0%{opacity:0;transform:translateX(-100%);}50%{opacity:1;}100%{opacity:0;transform:translateX(100%);}}
  @keyframes blink{0%,50%{opacity:1;}51%,100%{opacity:0;}}
  @keyframes bootFade{to{opacity:1;}}
  @keyframes cellPulse{0%,100%{box-shadow:0 0 0 0 rgba(230,230,232,0.3);}50%{box-shadow:0 0 6px 2px rgba(230,230,232,0.15);}}
  @keyframes quoteShimmer{0%{background-position:200% 0;}50%{background-position:-200% 0;}100%{background-position:200% 0;}}

  @keyframes gatewayGradient{
    0%,100%{color:#C9C9CC;text-shadow:0 0 20px rgba(230,230,232,0.25);}
    50%{color:#F6F6F7;text-shadow:0 0 28px rgba(230,230,232,0.5);}
  }
  @keyframes gatewayGradientBorder{
    0%,100%{border-color:rgba(230,230,232,0.4);box-shadow:0 0 20px rgba(230,230,232,0.08);}
    50%{border-color:rgba(230,230,232,0.65);box-shadow:0 0 25px rgba(230,230,232,0.14);}
  }
  .gateway-glitch-kanji{animation:gatewayGradient 5s ease-in-out infinite;}
  .gateway-glitch-text{animation:gatewayGradient 5s 0.8s ease-in-out infinite;}
  .gateway-glitch-btn{animation:gatewayGradientBorder 5s 0.4s ease-in-out infinite;}
  @keyframes brickDrop{0%{transform:translateY(-20px);opacity:0;}60%{transform:translateY(2px);opacity:1;}80%{transform:translateY(-1px);}100%{transform:translateY(0);}}

`;
